import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ArrowLeft, Check, Download, Funnel, LucideAngularModule, Pencil, Plus, ReceiptText, Search, ShieldCheck, Trash2, Upload, X } from 'lucide-angular';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { BillVerification, Contract, Loa, LoaBillConfig, Vendor } from '../../../core/models/domain.models';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { DialogComponent } from '../../../shared/ui/dialog.component';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { VendorsService } from '../../vendor/services/vendors.service';

type BillTypeOption = 'OA' | 'SOP' | 'TMR';

interface BillVerificationPayload {
  seller_vendor_id?: string | null;
  loa_id: string;
  bill_type: BillTypeOption;
  invoice_number: string;
  invoice_date?: string | null;
  bill_date: string;
  bill_period_from?: string | null;
  bill_period_to?: string | null;
  billed_quantum?: number | null;
  submitted_rate?: number | null;
  submitted_amount?: number | null;
  remarks?: string | null;
}

interface BillEntryForm {
  billDate: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  billedQuantum: string | number;
  submittedRate: string | number;
  submittedAmount: string | number;
}

interface BillApprovalPreview {
  loa_id: string;
  bill_type: string;
  bill_cycle?: string | null;
  approved_quantum?: number | null;
  approved_amount?: number | null;
  expected_rate?: number | null;
  matched_applications: number;
  note?: string | null;
}

interface ExtractedInvoiceItem {
  sr_no?: string | number | null;
  description?: string | null;
  bill_from_date?: string | null;
  bill_to_date?: string | null;
  hsn_sac?: string | null;
  quantity?: string | number | null;
  rate?: string | number | null;
  unit?: string | null;
  amount?: number | string | null;
}

interface ExtractedContractLoaItem {
  loa_id: string;
  loa_number: string;
  loaId?: string;
  loaNumber?: string;
}

interface ExtractedContractResponderItem {
  responder_vendor_id: string;
  responder_vendor_name: string;
  responderVendorId?: string;
  responderVendorName?: string;
}

interface ExtractedContractLoaCollection {
  contract_id: string;
  contract_number: string;
  loa_collection: ExtractedContractLoaItem[];
  responder_collection?: ExtractedContractResponderItem[];
  contractId?: string;
  contractNumber?: string;
  loaCollection?: ExtractedContractLoaItem[];
  responderCollection?: ExtractedContractResponderItem[];
}

interface ExtractedCollectionFilterState {
  key: string;
  contractId: string;
  loaIds: string[];
  buyerVendorIds: string[];
}

interface ExtractedInvoicePayload {
  invoice_type?: string | null;
  seller_name?: string | null;
  buyer_name?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  trade_date?: string | null;
  delivery_date?: string | null;
  exchange?: string | null;
  segment?: string | null;
  item_details?: ExtractedInvoiceItem[];
  totals?: {
    total_amount?: number | string | null;
    amount_in_words?: string | null;
  };
  matched_context?: {
    seller_vendor_id?: string | null;
    seller_vendor_name?: string | null;
    buyer_vendor_id?: string | null;
    buyer_vendor_name?: string | null;
    contract_id?: string | null;
    contract_number?: string | null;
    contract_loa_collections?: ExtractedContractLoaCollection[] | null;
    loa_id?: string | null;
    loa_number?: string | null;
    loa_rate?: number | string | null;
    loa_quantum?: number | string | null;
  };
  bill_mapping_row?: ExtractedBillMappingRow | ExtractedBillMappingRow[] | null;
  declaration?: string | null;
  period_from?: string | null;
  period_to?: string | null;
}

interface ExtractedBillMappingRow {
  rowKey?: string | null;
  applicationId?: string | null;
  applicationNumber?: string | null;
  applicationNumberTooltip?: string | null;
  invoiceNumber?: string | null;
  sellerVendorId?: string | null;
  sellerName?: string | null;
  buyerVendorId?: string | null;
  buyerName?: string | null;
  contractId?: string | null;
  contractNumber?: string | null;
  loaId?: string | null;
  loaNumber?: string | null;
  billType?: string | null;
  billCycle?: string | null;
  billDate?: string | null;
  billPeriodFrom?: string | null;
  billPeriodTo?: string | null;
  approvedQuantum?: number | null;
  totalQuantum?: number | null;
  totalAmount?: number | null;
  expectedAmount?: number | null;
  billedQuantum?: number | null;
  configuredRateLabel?: string | null;
  configuredRateValue?: number | null;
  submittedRate?: number | null;
  submittedAmount?: number | null;
  verificationId?: string | null;
  verificationStatus?: string | null;
  verificationNotes?: string[] | null;
  approvalStatus?: string | null;
  approvalReason?: string | null;
  approvalRole?: string | null;
  approvalAt?: string | null;
  statusTooltip?: string | null;
}

interface ApplicationScheduleApi {
  from_date: string;
  to_date?: string | null;
  from_time?: string | null;
  to_time?: string | null;
  quantum?: number | null;
}

interface ApplicationApprovalScheduleApi {
  schedule_from: string;
  schedule_to?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  mwh?: number | null;
  requested_mw?: number | null;
  accepted_mw?: number | null;
  is_approved?: boolean;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id?: string | null;
  seller_vendor_id?: string | null;
  seller_loa_id?: string | null;
  buyer_vendor_id?: string | null;
  status: string;
  schedules?: ApplicationScheduleApi[];
  approval_schedules?: ApplicationApprovalScheduleApi[];
}

interface SellerBillGenerateRow {
  rowKey: string;
  applicationId: string;
  applicationNumber: string;
  applicationNumberTooltip: string;
  invoiceNumber: string | null;
  sellerVendorId: string;
  sellerName: string;
  buyerVendorId: string | null;
  buyerName: string;
  contractId: string | null;
  contractNumber: string;
  loaId: string;
  loaNumber: string;
  billType: BillTypeOption;
  billCycle: string;
  billDate: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  approvedQuantum: number | null;
  totalQuantum: number;
  totalAmount: number | null;
  expectedAmount: number | null;
  billedQuantum: number | null;
  configuredRateLabel: string;
  configuredRateValue: number | null;
  submittedRate: number | null;
  submittedAmount: number | null;
  verificationId: string | null;
  verificationStatus: string;
  verificationNotes: string[];
  approvalStatus: string | null;
  approvalReason: string | null;
  approvalRole: string | null;
  approvalAt: string | null;
  statusTooltip: string | null;
}

interface SellerBillMismatchDetail {
  label: string;
  value: string;
}

type BillApprovalAction = 'approve' | 'reject' | 'force-approve';
type ApprovalDialogMode = 'review' | 'submit';

interface SellerBillGenerateGroup {
  groupKey: string;
  sellerVendorId: string;
  sellerName: string;
  loaId: string;
  loaNumber: string;
  billCycle: string;
  bucketKey: string;
  bucketLabel: string;
  bucketFrom: string;
  bucketTo: string;
  rows: SellerBillGenerateRow[];
}

interface HeroStatItem {
  label: string;
  value: string;
  segments: HeroStatSegment[];
}

interface HeroStatSegment {
  label: string;
  value: number;
  tone: 'danger' | 'success' | 'info' | 'muted';
}

interface ApprovalDialogSummary {
  invoiceNumber: string;
  loaNumber: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  quantum: number | null;
  rate: number | null;
  amount: number | null;
}

interface ApprovalDialogLineSummary {
  loaNumber: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  quantum: number | null;
  rate: number | null;
  amount: number | null;
  status: string;
  tone: 'success' | 'danger' | 'secondary';
}

interface ApplicationDetailItem {
  applicationNumber: string;
  buyerName: string;
  contractNumber: string;
  approvedPeriodLabel: string;
  approvedQuantum: number | null;
  status: string;
}

interface ManualSellerBillRowForm {
  buyerVendorId: string;
  contractId: string;
  loaId: string;
  billType: BillTypeOption;
  billCycle: string;
  invoiceDate: string;
  billDate: string;
  billPeriodFrom: string;
  billPeriodTo: string;
  approvedQuantum: string;
  billedQuantum: string;
  loaRate: string;
  submittedRate: string;
  expectedAmount: string;
  submittedAmount: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, DialogComponent],
  templateUrl: './bill-verification1.page.html',
  styleUrl: './bill-verification1.page.scss',
})
export class BillVerification1Page implements OnInit, OnDestroy {
  protected readonly allSellerBillsPageSize = Number.MAX_SAFE_INTEGER;
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly vendorsService = inject(VendorsService);
  private readonly contractsService = inject(ContractsService);
  private readonly loaService = inject(LoaService);
  private readonly toast = inject(ToastService);
  @ViewChild('invoiceFormSection') private invoiceFormSection?: ElementRef<HTMLElement>;
  @ViewChild('invoiceFileInput') private invoiceFileInput?: ElementRef<HTMLInputElement>;

  protected readonly ReceiptTextIcon = ReceiptText;
  protected readonly UploadIcon = Upload;
  protected readonly DownloadIcon = Download;
  protected readonly FilterIcon = Funnel;
  protected readonly PlusIcon = Plus;
  protected readonly ShieldCheckIcon = ShieldCheck;
  protected readonly SearchIcon = Search;
  protected readonly TrashIcon = Trash2;
  protected readonly PencilIcon = Pencil;
  protected readonly CheckIcon = Check;
  protected readonly CloseIcon = X;

  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly fetchingApproval = signal(false);
  protected readonly extractingInvoice = signal(false);
  protected readonly uploadSectionError = signal('');
  protected readonly editingId = signal<string | null>(null);
  protected readonly showForm = signal(false);
  protected readonly showBillSection = signal(false);
  protected readonly showVerificationSection = signal(false);
  protected readonly showPreviewPanel = signal(true);
  protected readonly suppressStoredInvoiceFile = signal(false);
  protected readonly showSellerBillFilters = signal(false);
  protected readonly approvalActionLoadingId = signal<string | null>(null);
  protected readonly approvalReasonDialogOpen = signal(false);
  protected readonly applicationDetailsDialogOpen = signal(false);
  protected readonly addSellerBillRowDialogOpen = signal(false);
  protected readonly fetchingSellerBillRows = signal(false);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly contracts = signal<Contract[]>([]);
  protected readonly loas = signal<Loa[]>([]);
  protected readonly verifications = signal<BillVerification[]>([]);
  protected readonly applications = signal<ApplicationApi[]>([]);
  private readonly extractedBillMappingActive = signal(false);
  private readonly extractedBillMappingRows = signal<SellerBillGenerateRow[]>([]);
  private readonly sellerBillRowsFromBackend = signal<SellerBillGenerateRow[]>([]);
  private readonly landingSellerBillRowsFromBackend = signal<SellerBillGenerateRow[]>([]);
  private readonly selectionVersion = signal(0);
  private readonly selectedSellerBillRowKey = signal<string | null>(null);
  private readonly invoiceRedirectReferenceRow = signal<SellerBillGenerateRow | null>(null);
  private readonly uploadedBillCycle = signal('');
  private readonly uploadedPeriodFrom = signal('');
  private readonly uploadedPeriodTo = signal('');
  private readonly extractedContextLoaRate = signal<number | null>(null);
  private readonly extractedContextLoaQuantum = signal<number | null>(null);
  protected readonly extractedContractLoaCollections = signal<ExtractedContractLoaCollection[]>([]);
  private readonly extractedMatchedContractIds = signal<string[]>([]);
  private readonly selectedExtractedCollectionKeys = signal<string[]>([]);
  private readonly billConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  private readonly hiddenSellerBillRowKeys = signal<string[]>([]);
  private readonly manualSellerBillRows = signal<SellerBillGenerateRow[]>([]);
  protected readonly billEntries = signal<BillEntryForm[]>([]);
  protected readonly search = signal('');
  protected readonly clientFilter = signal('');
  protected readonly loaFilter = signal('');
  protected readonly verificationPage = signal(1);
  protected readonly verificationPageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly verificationPageSize = signal<number>(DEFAULT_PAGE_SIZE_OPTIONS[0] || 10);
  protected readonly generateClientFilter = signal('');
  protected readonly generateLoaFilter = signal('');
  protected readonly generateSearch = signal('Pending Verification');
  protected readonly generateBillCycleFilter = signal('');
  protected readonly generateBillDateFrom = signal('');
  protected readonly generateBillDateTo = signal('');
  protected readonly sellerBillsPage = signal(1);
  protected readonly sellerBillsPageSizeOptions = [this.allSellerBillsPageSize, ...DEFAULT_PAGE_SIZE_OPTIONS];
  protected readonly sellerBillsPageSize = signal<number>(this.allSellerBillsPageSize);

  protected sellerVendorId = '';
  protected buyerVendorId = '';
  protected contractId = '';
  protected selectedContractIds: string[] = [];
  protected loaId = '';
  protected billMappingLoaId = '';
  protected generateClientFilterValue = '';
  protected generateLoaFilterValue = '';
  protected generateSearchValue = 'Pending Verification';
  protected generateBillCycleFilterValue = '';
  protected generateBillDateFromValue = '';
  protected generateBillDateToValue = '';
  protected billType: BillTypeOption = 'SOP';
  protected billTypeLockedByUpload = false;
  protected invoiceNumber = '';
  protected invoiceDate = '';
  protected billDate = '';
  protected billPeriodFrom = '';
  protected billPeriodTo = '';
  protected billedQuantum: string | number = '';
  protected submittedRate: string | number = '';
  protected submittedAmount: string | number = '';
  protected remarks = '';
  protected selectedFile: File | null = null;
  protected readonly extractedInvoicePreview = signal<ExtractedInvoicePayload | null>(null);
  private invoicePreviewObjectUrl: string | null = null;
  private invoicePreviewSourceFile: File | null = null;
  protected approvalReason = '';
  protected pendingApprovalAction: BillApprovalAction | null = null;
  protected pendingApprovalVerificationId: string | null = null;
  protected pendingApprovalInvoiceNumber = '';
  protected pendingApprovalSummary: ApprovalDialogSummary | null = null;
  protected pendingApprovalDialogMode: ApprovalDialogMode = 'review';
  protected pendingApprovalStatusLabel = 'Pending';
  protected pendingApprovalStatusTone: 'success' | 'warning' | 'danger' | 'secondary' = 'secondary';
  protected pendingApprovalAllowActionSelection = true;
  protected applicationDetailsTitle = 'Application Details';
  protected applicationDetailItems: ApplicationDetailItem[] = [];
  protected manualSellerBillRowForm: ManualSellerBillRowForm = this.createManualSellerBillRowForm();
  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly isVendor = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly isBillViewOnlyAdmin = computed(() => {
    const role = (this.auth.user()?.role || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return ['admin', 'super_admin'].includes(role);
  });
  protected readonly isApprovalAdmin = computed(() => {
    const role = (this.auth.user()?.role || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return ['admin', 'super_admin'].includes(role);
  });
  protected readonly isReadOnlyViewMode = computed(() => this.isBillViewOnlyAdmin() && this.showForm());
  protected readonly sellerBillTableLoading = computed(() => this.loading() || this.fetchingSellerBillRows());
  protected readonly visibleRows = computed(() => {
    const term = this.search().trim().toLowerCase();
    const clientId = this.clientFilter().trim();
    const loaId = this.loaFilter().trim();
    return this.verifications().filter((row) => {
      if (clientId && row.seller_vendor_id !== clientId) return false;
      if (loaId && row.loa_id !== loaId) return false;
      if (!term) return true;
      return [
        row.seller_name,
        row.loa_number,
        row.bill_type,
        row.invoice_number,
        row.verification_status,
        ...(row.verification_notes || []),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  });
  protected readonly verificationTotalPages = computed(() => Math.max(1, Math.ceil(this.visibleRows().length / this.verificationPageSize())));
  protected readonly verificationCurrentPage = computed(() => Math.min(this.verificationPage(), this.verificationTotalPages()));
  protected readonly verificationPageStartIndex = computed(() => (this.verificationCurrentPage() - 1) * this.verificationPageSize());
  protected readonly paginatedVisibleRows = computed(() => {
    const start = this.verificationPageStartIndex();
    return this.visibleRows().slice(start, start + this.verificationPageSize());
  });
  protected readonly verificationStartRow = computed(() => (this.visibleRows().length ? this.verificationPageStartIndex() + 1 : 0));
  protected readonly verificationEndRow = computed(() =>
    Math.min(this.verificationPageStartIndex() + this.verificationPageSize(), this.visibleRows().length),
  );
  protected readonly sellerBillsToGenerate = computed(() => {
    const loasById = new Map(this.loas().map((row) => [String(row.id), row]));
    const contractsById = new Map(this.contracts().map((row) => [String(row.id), row]));
    const vendorsById = new Map(this.vendors().map((row) => [String(row.id), row]));
    const verifications = this.verifications();

    const appsByLoa = new Map<string, ApplicationApi[]>();
    this.applications()
      .filter((app) => (app.status || '').trim().toLowerCase() === 'punched')
      .filter((app) => this.applicationHasApprovedSchedule(app))
      .forEach((app) => {
        const sellerVendorId = String(app.seller_vendor_id || '').trim();
        const loaId = String(app.seller_loa_id || '').trim();
        if (!sellerVendorId || !loaId) return;
        console.log(this.auth.user()?.role)
        if (this.isVendor() && this.auth.user()?.role !== 'accounts' && this.sellerVendorId && sellerVendorId !== this.sellerVendorId) return;
        const list = appsByLoa.get(loaId) || [];
        list.push(app);
        appsByLoa.set(loaId, list);
      });

    const rows: SellerBillGenerateRow[] = [];
    for (const [loaId, apps] of appsByLoa.entries()) {
      const loa = loasById.get(loaId);
      const sellerVendorId = String(loa?.vendor_id || apps[0]?.seller_vendor_id || '').trim();
      if (!sellerVendorId) continue;

      const bounds = this.loaScheduleBounds(apps);
      if (!bounds) continue;

      const appsWithBounds = apps
        .map((app) => {
          const range = this.applicationDateBounds(app);
          if (!range) return null;
          const applicationNumber = String(app.application_number || app.id || '').trim();
          return applicationNumber
            ? { applicationNumber, start: range.start, end: range.end }
            : null;
        })
        .filter((item): item is { applicationNumber: string; start: Date; end: Date } => item !== null);

      const configs = this.supportedBillConfigs(this.billConfigsByLoa()[loaId] || []);
      configs.forEach((config) => {
        const billType = ((config.bill_type || '').trim().toUpperCase() || 'SOP') as BillTypeOption;
        const billCycle = (config.bill_cycle || '').trim() || 'NA';
        const periods = this.billPeriodsForCycle(bounds.start, bounds.end, billCycle);

        periods.forEach((period) => {
          const overlappingApps = apps
            .map((app) => ({ app, bounds: this.applicationDateBounds(app) }))
            .filter(
              (
                item,
              ): item is {
                app: ApplicationApi;
                bounds: { start: Date; end: Date };
              } => !!item.bounds && this.rangesOverlap(period.from, period.to, item.bounds.start, item.bounds.end),
            );

          if (!overlappingApps.length) return;

          overlappingApps.forEach(({ app }) => {
            const buyerId = String(app.buyer_vendor_id || '').trim();
            const contractId = String(app.contract_id || '').trim();
            const applicationId = String(app.id || '').trim();
            const applicationNumber = String(app.application_number || app.id || '').trim();
            if (!buyerId || !contractId || !applicationId) return;

            const buyerName = vendorsById.get(buyerId)?.vendor_name || buyerId;
            const contractNumber = contractsById.get(contractId)?.contract_number || contractId;
            const applicationQuantum = Number(this.applicationQuantumForRange(app, period.from, period.to).toFixed(4));
            if (applicationQuantum <= 0) return;

            const billPeriodFrom = this.formatIsoDate(period.from);
            const billPeriodTo = this.formatIsoDate(period.to);
            const billDate = this.formatIsoDate(period.billDate);
            const configuredRate = this.sellerLoaRateValue(loa);
            const derivedTotalAmount =
              configuredRate !== null ? Number((applicationQuantum * configuredRate).toFixed(2)) : null;
            const existing = this.matchingVerification(verifications, loaId, billType, billCycle, period.from, period.to, applicationQuantum);

            rows.push({
              rowKey: `${loaId}::${billType}::${billPeriodFrom}::${billPeriodTo}::${contractId}::${buyerId}::${applicationId}`,
              applicationId,
              applicationNumber: applicationNumber || '-',
              applicationNumberTooltip: applicationNumber || '-',
              invoiceNumber: existing?.invoice_number || null,
              sellerVendorId,
              sellerName: this.sellerName(sellerVendorId),
              buyerVendorId: buyerId,
              buyerName,
              contractId,
              contractNumber,
              loaId,
              loaNumber: loa?.loa_number || loaId,
              billType,
              billCycle,
              billDate,
              billPeriodFrom,
              billPeriodTo,
              approvedQuantum: applicationQuantum,
              totalQuantum: applicationQuantum,
              totalAmount: derivedTotalAmount,
              expectedAmount: derivedTotalAmount ?? existing?.expected_amount ?? null,
              billedQuantum: existing?.billed_quantum ?? null,
              configuredRateLabel: this.sellerLoaRateLabel(loa),
              configuredRateValue: configuredRate,
              submittedRate: existing?.submitted_rate ?? null,
              submittedAmount: existing?.submitted_amount ?? null,
              verificationId: existing?.id || null,
              verificationStatus: existing?.verification_status || 'Pending Verification',
              verificationNotes: existing?.verification_notes || [],
              approvalStatus: existing?.approval_status || null,
              approvalReason: existing?.approval_reason || null,
              approvalRole: existing?.approval_role || null,
              approvalAt: existing?.approval_at || null,
              statusTooltip: this.sellerBillStatusTooltip({
                rowKey: `${loaId}::${billType}::${billPeriodFrom}::${billPeriodTo}::${contractId}::${buyerId}::${applicationId}`,
                applicationId,
                applicationNumber: applicationNumber || '-',
                applicationNumberTooltip: applicationNumber || '-',
                invoiceNumber: existing?.invoice_number || null,
                sellerVendorId,
                sellerName: this.sellerName(sellerVendorId),
                buyerVendorId: buyerId,
                buyerName,
                contractId,
                contractNumber,
                loaId,
                loaNumber: loa?.loa_number || loaId,
                billType,
                billCycle,
                billDate,
                billPeriodFrom,
                billPeriodTo,
                approvedQuantum: applicationQuantum,
                totalQuantum: applicationQuantum,
                totalAmount: derivedTotalAmount,
                expectedAmount: derivedTotalAmount ?? existing?.expected_amount ?? null,
                billedQuantum: existing?.billed_quantum ?? null,
                configuredRateLabel: this.sellerLoaRateLabel(loa),
                configuredRateValue: configuredRate,
                submittedRate: existing?.submitted_rate ?? null,
                submittedAmount: existing?.submitted_amount ?? null,
                verificationId: existing?.id || null,
                verificationStatus: existing?.verification_status || 'Pending Verification',
                verificationNotes: existing?.verification_notes || [],
                approvalStatus: existing?.approval_status || null,
                approvalReason: existing?.approval_reason || null,
                approvalRole: existing?.approval_role || null,
                approvalAt: existing?.approval_at || null,
                statusTooltip: null,
              }),
            } satisfies SellerBillGenerateRow);
          });
        });
      });
    }

    return rows.sort((left, right) => {
      if (left.sellerName !== right.sellerName) return left.sellerName.localeCompare(right.sellerName);
      if (left.loaNumber !== right.loaNumber) return left.loaNumber.localeCompare(right.loaNumber);
      if (left.billPeriodFrom !== right.billPeriodFrom) return right.billPeriodFrom.localeCompare(left.billPeriodFrom);
      return left.billType.localeCompare(right.billType);
    });
  });

  protected readonly landingSellerBillsToGenerate = computed(() => {
    const backendRows = this.landingSellerBillRowsFromBackend();
    return backendRows.length ? backendRows : this.sellerBillsToGenerate();
  });

  protected readonly landingSellerBillRows = computed<SellerBillGenerateRow[]>(() =>
    (
      !this.showForm() && !this.extractedBillMappingActive() ? this.landingSellerBillsToGenerate() : this.effectiveSellerBillRows()
    ).filter((row) => this.isAdminVisibleBillRow(row)),
  );

  protected readonly effectiveSellerBillRows = computed<SellerBillGenerateRow[]>(() => {
    this.selectionVersion();
    const selectedSellerLoaId = String(this.loaId || '').trim();
    const selectedSellerId = String(this.sellerVendorId || '').trim();
    const activeBillLines = this.activeBillLineEntries();
    const selectedBillPeriodFrom = String(activeBillLines[0]?.billPeriodFrom || this.billPeriodFrom || '').trim();
    const selectedBillPeriodTo = String(activeBillLines[0]?.billPeriodTo || this.billPeriodTo || '').trim();
    const shouldScopeBillMappingRows = this.showForm() && this.showBillSection();
    const sourceRows = shouldScopeBillMappingRows
      ? this.mergedMappingRows(this.sellerBillRowsFromBackend(), this.extractedBillMappingRows())
      : this.extractedBillMappingActive()
        ? this.extractedBillMappingRows()
        : this.sellerBillsToGenerate();

    return sourceRows
      .map((row) => this.withResolvedConfiguredRate(row))
      .filter((row) => {
        const buyerLabel = String(row.buyerName || '').trim().toLowerCase();
        const hasBuyerMapping = !!buyerLabel && buyerLabel !== 'no buyer linked';
        if (!hasBuyerMapping) return false;
        if (selectedSellerId && row.sellerVendorId !== selectedSellerId) return false;

        const rowFrom = String(row.billPeriodFrom || '').trim();
        const rowTo = String(row.billPeriodTo || '').trim();
        const matchesSelectedPeriod = this.rowMatchesAnyBillLinePeriod(rowFrom, rowTo, activeBillLines);
        const shouldRelaxPeriodFilter = this.shouldRelaxUploadPeriodFilter(sourceRows, selectedBillPeriodFrom, selectedBillPeriodTo);
        if (!shouldRelaxPeriodFilter && !matchesSelectedPeriod) return false;

        if (this.editingId()) {
          if (!selectedSellerLoaId) return true;
          return row.loaId === selectedSellerLoaId;
        }

        // For extracted upload mapping, show all suggested contract rows (not only one selected LOA).
        if (this.extractedBillMappingActive()) {
          if (!selectedSellerLoaId) return true;
          return row.loaId === selectedSellerLoaId;
        }

        if (shouldScopeBillMappingRows && !selectedSellerId) return false;

        if (shouldScopeBillMappingRows && !selectedSellerLoaId) return true;

        if (!selectedSellerLoaId) return false;
        return row.loaId === selectedSellerLoaId;
      });
  });
  protected readonly sellerBillGroups = computed<SellerBillGenerateGroup[]>(() => {
    const groups = new Map<string, SellerBillGenerateGroup>();

    this.landingSellerBillRows().forEach((row) => {
      const bucket = this.billCycleBucket(row.billCycle, row.billDate || row.billPeriodTo || row.billPeriodFrom);
      const groupKey = `${row.loaId}::${(row.billCycle || '').trim().toLowerCase()}::${bucket.bucketKey}`;

      const existing = groups.get(groupKey);
      if (existing) {
        existing.rows.push(row);
        return;
      }

      groups.set(groupKey, {
        groupKey,
        sellerVendorId: row.sellerVendorId,
        sellerName: row.sellerName,
        loaId: row.loaId,
        loaNumber: row.loaNumber,
        billCycle: row.billCycle,
        bucketKey: bucket.bucketKey,
        bucketLabel: bucket.bucketLabel,
        bucketFrom: bucket.bucketFrom,
        bucketTo: bucket.bucketTo,
        rows: [row],
      });
    });

    const sortedGroups = [...groups.values()].sort((left, right) => {
      if (left.sellerName !== right.sellerName) return left.sellerName.localeCompare(right.sellerName);
      if (left.loaNumber !== right.loaNumber) return left.loaNumber.localeCompare(right.loaNumber);
      if (left.bucketFrom !== right.bucketFrom) return right.bucketFrom.localeCompare(left.bucketFrom);
      return left.billCycle.localeCompare(right.billCycle);
    });

    sortedGroups.forEach((group) => {
      group.rows.sort((left, right) => {
        if (left.billType !== right.billType) return left.billType.localeCompare(right.billType);
        if (left.billDate !== right.billDate) return right.billDate.localeCompare(left.billDate);
        return left.applicationNumber.localeCompare(right.applicationNumber);
      });
    });

    return sortedGroups;
  });
  protected readonly filteredSellerBillGroups = computed<SellerBillGenerateGroup[]>(() => {
    const clientId = this.generateClientFilter().trim();
    const loaId = this.generateLoaFilter().trim();
    const cycle = this.generateBillCycleFilter().trim().toLowerCase();
    const term = this.generateSearch().trim().toLowerCase();
    const dateFrom = this.generateBillDateFrom().trim();
    const dateTo = this.generateBillDateTo().trim();
    const uploadedCycle = this.uploadedBillCycle().trim().toLowerCase();
    const uploadedFrom = this.uploadedPeriodFrom().trim();
    const uploadedTo = this.uploadedPeriodTo().trim();
    const selectedCollectionFilters = this.selectedExtractedCollectionFilters();

    return this.sellerBillGroups().filter((group) => {
      return group.rows.some((row) =>
        this.matchesSellerBillRowFilters(
          row,
          clientId,
          loaId,
          cycle,
          term,
          dateFrom,
          dateTo,
          uploadedCycle,
          uploadedFrom,
          uploadedTo,
          selectedCollectionFilters,
        ),
      );
    });
  });
  protected readonly filteredSellerBillRows = computed<SellerBillGenerateRow[]>(() =>
    this.filteredSellerBillGroups().flatMap((group) =>
      group.rows.filter((row) =>
        this.matchesSellerBillRowFilters(
          row,
          this.generateClientFilter().trim(),
          this.generateLoaFilter().trim(),
          this.generateBillCycleFilter().trim().toLowerCase(),
          this.generateSearch().trim().toLowerCase(),
          this.generateBillDateFrom().trim(),
          this.generateBillDateTo().trim(),
          this.uploadedBillCycle().trim().toLowerCase(),
          this.uploadedPeriodFrom().trim(),
          this.uploadedPeriodTo().trim(),
          this.selectedExtractedCollectionFilters(),
        ),
      ),
    ),
  );
  protected readonly mappingSellerBillRows = computed<SellerBillGenerateRow[]>(() => {
    const baseRows = this.effectiveSellerBillRows();
    const fallbackRows = this.fallbackSellerBillRowsFromEntries();
    const meaningfulEntries = this.meaningfulBillLineEntries();
    if (meaningfulEntries.length && !this.rowsMatchUploadedBillLinePeriods(baseRows, meaningfulEntries)) {
      return fallbackRows.length ? fallbackRows : baseRows;
    }

    if (!this.visibleExtractedContractLoaCollections().length) return baseRows.length ? baseRows : fallbackRows;

    const selectedCollectionFilters = this.selectedExtractedCollectionFilters();
    if (!selectedCollectionFilters.length) return baseRows.length ? baseRows : fallbackRows;

    const filteredRows = baseRows.filter((row) => this.matchesSelectedCollectionRow(row, selectedCollectionFilters));
    return filteredRows.length ? filteredRows : fallbackRows;
  });
  protected readonly visibleExtractedContractLoaCollections = computed<ExtractedContractLoaCollection[]>(() => {
    const collections = this.extractedContractLoaCollections();
    if (!collections.length) return [];

    const invoiceRows = this.sellerBillRowsFromBackend().length
      ? this.mergedMappingRows(this.sellerBillRowsFromBackend(), this.extractedBillMappingRows())
      : this.mergedMappingRows(this.sellerBillsToGenerate(), this.extractedBillMappingRows());
    const selectedFrom = String(this.billEntries()[0]?.billPeriodFrom || this.billPeriodFrom || '').trim();
    const selectedTo = String(this.billEntries()[0]?.billPeriodTo || this.billPeriodTo || '').trim();
    const extractedContractIds = new Set(this.extractedMatchedContractIds().map((id) => String(id || '').trim()).filter(Boolean));

    return collections.filter((item) => {
      const scope = this.extractedCollectionScope(item);
      if (!scope) return false;

      const hasMatchedInvoiceRow = invoiceRows.some((row) => {
        if (!this.rowHasPunchedApplications(row)) return false;
        if (!this.matchesSelectedCollectionRow(row, [scope])) return false;

        const rowFrom = String(row.billPeriodFrom || '').trim();
        const rowTo = String(row.billPeriodTo || '').trim();
        return this.rowMatchesSelectedPeriod(rowFrom, rowTo, selectedFrom, selectedTo);
      });
      if (hasMatchedInvoiceRow) return true;

      return extractedContractIds.has(scope.contractId);
    });
  });
  protected readonly tableSellerBillRows = computed<SellerBillGenerateRow[]>(() =>
    {
      this.selectionVersion();
      const hiddenRowKeys = new Set(this.hiddenSellerBillRowKeys());
      const rows = this.showForm() && this.showBillSection() && !this.showVerificationSection()
        ? this.mappingSellerBillRows()
        : this.filteredSellerBillRows();
      const withManualRows =
        this.showForm() && this.showBillSection() && !this.showVerificationSection()
          ? this.mergeTableSellerBillRows(this.withActiveBillLineValues(rows), this.manualSellerBillRows())
          : this.withActiveBillLineValues(rows);
      return withManualRows.filter((row) => !hiddenRowKeys.has(row.rowKey));
    },
  );
  protected readonly reviewSellerBillRows = computed<SellerBillGenerateRow[]>(() => {
    const mappedRows = this.mergeTableSellerBillRows(this.withActiveBillLineValues(this.mappingSellerBillRows()), this.manualSellerBillRows());
    if (mappedRows.length) return mappedRows;
    return this.currentReferenceRow() ? [this.currentReferenceRow() as SellerBillGenerateRow] : [];
  });
  protected readonly paginatedSellerBillRows = computed<SellerBillGenerateRow[]>(() => {
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    const pageSize = this.sellerBillsPageSize();
    const start = (page - 1) * pageSize;
    return this.tableSellerBillRows().slice(start, start + pageSize);
  });
  protected readonly sellerBillTotalPages = computed<number>(() => {
    const totalRows = this.tableSellerBillRows().length;
    return Math.max(1, Math.ceil(totalRows / this.sellerBillsPageSize()));
  });
  protected readonly sellerBillCycleOptions = computed<string[]>(() =>
    Array.from(new Set(this.landingSellerBillRows().map((row) => row.billCycle).filter((value) => !!value))).sort((left, right) =>
      left.localeCompare(right),
    ),
  );
  protected readonly buyerListVendors = computed<Vendor[]>(() => {
    this.selectionVersion();

    const selectedContractIds = new Set(this.selectedContractIds.map((id) => String(id || '').trim()).filter(Boolean));
    if (!selectedContractIds.size && this.contractId) {
      selectedContractIds.add(String(this.contractId || '').trim());
    }

    const buyerIds = new Set(
      this.effectiveSellerBillRows()
        .filter((row) => {
          const buyerId = String(row.buyerVendorId || '').trim();
          if (!buyerId) return false;
          if (!selectedContractIds.size) return true;
          return selectedContractIds.has(String(row.contractId || '').trim());
        })
        .map((row) => String(row.buyerVendorId || '').trim())
        .filter(Boolean),
    );

    if (buyerIds.size) {
      return this.vendors()
        .filter((vendor) => buyerIds.has(String(vendor.id || '').trim()))
        .sort((left, right) => String(left.vendor_name || '').localeCompare(String(right.vendor_name || '')));
    }

    if (this.extractedBillMappingActive()) {
      return [];
    }

    return this.availableBuyers();
  });
  protected readonly heroStats = computed<HeroStatItem[]>(() => {
    const rows = this.landingSellerBillRows();
    const verifications = this.verifications();
    const verifiedCount = rows.filter((row) => this.isVerifiedStatus(row.verificationStatus)).length;
    const attentionCount = rows.filter((row) => this.needsAttentionStatus(row.verificationStatus)).length;
    const pendingCount = rows.filter((row) => this.isPendingStatus(row.verificationStatus)).length;
    const uniqueSellers = new Set(rows.map((row) => row.sellerVendorId).filter(Boolean)).size;
    const uniqueLoas = new Set(rows.map((row) => row.loaId).filter(Boolean)).size;
    const submittedAmountTotal = verifications.reduce((sum, row) => sum + this.safeNumber(row.submitted_amount), 0);
    const submittedAmountCount = verifications.reduce(
      (count, row) => count + (row.submitted_amount !== null && row.submitted_amount !== undefined && row.submitted_amount !== null ? 1 : 0),
      0,
    );
    const pendingAmountTotal = rows
      .filter((row) => this.isPendingStatus(row.verificationStatus))
      .reduce((sum, row) => sum + this.safeNumber(row.expectedAmount), 0);
    const expectedAmountTotal = rows.reduce((sum, row) => sum + this.safeNumber(row.expectedAmount), 0);

    if (this.isVendor()) {
      return [
        {
          label: 'Total Bills',
          value: this.formatStatNumber(rows.length),
          segments: [
            { label: 'Attention', value: attentionCount, tone: 'danger' },
            { label: 'Pending', value: pendingCount, tone: 'info' },
            { label: 'Verified', value: verifiedCount, tone: 'success' },
          ],
        },
        {
          label: 'Verified',
          value: this.formatStatNumber(verifiedCount),
          segments: [
            { label: 'Verified', value: verifiedCount, tone: 'danger' },
            { label: 'Others', value: Math.max(rows.length - verifiedCount, 0), tone: 'info' },
          ],
        },
        {
          label: 'Pending Bills',
          value: this.formatStatNumber(pendingCount),
          segments: [
            { label: 'Pending', value: pendingCount, tone: 'danger' },
            { label: 'Attention', value: attentionCount, tone: 'info' },
          ],
        },
        {
          label: 'Invoice value',
          value: this.formatCurrencyStat(submittedAmountTotal || expectedAmountTotal),
          segments: [{ label: 'Amount', value: 1, tone: 'muted' }],
        },
      ];
    }

    return [
      {
        label: 'Total checks',
        value: this.formatStatNumber(verifications.length),
        segments: [
          { label: 'Verified', value: verifiedCount, tone: 'danger' },
          { label: 'Pending', value: pendingCount, tone: 'info' },
          { label: 'Attention', value: attentionCount, tone: 'success' },
        ],
      },
      {
        label: 'Active sellers',
        value: this.formatStatNumber(uniqueSellers),
        segments: [{ label: 'Sellers', value: uniqueSellers, tone: uniqueSellers > 0 ? 'info' : 'muted' }],
      },
      {
        label: 'LOAs covered',
        value: this.formatStatNumber(uniqueLoas),
        segments: [{ label: 'LOAs', value: uniqueLoas, tone: uniqueLoas > 0 ? 'info' : 'muted' }],
      },
      {
        label: 'Pending bills',
        value: this.formatStatNumber(pendingCount),
        segments: [
          { label: 'Pending', value: pendingCount, tone: 'danger' },
          { label: 'Attention', value: attentionCount, tone: 'info' },
        ],
      },
      {
        label: 'Submitted Amount',
        value: this.formatCurrencyStat(submittedAmountTotal),
        segments: [{ label: 'Bills', value: submittedAmountCount, tone: submittedAmountCount > 0 ? 'info' : 'muted' }],
      },
      {
        label: 'Pending Amount',
        value: this.formatCurrencyStat(pendingAmountTotal),
        segments: [{ label: 'Bills', value: pendingCount, tone: pendingCount > 0 ? 'danger' : 'muted' }],
      },
    ];
  });

  protected readonly activeBillLineEntries = computed<BillEntryForm[]>(() => {
    if (this.editingId()) {
      return [
        {
          billDate: this.billDate,
          billPeriodFrom: this.billPeriodFrom,
          billPeriodTo: this.billPeriodTo,
          billedQuantum: this.billedQuantum,
          submittedRate: this.submittedRate,
          submittedAmount: this.submittedAmount,
        },
      ];
    }
    return this.billEntries();
  });

  protected readonly currentReferenceRow = computed<SellerBillGenerateRow | null>(() => {
    const selectedRowKey = this.selectedSellerBillRowKey();
    if (selectedRowKey) {
      const selectedRow = this.effectiveSellerBillRows().find((row) => row.rowKey === selectedRowKey) || null;
      if (selectedRow) return selectedRow;
    }

    const candidateLoaIds = [this.billMappingLoaId, this.loaId]
      .map((value) => String(value || '').trim())
      .filter((value, index, items) => !!value && items.indexOf(value) === index);
    if (!candidateLoaIds.length) return null;

    const referenceDate = this.editingId() ? this.billDate : this.billEntries()[0]?.billDate;
    const referenceFrom = this.editingId() ? this.billPeriodFrom : this.billEntries()[0]?.billPeriodFrom;
    const referenceTo = this.editingId() ? this.billPeriodTo : this.billEntries()[0]?.billPeriodTo;

    if (referenceFrom && referenceTo) {
      for (const loaId of candidateLoaIds) {
        const exactRangeMatch =
          this.effectiveSellerBillRows().find(
            (row) =>
              row.loaId === loaId &&
              row.billType === this.billType &&
              row.billPeriodFrom === referenceFrom &&
              row.billPeriodTo === referenceTo,
          ) || null;
        if (exactRangeMatch) return exactRangeMatch;
      }
    }

    if (referenceDate) {
      for (const loaId of candidateLoaIds) {
        const dateMatch =
          this.effectiveSellerBillRows().find(
            (row) => row.loaId === loaId && row.billType === this.billType && row.billDate === referenceDate,
          ) || null;
        if (dateMatch) return dateMatch;
      }
    }

    for (const loaId of candidateLoaIds) {
      const fallback = this.effectiveSellerBillRows().find((row) => row.loaId === loaId && row.billType === this.billType) || null;
      if (fallback) return fallback;
    }
    return null;
  });
  ngOnInit(): void {
    if (this.isVendor()) {
      this.sellerVendorId = this.auth.user()?.vendor_id || '';
    }
    if (this.isBillViewOnlyAdmin()) {
      this.generateSearchValue = '';
      this.generateSearch.set('');
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.revokeInvoicePreviewObjectUrl();
  }

  protected availableSellerLoas(): Loa[] {
    const sellerId = this.sellerVendorId;
    if (!sellerId) return [];
    const sellerLoas = this.loas().filter((row) => row.vendor_id === sellerId && this.resolveLoaKind(row) === 'sell');
    const selectedContracts = this.selectedContracts();
    if (!selectedContracts.length) return sellerLoas;

    const allowedLoaIds = new Set(selectedContracts.flatMap((contract) => this.contractSellerLoaIds(contract)));
    return sellerLoas.filter((row) => allowedLoaIds.has(String(row.id)));
  }

  protected availableBuyerLoas(): Loa[] {
    const buyerId = this.buyerVendorId.trim();
    if (!buyerId) return [];
    const buyerLoas = this.loas().filter((row) => row.vendor_id === buyerId && this.resolveLoaKind(row) === 'buy');
    const selectedContracts = this.selectedContracts();
    if (!selectedContracts.length) return buyerLoas;

    const allowedLoaIds = new Set(selectedContracts.flatMap((contract) => this.contractBuyerLoaIds(contract)));
    return buyerLoas.filter((row) => allowedLoaIds.has(String(row.id)));
  }

  protected availableBuyers(): Vendor[] {
    const sellerId = this.sellerVendorId.trim();
    if (!sellerId) return [];

    const buyerIds = new Set(
      this.contracts()
        .filter((contract) => this.contractIncludesVendor(contract, sellerId, 'seller'))
        .flatMap((contract) => this.contractPartyVendorIds(contract, 'buyer')),
    );

    const buyerOptions = this.vendors().filter((vendor) => vendor.id !== sellerId && buyerIds.has(String(vendor.id)));
    return buyerOptions;
  }

  protected availableContracts(): Contract[] {
    const sellerId = this.sellerVendorId.trim();
    const buyerId = this.buyerVendorId.trim();
    return this.contracts().filter((contract) => {
      if (sellerId && !this.contractIncludesVendor(contract, sellerId, 'seller')) return false;
      if (buyerId && !this.contractIncludesVendor(contract, buyerId, 'buyer')) return false;
      return true;
    });
  }

  protected contractOptions(): Contract[] {
    const available = this.availableContracts();
    const extractedIds = new Set(this.extractedMatchedContractIds().map((id) => String(id || '').trim()).filter(Boolean));
    if (!extractedIds.size) return available;

    const merged = new Map<string, Contract>();
    available.forEach((contract) => merged.set(String(contract.id), contract));
    this.contracts()
      .filter((contract) => extractedIds.has(String(contract.id)))
      .forEach((contract) => merged.set(String(contract.id), contract));
    return [...merged.values()];
  }

  protected filterLoas(): Loa[] {
    const clientId = this.clientFilter().trim();
    if (!clientId) return this.loas();
    return this.loas().filter((row) => row.vendor_id === clientId);
  }

  protected generateFilterLoas(): Loa[] {
    const clientId = this.generateClientFilterValue.trim();
    if (!clientId) return this.loas();
    return this.loas().filter((row) => row.vendor_id === clientId);
  }

  protected hasActiveExtractedCollectionFilter(): boolean {
    const collections = this.visibleExtractedContractLoaCollections();
    if (!collections.length) return false;
    return this.selectedExtractedCollectionKeys().length !== collections.length;
  }

  protected isExtractedCollectionActive(item: ExtractedContractLoaCollection): boolean {
    return this.selectedExtractedCollectionKeys().includes(this.extractedCollectionKey(item));
  }

  protected toggleExtractedCollectionFilter(item: ExtractedContractLoaCollection): void {
    const key = this.extractedCollectionKey(item);
    if (!key) return;

    this.selectedExtractedCollectionKeys.update((keys) =>
      keys.includes(key) ? keys.filter((itemKey) => itemKey !== key) : [...keys, key],
    );
    this.sellerBillsPage.set(1);
  }

  protected clearExtractedCollectionFilter(): void {
    this.selectAllExtractedCollections();
    this.sellerBillsPage.set(1);
  }

  protected sellerName(vendorId: string): string {
    return this.vendors().find((row) => row.id === vendorId)?.vendor_name || '-';
  }

  protected selectedBuyerName(): string {
    return this.vendors().find((row) => String(row.id) === String(this.buyerVendorId))?.vendor_name || 'Select buyer';
  }

  protected selectedContractNumber(): string {
    const selected = this.selectedContracts();
    if (!selected.length) return 'Select contract';
    if (selected.length === 1) return selected[0].contract_number || 'Select contract';
    return `${selected.length} contracts selected`;
  }

  protected selectedLoaNumber(): string {
    const selectedLoaId = String(this.billMappingLoaId || this.loaId || '').trim();
    return this.loas().find((row) => String(row.id) === selectedLoaId)?.loa_number || 'Select LOA';
  }

  protected selectedSellerName(): string {
    return this.sellerVendorId ? this.sellerName(this.sellerVendorId) : 'Select seller';
  }

  protected currentBillStatusTone(): 'success' | 'warning' | 'danger' | 'secondary' {
    const row = this.currentReferenceRow();
    if (!row) {
      return this.showForm() ? 'warning' : 'secondary';
    }

    const approval = this.displayApprovalStatus(row);
    if (approval === 'Approved') return 'success';
    if (approval === 'Rejected') return 'danger';

    const verificationTone = this.statusVariant(row.verificationStatus);
    if (verificationTone === 'success') return 'success';
    if (verificationTone === 'danger') return 'danger';
    if (verificationTone === 'warning') return 'warning';
    return 'secondary';
  }

  protected currentBillStatusLabel(): string {
    const row = this.currentReferenceRow();
    if (!row) return this.showForm() ? 'Draft' : 'Select Bill';
    const approval = this.displayApprovalStatus(row);
    if (approval !== 'Pending') return approval;
    return this.displayVerificationStatus(row.verificationStatus);
  }

  protected currentIssueCount(): number {
    let issues = 0;
    if (!this.sellerVendorId) issues += 1;
    if (!String(this.billMappingLoaId || this.loaId || '').trim()) issues += 1;

    const rateMatch = this.rateMatchesCurrentReference();
    const quantumMatch = this.quantumMatchesCurrentReference();
    const amountMatch = this.amountMatchesCurrentReference();
    if (rateMatch === false) issues += 1;
    if (quantumMatch === false) issues += 1;
    if (amountMatch === false) issues += 1;
    return issues;
  }

  protected sellerCoverageLabel(): string {
    return this.sellerVendorId ? 'Matched' : 'Missing';
  }

  protected loaCoverageLabel(): string {
    return String(this.billMappingLoaId || this.loaId || '').trim() ? 'Matched' : 'Missing';
  }

  protected buyerCoverageLabel(): string {
    const selectedBuyer = String(this.buyerVendorId || '').trim();
    if (!selectedBuyer) return '0 / 0';
    return `1 / 1`;
  }

  protected overviewSummaryText(): string {
    const seller = this.selectedSellerName();
    const loa = this.selectedLoaNumber();
    const rateMatch = this.rateMatchesCurrentReference();
    const quantumMatch = this.quantumMatchesCurrentReference();
    const amountMatch = this.amountMatchesCurrentReference();

    const summaryParts = [`Seller ${seller === 'Select seller' ? 'is pending selection' : 'is selected'}.`, `LOA ${loa === 'Select LOA' ? 'is pending selection' : 'is selected'}.`];

    if (rateMatch === true) {
      summaryParts.push('Rate matches the configured LOA rate.');
    } else if (rateMatch === false) {
      summaryParts.push('Rate differs from the configured LOA rate and needs review.');
    }

    if (quantumMatch === true) {
      summaryParts.push('Quantum matches the reference bill window.');
    } else if (quantumMatch === false) {
      summaryParts.push('Quantum differs from the generated reference values and needs review.');
    }

    if (amountMatch === true) {
      summaryParts.push('Amount matches the expected bill amount.');
    } else if (amountMatch === false) {
      summaryParts.push('Amount differs from the expected bill amount.');
    }

    return summaryParts.join(' ');
  }

  protected formatStatusLabel(value: string | null | undefined): string {
    const normalized = (value || '').trim();
    return normalized || 'Pending';
  }

  protected currentExpectedQuantum(): number | null {
    const row = this.currentExpectedReferenceRow();
    return row?.approvedQuantum ?? row?.totalQuantum ?? this.extractedContextLoaQuantum();
  }

  protected currentExpectedAmount(): number | null {
    const row = this.currentExpectedReferenceRow();
    const calculatedAmount = this.calculateExpectedAmount(this.currentExpectedQuantum(), this.currentExpectedRateValue());
    if (calculatedAmount !== null) return calculatedAmount;

    return this.toNumberOrNull(row?.expectedAmount) ?? this.toNumberOrNull(row?.totalAmount);
  }

  protected currentExpectedRateLabel(): string {
    const rowLabel = this.currentExpectedReferenceRow()?.configuredRateLabel || '';
    if (rowLabel && rowLabel !== '-') return rowLabel;
    const redirectRate = this.toNumberOrNull(this.invoiceRedirectReferenceRow()?.configuredRateValue);
    if (redirectRate !== null) return String(redirectRate);
    const contextRate = this.extractedContextLoaRate();
    return contextRate === null ? '-' : String(contextRate);
  }

  protected reviewRowIssueCount(row: SellerBillGenerateRow): number {
    let issues = 0;
    if (!row.sellerVendorId) issues += 1;
    if (!String(row.loaId || '').trim()) issues += 1;
    if (this.reviewRowMatchLabel(row, 'rate') === 'Mismatch') issues += 1;
    if (this.reviewRowMatchLabel(row, 'quantum') === 'Mismatch') issues += 1;
    if (this.reviewRowMatchLabel(row, 'amount') === 'Mismatch') issues += 1;
    return issues;
  }

  protected reviewRowMatchLabel(row: SellerBillGenerateRow, kind: 'seller' | 'loa' | 'rate' | 'quantum' | 'amount'): string {
    if (kind === 'seller') return row.sellerVendorId ? 'Yes' : 'No';
    if (kind === 'loa') return String(row.loaId || '').trim() ? 'Yes' : 'No';
    if (kind === 'rate') {
      const loaRate = this.toNumberOrNull(row.configuredRateValue) ?? this.extractRateValue(row.configuredRateLabel);
      const submittedRate = this.toNumberOrNull(row.submittedRate);
      if (loaRate === null || submittedRate === null) return 'Pending';
      return Math.abs(loaRate - submittedRate) < 0.0001 ? 'Matched' : 'Mismatch';
    }
    if (kind === 'quantum') {
      const approvedQuantum = this.toNumberOrNull(row.approvedQuantum ?? row.totalQuantum);
      const billedQuantum = this.toNumberOrNull(row.billedQuantum);
      if (approvedQuantum === null || billedQuantum === null) return 'Pending';
      return Math.abs(approvedQuantum - billedQuantum) < 0.0001 ? 'Matched' : 'Mismatch';
    }
    const expectedAmount = this.toNumberOrNull(row.expectedAmount ?? row.totalAmount);
    const submittedAmount = this.toNumberOrNull(row.submittedAmount);
    if (expectedAmount === null || submittedAmount === null) return 'Pending';
    return Math.abs(expectedAmount - submittedAmount) < 0.01 ? 'Matched' : 'Mismatch';
  }

  protected reviewRowMatchTone(row: SellerBillGenerateRow, kind: 'seller' | 'loa' | 'rate' | 'quantum' | 'amount'): 'success' | 'warning' | 'danger' | 'secondary' {
    const label = this.reviewRowMatchLabel(row, kind);
    if (label === 'Yes' || label === 'Matched') return 'success';
    if (label === 'No' || label === 'Mismatch') return 'danger';
    return 'secondary';
  }

  protected reviewRowStatusTone(row: SellerBillGenerateRow): 'success' | 'warning' | 'danger' | 'secondary' {
    const label = this.displayVerificationStatus(row);
    if (label === 'Matched') return 'success';
    if (label.includes('Mismatch')) return 'danger';
    return 'secondary';
  }

  protected lineStatusLabel(entry: BillEntryForm): string {
    const quantum = this.quantitiesMatch(entry.billedQuantum, this.currentExpectedQuantum());
    const amount = this.amountsMatch(entry.submittedAmount, this.currentExpectedAmount());
    if (quantum === false) return 'Quantum Mismatch';
    if (amount === false) return 'Amount Mismatch';
    if (this.currentReferenceRow()) return 'Matched';
    return 'Pending';
  }

  protected lineStatusTone(entry: BillEntryForm): 'success' | 'warning' | 'danger' | 'secondary' {
    const label = this.lineStatusLabel(entry);
    if (label === 'Matched') return 'success';
    if (label.includes('Mismatch')) return 'danger';
    return 'secondary';
  }

  protected fetchStatusLabel(entry: BillEntryForm): string {
    return entry.submittedAmount !== '' || entry.billedQuantum !== '' ? 'Fetched' : 'Pending';
  }

  protected fetchStatusTone(entry: BillEntryForm): 'success' | 'warning' | 'secondary' {
    return this.fetchStatusLabel(entry) === 'Fetched' ? 'success' : 'secondary';
  }

  protected lineMatchCheckLabel(entry: BillEntryForm, kind: 'rate' | 'quantum' | 'amount'): string {
    const value =
      kind === 'rate'
        ? this.lineRatesMatch(entry)
        : kind === 'quantum'
          ? this.quantitiesMatch(entry.billedQuantum, this.currentExpectedQuantum())
          : this.amountsMatch(entry.submittedAmount, this.currentExpectedAmount());
    return value === true ? 'Matched' : value === false ? 'Mismatch' : 'Pending';
  }

  protected lineMatchCheckTone(entry: BillEntryForm, kind: 'rate' | 'quantum' | 'amount'): 'success' | 'warning' | 'danger' | 'secondary' {
    const label = this.lineMatchCheckLabel(entry, kind);
    if (label === 'Matched') return 'success';
    if (label === 'Mismatch') return 'danger';
    return 'secondary';
  }

  protected stepTwoReviewRows(entry?: BillEntryForm): Array<{
    type: 'Rate' | 'Quantum' | 'Amount';
    loaValue: string;
    submittedValue: string;
    status: string;
    tone: 'success' | 'warning' | 'danger' | 'secondary';
  }> {
    const activeEntry = entry ?? this.activeBillLineEntries()[0];
    const submittedRate = this.editingId() && !entry
      ? this.toNumberOrNull(this.submittedRate)
      : this.toNumberOrNull(activeEntry?.submittedRate);
    const billedQuantum = this.editingId() && !entry
      ? this.toNumberOrNull(this.billedQuantum)
      : this.toNumberOrNull(activeEntry?.billedQuantum);
    const submittedAmount = this.editingId() && !entry
      ? this.toNumberOrNull(this.submittedAmount)
      : this.toNumberOrNull(activeEntry?.submittedAmount);
    return [
      {
        type: 'Rate',
        loaValue: this.currentExpectedRateLabel(),
        submittedValue: this.formatRate(submittedRate),
        status: entry ? this.lineMatchCheckLabel(entry, 'rate') : this.matchCheckLabel('rate'),
        tone: entry ? this.lineMatchCheckTone(entry, 'rate') : this.matchCheckTone('rate'),
      },
      {
        type: 'Quantum',
        loaValue: this.formatQuantum(this.currentExpectedQuantum()),
        submittedValue: this.formatQuantum(billedQuantum),
        status: entry ? this.lineMatchCheckLabel(entry, 'quantum') : this.matchCheckLabel('quantum'),
        tone: entry ? this.lineMatchCheckTone(entry, 'quantum') : this.matchCheckTone('quantum'),
      },
      {
        type: 'Amount',
        loaValue: this.formatAmount(this.currentExpectedAmount()),
        submittedValue: this.formatAmount(submittedAmount),
        status: entry ? this.lineMatchCheckLabel(entry, 'amount') : this.matchCheckLabel('amount'),
        tone: entry ? this.lineMatchCheckTone(entry, 'amount') : this.matchCheckTone('amount'),
      },
    ];
  }

  protected approvalDialogLineSummaries(): ApprovalDialogLineSummary[] {
    const loaNumber = this.selectedLoaNumber();
    return this.activeBillLineEntries().map((entry) => {
      const mismatchLabels: string[] = [];
      if (this.lineRatesMatch(entry) === false) mismatchLabels.push('Rate');
      if (this.quantitiesMatch(entry.billedQuantum, this.currentExpectedQuantum()) === false) mismatchLabels.push('Quantum');
      if (this.amountsMatch(entry.submittedAmount, this.currentExpectedAmount()) === false) mismatchLabels.push('Amount');

      const allMatched =
        this.lineRatesMatch(entry) === true &&
        this.quantitiesMatch(entry.billedQuantum, this.currentExpectedQuantum()) === true &&
        this.amountsMatch(entry.submittedAmount, this.currentExpectedAmount()) === true;
      const status = mismatchLabels.length
        ? `Mismatched ${mismatchLabels.join('/')}`
        : allMatched
          ? 'Matched'
          : 'Pending';
      const tone: ApprovalDialogLineSummary['tone'] = mismatchLabels.length
        ? 'danger'
        : allMatched
          ? 'success'
          : 'secondary';

      return {
        loaNumber: loaNumber || this.currentReferenceRow()?.loaNumber || '-',
        billPeriodFrom: String(entry.billPeriodFrom || '').trim(),
        billPeriodTo: String(entry.billPeriodTo || '').trim(),
        quantum: this.toNumberOrNull(entry.billedQuantum),
        rate: this.toNumberOrNull(entry.submittedRate),
        amount: this.toNumberOrNull(entry.submittedAmount),
        status,
        tone,
      };
    });
  }

  protected matchCheckLabel(kind: 'seller' | 'loa' | 'rate' | 'quantum' | 'amount'): string {
    if (kind === 'seller') return this.sellerVendorId ? 'Yes' : 'No';
    if (kind === 'loa') return String(this.billMappingLoaId || this.loaId || '').trim() ? 'Yes' : 'No';
    if (kind === 'rate') {
      const rateMatch = this.rateMatchesCurrentReference();
      return rateMatch === true ? 'Matched' : rateMatch === false ? 'Mismatch' : 'Pending';
    }
    if (kind === 'quantum') {
      const value = this.quantumMatchesCurrentReference();
      return value === true ? 'Matched' : value === false ? 'Mismatch' : 'Pending';
    }
    const value = this.amountMatchesCurrentReference();
    return value === true ? 'Matched' : value === false ? 'Mismatch' : 'Pending';
  }

  protected matchCheckTone(kind: 'seller' | 'loa' | 'rate' | 'quantum' | 'amount'): 'success' | 'warning' | 'danger' | 'secondary' {
    const label = this.matchCheckLabel(kind);
    if (label === 'Yes' || label === 'Matched') return 'success';
    if (label === 'No' || label === 'Not Matched' || label === 'Mismatch') return 'danger';
    return 'secondary';
  }

  protected submit(postSubmitAction: BillApprovalAction | null = null, approvalReason?: string): void {
    if (this.isBillViewOnlyAdmin()) {
      this.toast.show('Access denied', 'This role can only view invoice data in bill verification.', 'danger');
      return;
    }
    if (!this.loaId || !this.invoiceNumber.trim() || !this.invoiceDate.trim()) {
      this.toast.show('Missing details', 'Seller LOA, invoice number, and invoice date are required.', 'danger');
      return;
    }
    if (!this.editingId() && !this.billEntries()[0]?.billDate) {
      this.toast.show('Missing bill date', 'Bill date is required.', 'danger');
      return;
    }
    if (this.editingId() && !this.billDate) {
      this.toast.show('Missing details', 'Bill date is required.', 'danger');
      return;
    }

    const payload = this.editingId() ? this.buildEditFormData() : this.buildCreateFormData();
    const request$: Observable<BillVerification | BillVerification[] | null> = this.editingId()
      ? this.http.patch<BillVerification>(this.api.resolve(`/api/bill-verifications/${this.editingId()}`), payload, { withCredentials: true })
      : this.http.post<BillVerification[]>(this.api.resolve('/api/bill-verifications'), payload, { withCredentials: true });
    this.saving.set(true);
    request$
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const message = typeof error.error?.detail === 'string' && error.error.detail.trim()
            ? error.error.detail.trim()
            : 'Bill verification could not be completed.';
          this.toast.show('Submission failed', message, 'danger');
          return of(null);
        }),
      )
      .subscribe((response: BillVerification | BillVerification[] | null) => {
        this.saving.set(false);
        if (!response) {
          return;
        }
        const rows = Array.isArray(response) ? response : [response];
        this.verifications.update((items) => {
          const ids = new Set(rows.map((row) => row.id));
          const filtered = items.filter((item) => !ids.has(item.id));
          return [...rows, ...filtered];
        });
        const first = rows[0];
        if (postSubmitAction) {
          this.runApprovalActionForRows(rows, postSubmitAction, approvalReason);
          return;
        }
        this.toast.show(
          this.editingId() ? 'Bill updated' : 'Bills submitted',
          this.editingId()
            ? `${first.invoice_number} submitted and waiting for approval.`
            : `${rows.length} bill line(s) submitted for invoice ${first.invoice_number} and waiting for approval.`,
          'success',
        );
        this.closeApprovalReasonDialog();
        this.resetFormAfterSubmit();
      });
  }

  protected verifyAndSubmit(): void {
    if (!this.prepareSubmitApprovalDialog()) return;
    if (!this.isApprovalAdmin() && !this.currentSubmitHasAnyBillLineMismatch()) {
      this.submit();
      return;
    }
    this.approvalReasonDialogOpen.set(true);
  }

  protected edit(row: BillVerification): void {
    this.showForm.set(true);
    this.showBillSection.set(true);
    this.showVerificationSection.set(false);
    this.applyVerificationToForm(row);
    this.scrollToInvoiceSection();
  }

  protected cancelEdit(): void {
    this.resetFormAfterSubmit();
  }

  protected openAddInvoice(): void {
    if (this.isBillViewOnlyAdmin()) {
      this.toast.show('Access denied', 'This role can only view invoice data in bill verification.', 'danger');
      return;
    }
    this.resetFormAfterSubmit();
    this.showForm.set(true);
    this.showBillSection.set(false);
    this.showVerificationSection.set(false);
    this.scrollToInvoiceSection();
  }

  protected isSellerVerificationStepActive(step: 1 | 2 | 3): boolean {
    if (step === 1) return this.showForm() && !this.showBillSection() && !this.showVerificationSection();
    if (step === 2) return this.showForm() && this.showBillSection() && !this.showVerificationSection();
    return this.showForm() && this.showVerificationSection();
  }

  protected isSellerVerificationStepCompleted(step: 1 | 2 | 3): boolean {
    if (step === 1) return this.showForm() && (this.showBillSection() || this.showVerificationSection());
    if (step === 2) return this.showForm() && this.showVerificationSection();
    return false;
  }

  protected useSellerBill(row: SellerBillGenerateRow, openAtUploadStep = false): void {
    const isStepTwoActive = this.showForm() && this.showBillSection() && !this.showVerificationSection();
    const redirectRow = openAtUploadStep ? this.withResolvedConfiguredRate(row) : row;

    this.showForm.set(true);
    this.showBillSection.set(!openAtUploadStep);
    this.showVerificationSection.set(false);
    this.selectedSellerBillRowKey.set(redirectRow.rowKey);
    this.invoiceRedirectReferenceRow.set(openAtUploadStep ? redirectRow : null);

    if (!openAtUploadStep && isStepTwoActive) {
      this.bumpSelectionVersion();
      this.scrollToInvoiceSection();
      return;
    }

    const existingVerification = row.verificationId
      ? this.verifications().find((item) => item.id === row.verificationId) || null
      : null;

    if (existingVerification) {
      this.applyVerificationToForm(existingVerification, true);
      this.selectedSellerBillRowKey.set(redirectRow.rowKey);
      if (openAtUploadStep) {
        this.applyOpenInvoiceRedirectContext(redirectRow);
      }
      this.showBillSection.set(!openAtUploadStep);
      this.showVerificationSection.set(false);
      if (!openAtUploadStep) {
        this.fetchSellerBillRowsForEdit(existingVerification, redirectRow.rowKey);
      }
      this.scrollToInvoiceSection();
      return;
    }

    this.editingId.set(null);
    this.sellerVendorId = redirectRow.sellerVendorId;
    if (openAtUploadStep) {
      this.applyOpenInvoiceRedirectContext(redirectRow);
    } else {
      this.buyerVendorId = redirectRow.buyerVendorId || this.buyerVendorId;
      this.syncContractFilters();
      if (redirectRow.contractId) {
        this.onContractChange([redirectRow.contractId]);
      }
    }
    this.loaId = redirectRow.loaId;
    this.billType = redirectRow.billType;
    this.invoiceNumber = redirectRow.invoiceNumber || '';
    if (openAtUploadStep) {
      this.invoiceDate = new Date().toISOString().slice(0, 10);
    }
    this.billEntries.set([
      {
        billDate: redirectRow.billDate,
        billPeriodFrom: redirectRow.billPeriodFrom,
        billPeriodTo: redirectRow.billPeriodTo,
        billedQuantum: redirectRow.billedQuantum ?? redirectRow.totalQuantum ?? '',
        submittedRate: redirectRow.submittedRate ?? '',
        submittedAmount: redirectRow.submittedAmount ?? redirectRow.expectedAmount ?? '',
      },
    ]);
    this.scrollToInvoiceSection();
  }

  private applyOpenInvoiceRedirectContext(row: SellerBillGenerateRow): void {
    const loaId = String(row.loaId || '').trim();
    const loa = this.loas().find((item) => String(item.id) === loaId) || null;
    const resolvedBuyerVendorId = String(row.buyerVendorId || loa?.buyerVendorId || '').trim();

    this.buyerVendorId = resolvedBuyerVendorId;
    this.onBuyerChange();

    const directContractId = String(row.contractId || '').trim();
    if (directContractId) {
      this.onContractChange([directContractId]);
      return;
    }

    if (loaId && resolvedBuyerVendorId) {
      const matchingContracts = this.contractOptions().filter((contract) =>
        this.contractIncludesVendor(contract, resolvedBuyerVendorId, 'buyer') &&
        this.contractSellerLoaIds(contract).includes(loaId),
      );
      if (matchingContracts.length === 1) {
        this.onContractChange([String(matchingContracts[0].id)]);
        return;
      }
    }

    this.contractId = '';
    this.selectedContractIds = [];
  }

  private applyVerificationToForm(row: BillVerification, preserveSelectedFile = false): void {
    this.editingId.set(row.id);
    this.sellerVendorId = row.seller_vendor_id;
    this.buyerVendorId = '';
    this.contractId = '';
    this.selectedContractIds = [];
    this.loaId = row.loa_id;
    this.billMappingLoaId = '';
    this.billType = (row.bill_type as BillTypeOption) || 'SOP';
    this.invoiceNumber = row.invoice_number || '';
    this.invoiceDate = row.invoice_date || '';
    this.billDate = row.bill_date || '';
    this.billPeriodFrom = row.bill_period_from || '';
    this.billPeriodTo = row.bill_period_to || '';
    this.billedQuantum = row.billed_quantum ?? '';
    this.submittedRate = row.submitted_rate ?? '';
    this.submittedAmount = row.submitted_amount ?? '';
    this.remarks = row.remarks || '';
    if (!preserveSelectedFile) {
      this.selectedFile = null;
    }
  }

  protected closeForm(): void {
    this.resetFormAfterSubmit();
  }

  protected cancelUploadedInvoice(): void {
    if (this.hasCurrentInvoiceFile()) {
      this.replaceSelectedFile();
      this.showForm.set(true);
      this.showBillSection.set(false);
      this.showVerificationSection.set(false);
      this.scrollToInvoiceSection();
      return;
    }

    this.showBillSection.set(false);
    this.showVerificationSection.set(false);
    this.replaceSelectedFile();
    this.closeForm();
  }

  protected goToBillSection(): void {
    if (!this.canProceedFromUploadSection()) {
      this.toast.show('Complete step 1', this.uploadSectionValidationMessage(), 'info');
      return;
    }
    this.showBillSection.set(true);
    this.showVerificationSection.set(false);
    if (this.sellerVendorId) {
      this.fetchSellerBillRowsForMapping();
    }
    this.scrollToInvoiceSection();
  }

  protected backToUploadSection(): void {
    this.showBillSection.set(false);
    this.showVerificationSection.set(false);
    this.scrollToInvoiceSection();
  }

  protected goToVerificationSection(): void {
    if (!this.canProceedFromUploadSection()) {
      this.toast.show('Complete step 1', this.uploadSectionValidationMessage(), 'info');
      return;
    }
    if (!this.canProceedFromBillSection()) {
      this.toast.show('Complete step 2', this.billSectionValidationMessage(), 'info');
      return;
    }
    const billLineValidationMessage = this.billLineValidationMessage();
    if (billLineValidationMessage) {
      this.toast.show('Complete bill line details', billLineValidationMessage, 'info');
      return;
    }
    this.showBillSection.set(true);
    this.showVerificationSection.set(true);
    this.scrollToInvoiceSection();
  }

  protected backToBillSection(): void {
    this.showVerificationSection.set(false);
    this.scrollToInvoiceSection();
  }

  protected hidePreviewPanel(): void {
    this.showPreviewPanel.set(false);
  }

  protected setBillType(value: BillTypeOption): void {
    if (this.billTypeLockedByUpload && this.billType !== value) return;
    this.billType = value;
  }

  protected isBillTypeOptionDisabled(value: BillTypeOption): boolean {
    return this.billTypeLockedByUpload && this.billType !== value;
  }

  protected canPreviewCurrentInvoiceFile(): boolean {
    if (this.selectedFile) return true;
    if (this.suppressStoredInvoiceFile()) return false;
    const row = this.currentEditingVerification();
    return !!row?.invoice_download_url;
  }

  protected previewCurrentInvoiceFile(): void {
    if (!this.showPreviewPanel()) {
      this.showPreviewPanel.set(true);
      this.scrollToInvoiceSection();
      return;
    }

    if (this.selectedFile) {
      const objectUrl = URL.createObjectURL(this.selectedFile);
      const opened = this.openPdfPreviewWindow(objectUrl, this.selectedFile.name || 'invoice.pdf');
      if (!opened) {
        this.toast.show('Preview blocked', 'Please allow pop-ups to preview the invoice file.', 'info');
      } else {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      }
      return;
    }

    if (this.suppressStoredInvoiceFile()) {
      this.toast.show('No file available', 'There is no invoice file available to preview yet.', 'info');
      return;
    }

    const row = this.currentEditingVerification();
    if (!row?.invoice_download_url) {
      this.toast.show('No file available', 'There is no invoice file available to preview yet.', 'info');
      return;
    }
    window.open(this.api.resolve(`/api/bill-verifications/${row.id}/preview`), '_blank');
  }

  protected currentInvoicePreviewUrl(): string | null {
    if (this.selectedFile) {
      if (this.invoicePreviewSourceFile !== this.selectedFile || !this.invoicePreviewObjectUrl) {
        this.revokeInvoicePreviewObjectUrl();
        this.invoicePreviewObjectUrl = URL.createObjectURL(this.selectedFile);
        this.invoicePreviewSourceFile = this.selectedFile;
      }
      return `${this.invoicePreviewObjectUrl}#toolbar=0&navpanes=0&scrollbar=1`;
    }

    this.revokeInvoicePreviewObjectUrl();
    if (this.suppressStoredInvoiceFile()) return null;
    const row = this.currentEditingVerification();
    if (!row?.invoice_download_url) return null;
    return `${this.api.resolve(`/api/bill-verifications/${row.id}/preview`)}#toolbar=0&navpanes=0&scrollbar=1`;
  }

  protected trustedCurrentInvoicePreviewUrl(): SafeResourceUrl | null {
    const url = this.currentInvoicePreviewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  }

  protected previewValue(value: unknown, fallback = '-'): string {
    const text = String(value ?? '').trim();
    return text || fallback;
  }

  protected currentInvoicePreviewTitle(): string {
    const selectedName = String(this.selectedFile?.name || '').trim();
    if (selectedName) return selectedName;
    if (this.suppressStoredInvoiceFile()) return 'invoice.pdf';
    const row = this.currentEditingVerification();
    return String(row?.invoice_file_original_name || row?.invoice_number || 'invoice.pdf').trim() || 'invoice.pdf';
  }

  private openPdfPreviewWindow(fileUrl: string, fileName: string): boolean {
    const previewWindow = window.open('', '_blank');
    if (!previewWindow) return false;

    const safeTitle = this.escapeHtml(fileName || 'invoice.pdf');
    const safeSrc = this.escapeHtml(`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1`);
    previewWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${safeTitle}</title>
    <style>
      :root { color-scheme: light; }
      html, body { margin: 0; height: 100%; background: #0f172a; font-family: Arial, sans-serif; }
      .shell { display: grid; grid-template-rows: auto 1fr; height: 100%; }
      .topbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 16px;
        background: linear-gradient(180deg, #1e293b 0%, #111827 100%);
        color: #f8fafc;
        border-bottom: 1px solid rgba(148, 163, 184, 0.25);
      }
      .title {
        min-width: 0;
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .badge {
        flex: 0 0 auto;
        font-size: 12px;
        color: #cbd5e1;
        background: rgba(148, 163, 184, 0.16);
        border: 1px solid rgba(148, 163, 184, 0.22);
        border-radius: 999px;
        padding: 4px 10px;
      }
      .viewer {
        width: 100%;
        height: 100%;
        border: 0;
        background: #ffffff;
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <div class="topbar">
        <div class="title">${safeTitle}</div>
        <div class="badge">PDF Preview</div>
      </div>
      <embed class="viewer" src="${safeSrc}" type="application/pdf" />
    </div>
  </body>
</html>`);
    previewWindow.document.close();
    return true;
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  protected canDownloadCurrentInvoiceCopy(): boolean {
    if (this.selectedFile) return true;
    if (this.suppressStoredInvoiceFile()) return false;
    const row = this.currentEditingVerification();
    return !!row?.invoice_download_url;
  }

  protected hasCurrentInvoiceFile(): boolean {
    if (this.selectedFile) return true;
    if (this.suppressStoredInvoiceFile()) return false;
    return !!this.currentEditingVerification()?.invoice_download_url;
  }

  protected hasPrefilledSellerBillContext(): boolean {
    if (!!this.currentReferenceRow()) return true;
    if (!!this.selectedSellerBillRowKey()) return true;
    if (!!String(this.sellerVendorId || '').trim() && !!String(this.loaId || '').trim()) return true;

    const firstEntry = this.billEntries()[0];
    return !!String(firstEntry?.billDate || firstEntry?.billPeriodFrom || firstEntry?.billPeriodTo || '').trim();
  }

  protected showUploadContextContent(): boolean {
    return this.hasCurrentInvoiceFile() || !!this.editingId() || this.hasPrefilledSellerBillContext();
  }

  protected canProceedFromUploadSection(): boolean {
    if (this.uploadSectionError().trim()) return false;
    const hasInvoiceNumber = !!String(this.invoiceNumber || '').trim();
    const hasInvoiceDate = !!String(this.invoiceDate || '').trim();
    return hasInvoiceNumber && hasInvoiceDate && (this.hasCurrentInvoiceFile() || !!this.editingId() || this.hasPrefilledSellerBillContext());
  }

  protected canProceedFromBillSection(): boolean {
    const hasBuyer = !!String(this.buyerVendorId || '').trim();
    const hasContract = !!String(this.contractId || '').trim() || this.selectedContractIds.some((id) => !!String(id || '').trim());
    const hasSellerLoa = !!String(this.loaId || '').trim();
    return hasBuyer && hasContract && hasSellerLoa;
  }

  private uploadSectionValidationMessage(): string {
    const uploadError = this.uploadSectionError().trim();
    if (uploadError) return uploadError;
    const hasInvoiceNumber = !!String(this.invoiceNumber || '').trim();
    const hasInvoiceDate = !!String(this.invoiceDate || '').trim();
    const hasSource = this.hasCurrentInvoiceFile() || !!this.editingId() || this.hasPrefilledSellerBillContext();

    if (!hasSource) return 'Upload an invoice or open a seller bill before continuing.';
    if (!hasInvoiceNumber && !hasInvoiceDate) return 'Enter invoice number and invoice date before continuing.';
    if (!hasInvoiceNumber) return 'Enter invoice number before continuing.';
    if (!hasInvoiceDate) return 'Enter invoice date before continuing.';
    return 'Complete the required invoice details before continuing.';
  }

  private billSectionValidationMessage(): string {
    const missing: string[] = [];
    if (!String(this.buyerVendorId || '').trim()) missing.push('buyer');
    if (!String(this.contractId || '').trim() && !this.selectedContractIds.some((id) => !!String(id || '').trim())) missing.push('contract');
    if (!String(this.loaId || '').trim()) missing.push('seller LOA');

    if (!missing.length) return 'Complete the required bill details before continuing.';
    return `Select ${missing.join(', ')} before continuing.`;
  }

  private billLineValidationMessage(): string | null {
    if (this.editingId()) {
      const missing: string[] = [];
      if (!String(this.billDate || this.invoiceDate || '').trim()) missing.push('bill date');
      if (!String(this.billPeriodFrom || '').trim()) missing.push('bill period from');
      if (!String(this.billPeriodTo || '').trim()) missing.push('bill period to');
      if (this.toNumberOrNull(this.billedQuantum) === null) missing.push('billed quantum');
      if (this.toNumberOrNull(this.submittedRate) === null) missing.push('submitted rate');
      if (this.toNumberOrNull(this.submittedAmount) === null) missing.push('submitted amount');
      return missing.length ? `Complete ${missing.join(', ')} before continuing.` : null;
    }

    const entries = this.billEntries();
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index];
      const missing: string[] = [];
      if (!String(entry.billDate || '').trim()) missing.push('bill date');
      if (!String(entry.billPeriodFrom || '').trim()) missing.push('bill period from');
      if (!String(entry.billPeriodTo || '').trim()) missing.push('bill period to');
      if (this.toNumberOrNull(entry.billedQuantum) === null) missing.push('billed quantum');
      if (this.toNumberOrNull(entry.submittedRate) === null) missing.push('submitted rate');
      if (this.toNumberOrNull(entry.submittedAmount) === null) missing.push('submitted amount');
      if (missing.length) {
        return `Complete ${missing.join(', ')} in bill line ${index + 1} before continuing.`;
      }
    }

    return null;
  }

  protected downloadCurrentInvoiceCopy(): void {
    if (this.selectedFile) {
      this.downloadPdfWithTitle(this.selectedFile, this.selectedFile.name || 'invoice.pdf');
      return;
    }

    if (this.suppressStoredInvoiceFile()) {
      this.toast.show('No file available', 'There is no invoice copy available to download yet.', 'info');
      return;
    }

    const row = this.currentEditingVerification();
    if (!row?.invoice_download_url) {
      this.toast.show('No file available', 'There is no invoice copy available to download yet.', 'info');
      return;
    }
    const downloadName = row.invoice_file_original_name?.trim() || `${row.invoice_number || 'invoice'}.pdf`;
    this.http
      .get(this.api.resolve(row.invoice_download_url), { withCredentials: true, responseType: 'blob' })
      .pipe(
        catchError(() => {
          this.toast.show('Download failed', 'Invoice copy could not be downloaded.', 'danger');
          return of(null);
        }),
      )
      .subscribe((blob) => {
        if (!blob) return;
        this.downloadPdfWithTitle(blob, downloadName);
      });
  }

  private async downloadPdfWithTitle(source: Blob, fileName: string): Promise<void> {
    const finalName = (fileName || 'invoice.pdf').trim() || 'invoice.pdf';
    try {
      const patchedBlob = await this.buildPdfDownloadBlob(source, finalName);
      const objectUrl = URL.createObjectURL(patchedBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = finalName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch {
      const objectUrl = URL.createObjectURL(source);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = finalName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
  }

  private async buildPdfDownloadBlob(source: Blob, fileName: string): Promise<Blob> {
    if ((source.type || '').toLowerCase() !== 'application/pdf' && !fileName.toLowerCase().endsWith('.pdf')) {
      return source;
    }

    const buffer = await source.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const text = new TextDecoder('latin1').decode(bytes);
    const startXrefMatches = [...text.matchAll(/startxref\s+(\d+)\s+%%EOF/gi)];
    const sizeMatches = [...text.matchAll(/trailer\s*<<[\s\S]*?\/Size\s+(\d+)/gi)];
    const previousXref = startXrefMatches.length ? Number(startXrefMatches.at(-1)?.[1] || '') : NaN;
    const currentSize = sizeMatches.length ? Number(sizeMatches.at(-1)?.[1] || '') : NaN;

    if (!Number.isFinite(previousXref) || !Number.isFinite(currentSize) || currentSize <= 0) {
      return source;
    }

    const infoObjectNumber = currentSize;
    const infoObjectBody = `${infoObjectNumber} 0 obj\n<< /Title (${this.escapePdfString(fileName)}) >>\nendobj\n`;
    const infoBytes = new TextEncoder().encode(infoObjectBody);
    const infoOffset = bytes.length;
    const xrefOffset = infoOffset + infoBytes.length;
    const xrefBody =
      `xref\n${infoObjectNumber} 1\n${String(infoOffset).padStart(10, '0')} 00000 n \n` +
      `trailer\n<< /Size ${infoObjectNumber + 1} /Info ${infoObjectNumber} 0 R /Prev ${previousXref} >>\n` +
      `startxref\n${xrefOffset}\n%%EOF`;
    const xrefBytes = new TextEncoder().encode(xrefBody);

    return new Blob([bytes, infoBytes, xrefBytes], { type: 'application/pdf' });
  }

  private escapePdfString(value: string): string {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)');
  }

  protected replaceSelectedFile(): void {
    this.showBillSection.set(false);
    this.showVerificationSection.set(false);
    this.showPreviewPanel.set(false);
    this.clearExtractedInvoiceData();
    this.clearSelectedFile();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const nextFile = input?.files?.[0] || null;
    if (!nextFile) {
      this.clearSelectedFile();
      return;
    }
    const fileName = String(nextFile.name || '').trim().toLowerCase();
    const isPdf =
      nextFile.type === 'application/pdf' ||
      fileName.endsWith('.pdf');

    const isJpg =
      nextFile.type === 'image/jpeg' ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg');

    const isPng =
      nextFile.type === 'image/png' ||
      fileName.endsWith('.png');
    
      console.log()
    if (!isPdf && !isJpg && !isPng) {
      this.clearSelectedFile();
      this.toast.show('Invalid file type', 'Only PDF/PNG/JPEG/JPG invoice files are allowed.', 'danger');
      return;
    }

    this.selectedFile = nextFile;
    this.suppressStoredInvoiceFile.set(false);
    this.showPreviewPanel.set(true);
    this.extractAndApplyInvoiceDetails(this.selectedFile);
  }

  protected downloadFile(row: BillVerification): void {
    if (!row.invoice_download_url) return;
    window.open(this.api.resolve(row.invoice_download_url), '_blank');
  }

  protected delete(row: BillVerification): void {
    if (this.isBillViewOnlyAdmin()) {
      this.toast.show('Access denied', 'This role can only view invoice data in bill verification.', 'danger');
      return;
    }
    const confirmed = window.confirm(`Delete invoice ${row.invoice_number}?`);
    if (!confirmed) return;

    this.http
      .delete(this.api.resolve(`/api/bill-verifications/${row.id}`), { withCredentials: true })
      .subscribe({
        next: () => {
          this.verifications.update((items) => items.filter((item) => item.id !== row.id));
          if (this.editingId() === row.id) {
            this.resetFormAfterSubmit();
          }
          this.toast.show('Deleted', `${row.invoice_number} removed successfully.`, 'success');
        },
        error: () => {
          this.toast.show('Delete failed', 'Bill verification could not be deleted.', 'danger');
        },
      });
  }

  protected deleteSellerBill(row: SellerBillGenerateRow): void {
    if (this.isBillViewOnlyAdmin()) {
      this.toast.show('Access denied', 'This role can only view invoice data in bill verification.', 'danger');
      return;
    }
    const key = String(row.rowKey || '').trim();

    this.hiddenSellerBillRowKeys.update((items) => {
      if (!key || items.includes(key)) return items;
      return [...items, key];
    });

    if (key) {
      this.manualSellerBillRows.update((items) => items.filter((item) => String(item.rowKey || '').trim() !== key));
    }

    this.billEntries.update((items) => {
      const filtered = items.filter((entry) => !this.billEntryMatchesSellerBillRow(entry, row));

      return filtered.length ? filtered : [this.createEmptyBillEntry()];
    });

    if (this.editingId()) {
      const activeEditEntry: BillEntryForm = {
        billDate: this.billDate,
        billPeriodFrom: this.billPeriodFrom,
        billPeriodTo: this.billPeriodTo,
        billedQuantum: this.billedQuantum,
        submittedRate: this.submittedRate,
        submittedAmount: this.submittedAmount,
      };

      if (this.billEntryMatchesSellerBillRow(activeEditEntry, row)) {
        this.billDate = '';
        this.billPeriodFrom = '';
        this.billPeriodTo = '';
        this.billedQuantum = '';
        this.submittedRate = '';
        this.submittedAmount = '';
      }
    }

    if (this.selectedSellerBillRowKey() === row.rowKey) {
      this.selectedSellerBillRowKey.set(null);
    }

    const remainingVisibleRows = Math.max(this.tableSellerBillRows().length - 1, 0);
    const maxPage = Math.max(1, Math.ceil(remainingVisibleRows / this.sellerBillsPageSize()));
    if (this.sellerBillsPage() > maxPage) {
      this.sellerBillsPage.set(maxPage);
    }

    this.bumpSelectionVersion();
    this.toast.show('Bill row removed', 'The selected bill row was removed from the table list.', 'success');
  }

  protected canManageBillApproval(): boolean {
    const role = (this.auth.user()?.role || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    return ['accounts', 'account'].includes(role);
  }

  protected canUploadInvoices(): boolean {
    return this.isVendor() || this.canManageBillApproval();
  }

  protected shouldShowReviewDecision(row: SellerBillGenerateRow): boolean {
    return !!row.verificationId && this.canManageBillApproval() && this.displayApprovalStatus(row) === 'Pending';
  }

  protected shouldShowOpenVerification(row: SellerBillGenerateRow): boolean {
    if (!row.verificationId) return true;
    return this.displayApprovalStatus(row) !== 'Approved';
  }

  protected invoiceActionLabel(row: SellerBillGenerateRow): string {
    if (!this.canUploadInvoices()) {
      return 'View Invoice';
    }
    return row.verificationId ? 'Edit Invoice' : 'Add Invoice';
  }

  protected canShowReadOnlyStepThreeAction(): boolean {
    return this.isReadOnlyViewMode() && this.canManageBillApproval() && !!this.currentReadOnlyReviewRow()?.verificationId;
  }

  protected stepThreePrimaryActionLabel(): string {
    if (this.canShowReadOnlyStepThreeAction()) {
      return 'Review & Verify';
    }
    return this.saving() ? 'Submitting...' : this.editingId() ? 'Update & Submit' : 'Verify & Submit';
  }

  protected handleStepThreePrimaryAction(): void {
    if (this.canShowReadOnlyStepThreeAction()) {
      const row = this.currentReadOnlyReviewRow();
      if (row) {
        this.openApprovalDecisionDialog(row);
      }
      return;
    }
    this.verifyAndSubmit();
  }

  protected shouldShowVerificationStatus(row: SellerBillGenerateRow): boolean {
    return this.displayApprovalStatus(row) !== 'Approved';
  }

  protected displayVerificationStatus(value: string | SellerBillGenerateRow | null | undefined): string {
    if (value && typeof value === 'object' && 'loaId' in value) {
      return this.rowVerificationStatus(value);
    }
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'verified') return 'Matched';
    return (value || '').trim() || 'Pending';
  }

  protected displayApprovalStatus(row: SellerBillGenerateRow | BillVerification): string {
    const raw = ('approvalStatus' in row ? row.approvalStatus : row.approval_status) || null;
    const normalized = (raw || '').trim().toLowerCase();
    if (!normalized || normalized === 'pending approval') return 'Pending';
    if (normalized === 'approved') return 'Approved';
    if (normalized === 'force approved') return 'Force Approved';
    if (normalized === 'rejected') return 'Rejected';
    return (raw || '').trim() || 'Pending';
  }

  protected approvalStatusVariant(value: string | null | undefined): 'success' | 'warning' | 'danger' | 'secondary' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'approved') return 'success';
    if (normalized === 'force approved') return 'warning';
    if (normalized === 'rejected') return 'danger';
    return 'secondary';
  }

  protected openApprovalDecisionDialog(row: SellerBillGenerateRow): void {
    if (!row.verificationId) {
      this.toast.show('Verification required', 'Create the bill verification before taking an approval decision.', 'danger');
      return;
    }
    if (!this.canManageBillApproval()) {
      this.toast.show('Access denied', 'Only accounts members can take bill approval actions.', 'danger');
      return;
    }
    this.pendingApprovalDialogMode = 'review';
    this.pendingApprovalAction = this.defaultApprovalAction(row.approvalStatus);
    this.pendingApprovalVerificationId = row.verificationId;
    this.pendingApprovalInvoiceNumber = row.invoiceNumber || row.applicationNumber || row.loaNumber || 'this bill';
    this.pendingApprovalSummary = {
      invoiceNumber: row.invoiceNumber || '-',
      loaNumber: row.loaNumber || '-',
      billPeriodFrom: row.billPeriodFrom || '',
      billPeriodTo: row.billPeriodTo || '',
      quantum: row.billedQuantum ?? row.totalQuantum ?? null,
      rate: row.submittedRate ?? null,
      amount: row.submittedAmount ?? row.expectedAmount ?? row.totalAmount ?? null,
    };
    this.pendingApprovalStatusLabel = this.rowVerificationStatus(row);
    this.pendingApprovalStatusTone = this.statusVariant(row.verificationStatus);
    this.pendingApprovalAllowActionSelection = true;
    this.approvalReason = row.approvalReason || '';
    this.approvalReasonDialogOpen.set(true);
  }

  protected closeApprovalReasonDialog(): void {
    this.approvalReasonDialogOpen.set(false);
    this.pendingApprovalDialogMode = 'review';
    this.pendingApprovalAction = null;
    this.pendingApprovalVerificationId = null;
    this.pendingApprovalInvoiceNumber = '';
    this.pendingApprovalSummary = null;
    this.pendingApprovalStatusLabel = 'Pending';
    this.pendingApprovalStatusTone = 'secondary';
    this.pendingApprovalAllowActionSelection = true;
    this.approvalReason = '';
  }

  protected submitApprovalReason(): void {
    const reason = this.approvalReason.trim();
    if (!reason) {
      this.toast.show('Reason required', 'Please enter a reason before continuing.', 'danger');
      return;
    }
    if (this.pendingApprovalDialogMode === 'submit') {
      this.submit(this.pendingApprovalAction, reason || undefined);
      return;
    }
    if (!this.pendingApprovalVerificationId || !this.pendingApprovalAction) return;
    this.runApprovalAction(this.pendingApprovalVerificationId, this.pendingApprovalAction, reason || undefined);
  }

  protected approvalActionLabel(action: BillApprovalAction | null): string {
    if (action === 'approve') return 'Approve';
    if (action === 'reject') return 'Reject';
    if (action === 'force-approve') return 'Force Approve';
    return 'Select Action';
  }

  protected openAddSellerBillRowDialog(): void {
    this.manualSellerBillRowForm = this.createManualSellerBillRowForm();
    this.addSellerBillRowDialogOpen.set(true);
  }

  protected closeAddSellerBillRowDialog(): void {
    this.addSellerBillRowDialogOpen.set(false);
    this.manualSellerBillRowForm = this.createManualSellerBillRowForm();
  }

  protected submitAddSellerBillRow(): void {
    const form = this.manualSellerBillRowForm;
    const loaId = String(form.loaId || this.loaId || '').trim();
    const billPeriodFrom = String(form.billPeriodFrom || '').trim();
    const billPeriodTo = String(form.billPeriodTo || '').trim();
    if (!loaId || !billPeriodFrom || !billPeriodTo) {
      this.toast.show('Missing details', 'LOA, bill period from, and bill period to are required.', 'danger');
      return;
    }

    const buyerVendorId = String(form.buyerVendorId || this.buyerVendorId || '').trim() || null;
    const contractId = String(form.contractId || this.contractId || '').trim() || null;
    const loa = this.loas().find((item) => String(item.id) === loaId);
    const buyerName = buyerVendorId ? this.vendors().find((item) => String(item.id) === buyerVendorId)?.vendor_name || buyerVendorId : 'No buyer linked';
    const contract = contractId ? this.contracts().find((item) => String(item.id) === contractId) || null : null;
    const approvedQuantum = this.toNumberOrNull(form.approvedQuantum);
    const billedQuantum = this.toNumberOrNull(form.billedQuantum);
    const configuredRateValue = this.toNumberOrNull(form.loaRate);
    const submittedRate = this.toNumberOrNull(form.submittedRate);
    const expectedAmount = this.toNumberOrNull(form.expectedAmount);
    const submittedAmount = this.toNumberOrNull(form.submittedAmount);
    const billCycle = String(form.billCycle || '').trim();
    const manualEntries = this.buildBillEntriesForCycle(
      {
        billDate: String(form.billDate || '').trim(),
        billPeriodFrom,
        billPeriodTo,
        billedQuantum: billedQuantum ?? '',
        submittedRate: submittedRate ?? '',
        submittedAmount: submittedAmount ?? '',
      },
      billCycle,
    );
    const manualRows = this.buildManualSellerBillRowsForCycle({
      loaId,
      loaNumber: loa?.loa_number || loaId,
      sellerVendorId: String(this.sellerVendorId || '').trim(),
      sellerName: this.selectedSellerName(),
      buyerVendorId,
      buyerName,
      contractId,
      contractNumber: contract?.contract_number || contractId || 'No contract linked',
      billType: form.billType,
      billCycle,
      approvedQuantum,
      billedQuantum,
      configuredRateValue,
      submittedRate,
      expectedAmount,
      submittedAmount,
      billPeriodFrom,
      billPeriodTo,
      billDate: String(form.billDate || '').trim(),
    });

    this.buyerVendorId = String(buyerVendorId || '').trim();
    this.onBuyerChange();
    if (contractId) {
      this.onContractChange([contractId]);
    }
    this.onLoaChange(loaId);
    this.billType = form.billType;

    const nextEntries = manualEntries.length ? manualEntries : [this.createEmptyBillEntry()];
    const existingEntries = this.meaningfulBillLineEntries();
    this.billEntries.set(existingEntries.length ? [...existingEntries, ...nextEntries] : nextEntries);
    this.manualSellerBillRows.update((items) => [...items, ...manualRows]);
    this.selectedSellerBillRowKey.set(manualRows[0]?.rowKey || null);
    this.bumpSelectionVersion();
    this.addSellerBillRowDialogOpen.set(false);
    this.manualSellerBillRowForm = this.createManualSellerBillRowForm();
    this.sellerBillsPage.set(1);
  }

  protected approvalActionDescription(action: BillApprovalAction): string {
    if (action === 'approve') return 'Use this when the bill is acceptable and can move ahead normally.';
    if (action === 'reject') return 'Use this when the bill should be sent back with a clear rejection remark.';
    return 'Use this when you want to override issues and approve with a mandatory justification.';
  }

  protected approvalDialogSubmitLabel(): string {
    if (this.pendingApprovalDialogMode === 'submit') {
      if (this.pendingApprovalAllowActionSelection) {
        return `Submit ${this.approvalActionLabel(this.pendingApprovalAction)}`;
      }
      return this.pendingApprovalAction === 'approve' ? 'Approve & Submit' : 'Submit for Approval';
    }
    return `Submit ${this.approvalActionLabel(this.pendingApprovalAction)}`;
  }

  protected shouldShowApprovalAction(action: BillApprovalAction): boolean {
    if (!this.pendingApprovalAllowActionSelection) return false;
    if (!this.isApprovalAdmin()) return true;
    if (this.pendingApprovalStatusLabel === 'Matched') {
      return action === 'approve';
    }
    return true;
  }

  protected statusVariant(value: string): 'success' | 'warning' | 'danger' | 'secondary' {
    const normalized = (value || '').trim().toLowerCase();
    if (normalized === 'matched') return 'success';
    if (normalized.includes('mismatched')) return 'danger';
    if (normalized === 'verified') return 'success';
    if (normalized.includes('mismatch')) return 'danger';
    if (normalized.includes('outside')) return 'warning';
    if (normalized.includes('missing')) return 'warning';
    return 'secondary';
  }

  protected statusTooltip(row: BillVerification): string | null {
    const messages = [row.rate_error, row.amount_error].map((value) => (value || '').trim()).filter((value) => !!value);
    if ((row.approval_status || '').trim()) {
      messages.push(`Approval Status: ${row.approval_status}`);
    }
    if ((row.approval_reason || '').trim()) {
      messages.push(`Approval Reason: ${row.approval_reason}`);
    }
    return messages.length ? messages.join('\n') : null;
  }

  protected formatAmount(value: number | null | undefined): string {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }

  protected formatQuantum(value: number | null | undefined): string {
    const quantity = Number(value);
    if (!Number.isFinite(quantity)) return '-';
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(quantity);
  }

  protected formatRate(value: number | null | undefined): string {
    const rate = Number(value);
    if (!Number.isFinite(rate)) return '-';
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(rate);
  }

  protected punchedApplicationCount(row: SellerBillGenerateRow): number {
    return this.rowApplicationNumbers(row).length;
  }

  protected punchedApplicationTooltip(row: SellerBillGenerateRow): string | null {
    const values = this.rowApplicationNumbers(row);
    if (!values.length) return null;
    return values.join(', ');
  }

  protected openApplicationDetails(row: SellerBillGenerateRow): void {
    const details = this.applicationDetailItemsForRow(row);
    if (!details.length) {
      this.toast.show('No applications', 'No punched application details are available for this row.', 'info');
      return;
    }

    this.applicationDetailsTitle = `Application Details (${details.length})`;
    this.applicationDetailItems = details;
    this.applicationDetailsDialogOpen.set(true);
  }

  protected closeApplicationDetailsDialog(): void {
    this.applicationDetailsDialogOpen.set(false);
    this.applicationDetailsTitle = 'Application Details';
    this.applicationDetailItems = [];
  }

  protected loaRateDisplay(row: SellerBillGenerateRow): string {
    const directValue = this.toNumberOrNull(row.configuredRateValue);
    if (directValue !== null) return String(directValue);

    const directLabel = String(row.configuredRateLabel || '').trim();
    if (directLabel && directLabel !== '-') return directLabel;

    const loa = this.loas().find((item) => String(item.id) === String(row.loaId || '').trim());
    const fallbackValue = this.sellerLoaRateValue(loa);
    if (fallbackValue !== null) return String(fallbackValue);

    const fallbackLabel = this.sellerLoaRateLabel(loa);
    return fallbackLabel && fallbackLabel !== '-' ? fallbackLabel : '-';
  }

  protected sellerBillMismatchDetails(row: SellerBillGenerateRow): SellerBillMismatchDetail[] {
    const details: SellerBillMismatchDetail[] = [];

    const approvedQuantum = this.toNumberOrNull(row.approvedQuantum ?? row.totalQuantum);
    const billedQuantum = this.toNumberOrNull(row.billedQuantum);
    if (approvedQuantum !== null && billedQuantum !== null && Math.abs(approvedQuantum - billedQuantum) >= 0.0001) {
      details.push({
        label: 'Quantum',
        value: this.formatQuantum(approvedQuantum - billedQuantum),
      });
    }

    const loaRate = this.toNumberOrNull(row.configuredRateValue) ?? this.extractRateValue(row.configuredRateLabel);
    const submittedRate = this.toNumberOrNull(row.submittedRate);
    if (loaRate !== null && submittedRate !== null && Math.abs(loaRate - submittedRate) >= 0.0001) {
      details.push({
        label: 'Rate',
        value: this.formatRate(loaRate - submittedRate),
      });
    }

    const expectedAmount = this.toNumberOrNull(row.expectedAmount ?? row.totalAmount);
    const submittedAmount = this.toNumberOrNull(row.submittedAmount);
    if (expectedAmount !== null && submittedAmount !== null && Math.abs(expectedAmount - submittedAmount) >= 0.01) {
      details.push({
        label: 'Amount',
        value: this.formatAmount(expectedAmount - submittedAmount),
      });
    }

    return details;
  }

  protected billedQuantumTooltip(row: SellerBillGenerateRow): string | null {
    const lines = [
      `Billed Quantum: ${this.formatQuantum(row.billedQuantum)}`,
      `Application Number: ${this.punchedApplicationTooltip(row) || '-'}`,
      `Buyer: ${row.buyerName || 'No buyer linked'}`,
      `Contract: ${row.contractNumber || 'No contract linked'}`,
      `LOA: ${row.loaNumber || '-'}`,
      `Bill Date: ${this.formatDate(row.billDate)}`,
      `Bill Period From: ${this.formatDate(row.billPeriodFrom)}`,
      `Bill Period To: ${this.formatDate(row.billPeriodTo)}`,
    ];

    return lines.join('\n');
  }

  protected statSegmentWidth(value: number, total: number): number {
    if (total <= 0) return 100;
    return (value / total) * 100;
  }

  protected statSegmentClass(tone: HeroStatSegment['tone']): string {
    return `summary-tile-segment ${tone}`;
  }

  protected statSegmentTotal(stat: HeroStatItem): number {
    const total = stat.segments.reduce((sum, item) => sum + item.value, 0);
    return total > 0 ? total : 1;
  }

  private formatStatNumber(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);
  }

  private formatCurrencyStat(value: number): string {
    return this.formatAmount(value);
  }

  private rowApplicationNumbers(row: SellerBillGenerateRow): string[] {
    const rawValues = [row.applicationNumberTooltip, row.applicationNumber]
      .map((value) => String(value || '').trim())
      .filter((value) => !!value && value !== '-');

    const displayValues = rawValues
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter((value) => !!value && value !== '-' && !/^\d+\s+application(s)?$/i.test(value));

    const inferredValues = this.matchingApplicationsForRow(row)
      .map((app) => String(app.application_number || app.id || '').trim())
      .filter((value) => !!value && value !== '-');
    return Array.from(new Set([...displayValues, ...inferredValues]));
  }

  private inferredApplicationNumbersForRow(row: SellerBillGenerateRow): string[] {
    return this.matchingApplicationsForRow(row)
      .map((app) => String(app.application_number || app.id || '').trim())
      .filter((value) => !!value && value !== '-');
  }

  private matchingApplicationsForRow(row: SellerBillGenerateRow): ApplicationApi[] {
    const rowFrom = this.parseDateValue(row.billPeriodFrom || row.billDate || '');
    const rowTo = this.parseDateValue(row.billPeriodTo || row.billDate || '') || rowFrom;
    if (!rowFrom || !rowTo) return [];

    const baseMatches = this.applications()
      .filter((app) => (app.status || '').trim().toLowerCase() === 'punched')
      .filter((app) => this.applicationHasApprovedSchedule(app))
      .filter((app) => String(app.seller_vendor_id || '').trim() === String(row.sellerVendorId || '').trim())
      .filter((app) => String(app.seller_loa_id || '').trim() === String(row.loaId || '').trim())
      .filter((app) => {
        const bounds = this.applicationDateBounds(app);
        return !!bounds && this.rangesOverlap(rowFrom, rowTo, bounds.start, bounds.end);
      });

    const strictMatches = baseMatches
      .filter((app) => {
        const rowContractId = String(row.contractId || '').trim();
        return !rowContractId || String(app.contract_id || '').trim() === rowContractId;
      })
      .filter((app) => {
          const rowBuyerId = String(row.buyerVendorId || '').trim();
          return !rowBuyerId || String(app.buyer_vendor_id || '').trim() === rowBuyerId;
      });

    return strictMatches.length ? strictMatches : baseMatches;
  }

  private applicationDetailItemsForRow(row: SellerBillGenerateRow): ApplicationDetailItem[] {
    const rowFrom = this.parseDateValue(row.billPeriodFrom || row.billDate || '');
    const rowTo = this.parseDateValue(row.billPeriodTo || row.billDate || '') || rowFrom;

    return this.matchingApplicationsForRow(row)
      .map((app) => {
        const buyerName =
          this.vendors().find((item) => String(item.id || '').trim() === String(app.buyer_vendor_id || '').trim())?.vendor_name ||
          String(app.buyer_vendor_id || '').trim() ||
          '-';
        const contractNumber =
          this.contracts().find((item) => String(item.id || '').trim() === String(app.contract_id || '').trim())?.contract_number ||
          String(app.contract_id || '').trim() ||
          '-';
        const bounds = this.applicationDateBounds(app);
        const approvedQuantum = rowFrom && rowTo ? this.applicationQuantumForRange(app, rowFrom, rowTo) : null;

        return {
          applicationNumber: String(app.application_number || app.id || '-').trim() || '-',
          buyerName,
          contractNumber,
          approvedPeriodLabel: bounds
            ? `${this.formatDate(this.formatIsoDate(bounds.start))} to ${this.formatDate(this.formatIsoDate(bounds.end))}`
            : '-',
          approvedQuantum: approvedQuantum !== null && approvedQuantum > 0 ? approvedQuantum : null,
          status: this.applicationDetailStatus(app.status, approvedQuantum),
        } satisfies ApplicationDetailItem;
      })
      .sort((left, right) => left.applicationNumber.localeCompare(right.applicationNumber));
  }

  private applicationDetailStatus(status: string | null | undefined, approvedQuantum: number | null): string {
    if (approvedQuantum !== null && approvedQuantum > 0) {
      return 'Approved';
    }

    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return 'Pending';
    if (normalized === 'punched') return 'Punched';
    if (normalized === 'approved') return 'Approved';
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private safeNumber(value: number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private isVerifiedStatus(value: string | null | undefined): boolean {
    return (value || '').trim().toLowerCase() === 'verified';
  }

  private isPendingStatus(value: string | null | undefined): boolean {
    return (value || '').trim().toLowerCase().includes('pending');
  }

  private needsAttentionStatus(value: string | null | undefined): boolean {
    const normalized = (value || '').trim().toLowerCase();
    return normalized.includes('mismatch') || normalized.includes('outside') || normalized.includes('missing');
  }

  private sellerBillStatusTooltip(row: SellerBillGenerateRow): string | null {
    const lines = [
      `Bill Date: ${this.formatDate(row.billDate)}`,
      `Bill Period From: ${this.formatDate(row.billPeriodFrom)}`,
      `Bill Period To: ${this.formatDate(row.billPeriodTo)}`,
      `Approved Quantum: ${this.formatQuantum(row.approvedQuantum ?? row.totalQuantum)}`,
      `Total Quantum: ${this.formatQuantum(row.totalQuantum)}`,
      `Billed Quantum: ${this.formatQuantum(row.billedQuantum)}`,
      `Configured Rate: ${row.configuredRateLabel || '-'}`,
      `Submitted Rate: ${row.submittedRate ?? '-'}`,
      `Expected Amount: ${this.formatAmount(row.expectedAmount)}`,
      `Submitted Amount: ${this.formatAmount(row.submittedAmount)}`,
      `Approval Status: ${this.displayApprovalStatus(row)}`,
    ];

    if (row.approvalReason) {
      lines.push(`Approval Reason: ${row.approvalReason}`);
    }

    if (row.verificationNotes.length) {
      lines.push(...row.verificationNotes);
    }

    return lines.filter((value) => !!String(value).trim()).join('\n');
  }

  protected formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const raw = value.trim();
    if (!raw) return '-';

    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day}-${month}-${year}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  protected onSellerChange(): void {
    this.syncContractFilters();
    this.bumpSelectionVersion();
    if (this.showBillSection()) {
      this.fetchSellerBillRowsForMapping();
    }
  }

  protected onBuyerChange(): void {
    this.syncContractFilters();
    this.bumpSelectionVersion();
  }

  protected onContractChange(values: string | string[] | null | undefined): void {
    const sourceValues = Array.isArray(values) ? values : [values || ''];
    const normalized = Array.from(new Set(sourceValues.map((value) => String(value || '').trim()).filter(Boolean)));
    const availableContractIds = new Set(this.contractOptions().map((contract) => String(contract.id)));
    this.selectedContractIds = normalized.filter((id) => availableContractIds.has(id));
    this.contractId = this.selectedContractIds[0] || '';

    const selectedSellerLoaId = this.loaId.trim();
    if (selectedSellerLoaId && !this.availableSellerLoas().some((row) => String(row.id) === selectedSellerLoaId)) {
      this.loaId = '';
      this.toast.show('Only seller LOA can be selected', 'Select a seller LOA linked to the chosen contract.', 'info');
    }

    const selectedBuyerLoaId = this.billMappingLoaId.trim();
    if (selectedBuyerLoaId && !this.availableBuyerLoas().some((row) => String(row.id) === selectedBuyerLoaId)) {
      this.billMappingLoaId = '';
      this.toast.show('Only buyer LOA can be selected', 'Select a buyer LOA linked to the chosen contract.', 'info');
    }
    this.bumpSelectionVersion();
  }

  protected onLoaChange(value: string): void {
    const nextLoaId = (value || '').trim();
    if (!nextLoaId) {
      this.loaId = '';
      return;
    }

    const loa = this.loas().find((row) => String(row.id) === nextLoaId) || null;
    if (!loa || this.resolveLoaKind(loa) !== 'sell') {
      this.loaId = '';
      this.toast.show('Only seller LOA can be selected', 'Please choose a seller LOA only.', 'info');
      return;
    }

    if (!this.availableSellerLoas().some((row) => String(row.id) === nextLoaId)) {
      this.loaId = '';
      this.toast.show('Only seller LOA can be selected', 'Select a seller LOA linked to the chosen contract.', 'info');
      return;
    }

    this.loaId = nextLoaId;
    this.bumpSelectionVersion();
  }

  protected onBillMappingLoaChange(value: string): void {
    const nextLoaId = (value || '').trim();
    if (!nextLoaId) {
      this.billMappingLoaId = '';
      return;
    }

    const loa = this.loas().find((row) => String(row.id) === nextLoaId) || null;
    if (!loa || this.resolveLoaKind(loa) !== 'buy') {
      this.billMappingLoaId = '';
      this.toast.show('Only buyer LOA can be selected', 'Please choose a buyer LOA only.', 'info');
      return;
    }

    if (!this.availableBuyerLoas().some((row) => String(row.id) === nextLoaId)) {
      this.billMappingLoaId = '';
      this.toast.show('Only buyer LOA can be selected', 'Select a buyer LOA linked to the chosen contract.', 'info');
      return;
    }

    this.billMappingLoaId = nextLoaId;
    this.bumpSelectionVersion();
  }

  private bumpSelectionVersion(): void {
    this.selectionVersion.update((value) => value + 1);
  }

  protected fetchApprovalValues(): void {
    const targetBillDate = this.editingId() ? this.billDate : this.billEntries()[0]?.billDate;
    const targetBillPeriodFrom = this.editingId() ? this.billPeriodFrom : this.billEntries()[0]?.billPeriodFrom;
    const targetBillPeriodTo = this.editingId() ? this.billPeriodTo : this.billEntries()[0]?.billPeriodTo;

    if (!this.loaId) {
      this.toast.show('Missing details', 'Select seller LOA before fetching approval values.', 'danger');
      return;
    }
    if (!targetBillDate) {
      this.toast.show('Missing bill date', 'Bill date is required to fetch approval quantum and amount.', 'danger');
      return;
    }

    const params = new URLSearchParams({
      loa_id: this.loaId,
      bill_type: this.billType,
      bill_date: targetBillDate,
    });
    if (this.sellerVendorId) params.set('seller_vendor_id', this.sellerVendorId);
    if (targetBillPeriodFrom) params.set('bill_period_from', targetBillPeriodFrom);
    if (targetBillPeriodTo) params.set('bill_period_to', targetBillPeriodTo);

    this.fetchingApproval.set(true);
    this.api
      .get<BillApprovalPreview>(`/api/bill-verifications/approval-preview?${params.toString()}`)
      .pipe(catchError(() => of(null)))
      .subscribe((preview) => {
        this.fetchingApproval.set(false);
        if (!preview) {
          this.toast.show('Fetch failed', 'Approval quantum and amount could not be fetched.', 'danger');
          return;
        }
        const derivedAmount = this.calculateSubmittedAmount(preview.approved_quantum, preview.expected_rate);
        const previewAmount = derivedAmount === '' ? (preview.approved_amount ?? '') : derivedAmount;

        if (this.editingId()) {
          this.billedQuantum = preview.approved_quantum ?? '';
          this.submittedAmount = previewAmount;
        } else {
          this.updateBillEntry(0, {
            billedQuantum: preview.approved_quantum ?? '',
            submittedAmount: previewAmount,
          });
        }

        this.toast.show(
          'Approval values fetched',
          preview.note || 'Seller LOA approval quantum and amount were loaded.',
          preview.approved_quantum || preview.approved_amount ? 'success' : 'info',
        );
      });
  }

  protected fetchManualRowApprovalValues(): void {
    const form = this.manualSellerBillRowForm;
    const loaId = String(form.loaId || this.loaId || '').trim();
    const billDate = String(form.billDate || '').trim();
    const billPeriodFrom = String(form.billPeriodFrom || '').trim();
    const billPeriodTo = String(form.billPeriodTo || '').trim();

    if (!loaId) {
      this.toast.show('Missing details', 'Select seller LOA before fetching approval values.', 'danger');
      return;
    }
    if (!billDate) {
      this.toast.show('Missing bill date', 'Bill date is required to fetch approval quantum and amount.', 'danger');
      return;
    }

    const params = new URLSearchParams({
      loa_id: loaId,
      bill_type: String(form.billType || this.billType || 'SOP'),
      bill_date: billDate,
    });
    if (this.sellerVendorId) params.set('seller_vendor_id', this.sellerVendorId);
    if (billPeriodFrom) params.set('bill_period_from', billPeriodFrom);
    if (billPeriodTo) params.set('bill_period_to', billPeriodTo);

    this.fetchingApproval.set(true);
    this.api
      .get<BillApprovalPreview>(`/api/bill-verifications/approval-preview?${params.toString()}`)
      .pipe(catchError(() => of(null)))
      .subscribe((preview) => {
        this.fetchingApproval.set(false);
        if (!preview) {
          this.toast.show('Fetch failed', 'Approval quantum and amount could not be fetched.', 'danger');
          return;
        }

        const derivedAmount = this.calculateSubmittedAmount(preview.approved_quantum, preview.expected_rate);
        const previewAmount = derivedAmount === '' ? (preview.approved_amount ?? '') : derivedAmount;

        this.manualSellerBillRowForm = {
          ...this.manualSellerBillRowForm,
          approvedQuantum: preview.approved_quantum !== null && preview.approved_quantum !== undefined ? String(preview.approved_quantum) : '',
          loaRate:
            preview.expected_rate !== null && preview.expected_rate !== undefined
              ? String(preview.expected_rate)
              : this.manualSellerBillRowForm.loaRate,
          expectedAmount: previewAmount !== '' && previewAmount !== null && previewAmount !== undefined ? String(previewAmount) : '',
          billedQuantum:
            this.manualSellerBillRowForm.billedQuantum ||
            (preview.approved_quantum !== null && preview.approved_quantum !== undefined ? String(preview.approved_quantum) : ''),
          submittedAmount:
            this.manualSellerBillRowForm.submittedAmount ||
            (previewAmount !== '' && previewAmount !== null && previewAmount !== undefined ? String(previewAmount) : ''),
        };

        this.toast.show(
          'Approval values fetched',
          preview.note || 'Seller LOA approval quantum and amount were loaded.',
          preview.approved_quantum || preview.approved_amount ? 'success' : 'info',
        );
      });
  }

  protected onClientFilterChange(): void {
    const loaId = this.loaFilter().trim();
    if (loaId && !this.filterLoas().some((row) => row.id === loaId)) {
      this.loaFilter.set('');
    }
    this.verificationPage.set(1);
  }

  protected onGenerateClientFilterChange(value: string): void {
    this.generateClientFilterValue = value;
    const loaId = this.generateLoaFilterValue.trim();
    if (loaId && !this.generateFilterLoas().some((row) => row.id === loaId)) {
      this.generateLoaFilterValue = '';
    }
  }

  protected onLoaFilterChange(value: string): void {
    this.loaFilter.set(value);
    this.verificationPage.set(1);
  }

  protected onVerificationSearchChange(value: string): void {
    this.search.set(value);
    this.verificationPage.set(1);
  }

  protected onGenerateLoaFilterChange(value: string): void {
    this.generateLoaFilterValue = value;
  }

  protected onGenerateSearchChange(value: string): void {
    this.generateSearchValue = value;
  }

  protected onGenerateBillDateFromChange(value: string): void {
    this.generateBillDateFromValue = value;
    if (this.generateBillDateToValue.trim() && value.trim() && this.generateBillDateToValue.trim() < value.trim()) {
      this.generateBillDateToValue = value;
    }
  }

  protected onGenerateBillDateToChange(value: string): void {
    this.generateBillDateToValue = value;
    if (this.generateBillDateFromValue.trim() && value.trim() && this.generateBillDateFromValue.trim() > value.trim()) {
      this.generateBillDateFromValue = value;
    }
  }

  protected onGenerateBillCycleFilterChange(value: string): void {
    this.generateBillCycleFilterValue = value;
  }

  protected onBillPeriodFromChange(value: string): void {
    this.billPeriodFrom = value;
    if (this.billPeriodTo.trim() && value.trim() && this.billPeriodTo.trim() < value.trim()) {
      this.billPeriodTo = value;
    }
    this.sellerBillsPage.set(1);
    this.bumpSelectionVersion();
    if (this.showBillSection()) {
      this.fetchSellerBillRowsForMapping();
    }
  }

  protected onBillPeriodToChange(value: string): void {
    this.billPeriodTo = value;
    if (this.billPeriodFrom.trim() && value.trim() && this.billPeriodFrom.trim() > value.trim()) {
      this.billPeriodFrom = value;
    }
    this.sellerBillsPage.set(1);
    this.bumpSelectionVersion();
    if (this.showBillSection()) {
      this.fetchSellerBillRowsForMapping();
    }
  }

  protected applySellerBillFilters(): void {
    this.generateClientFilter.set(this.generateClientFilterValue.trim());
    this.generateLoaFilter.set(this.generateLoaFilterValue.trim());
    this.generateSearch.set(this.generateSearchValue.trim());
    this.generateBillCycleFilter.set(this.generateBillCycleFilterValue.trim());
    this.generateBillDateFrom.set(this.generateBillDateFromValue.trim());
    this.generateBillDateTo.set(this.generateBillDateToValue.trim());
    this.sellerBillsPage.set(1);
  }

  protected toggleSellerBillFilters(): void {
    this.showSellerBillFilters.update((value) => !value);
  }

  protected clearSellerBillFilters(): void {
    this.generateClientFilterValue = '';
    this.generateLoaFilterValue = '';
    this.generateSearchValue = '';
    this.generateBillCycleFilterValue = '';
    this.generateBillDateFromValue = '';
    this.generateBillDateToValue = '';
    this.generateClientFilter.set('');
    this.generateLoaFilter.set('');
    this.generateSearch.set('');
    this.generateBillCycleFilter.set('');
    this.generateBillDateFrom.set('');
    this.generateBillDateTo.set('');
    this.selectAllExtractedCollections();
    this.sellerBillsPage.set(1);
  }

  protected onSellerBillsPageSizeChange(value: string | number): void {
    const parsed = Number(value);
    const nextSize = this.sellerBillsPageSizeOptions.find((size) => size === parsed) ?? this.sellerBillsPageSizeOptions[0] ?? 10;
    this.sellerBillsPageSize.set(nextSize);
    this.sellerBillsPage.set(1);
  }

  protected sellerBillSerialNumber(index: number): number {
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    return (page - 1) * this.sellerBillsPageSize() + index + 1;
  }

  protected sellerBillsStartRow(): number {
    if (!this.filteredSellerBillRows().length) return 0;
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    return (page - 1) * this.sellerBillsPageSize() + 1;
  }

  protected sellerBillsEndRow(): number {
    const page = Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
    return Math.min(page * this.sellerBillsPageSize(), this.filteredSellerBillRows().length);
  }

  protected sellerBillsCurrentPage(): number {
    return Math.min(this.sellerBillsPage(), this.sellerBillTotalPages());
  }

  protected goToSellerBillsPage(page: number): void {
    const totalPages = this.sellerBillTotalPages();
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    this.sellerBillsPage.set(nextPage);
  }

  protected onVerificationPageSizeChange(value: string | number): void {
    const parsed = Number(value);
    const nextSize = this.verificationPageSizeOptions.find((size) => size === parsed) ?? this.verificationPageSizeOptions[0] ?? 10;
    this.verificationPageSize.set(nextSize);
    this.verificationPage.set(1);
  }

  protected verificationSerialNumber(index: number): number {
    return this.verificationPageStartIndex() + index + 1;
  }

  protected goToVerificationPage(page: number): void {
    const nextPage = Math.min(Math.max(page, 1), this.verificationTotalPages());
    this.verificationPage.set(nextPage);
  }

  private runApprovalAction(verificationId: string, action: BillApprovalAction, reason?: string): void {
    const endpoint =
      action === 'approve'
        ? 'approve'
        : action === 'reject'
          ? 'reject'
          : 'force-approve';
    const titles: Record<BillApprovalAction, string> = {
      approve: 'Approved',
      reject: 'Rejected',
      'force-approve': 'Force approved',
    };

    this.approvalActionLoadingId.set(verificationId);
    this.api
      .post<BillVerification>(`/api/bill-verifications/${verificationId}/${endpoint}`, { reason: reason || null })
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        this.approvalActionLoadingId.set(null);
        if (!response) {
          this.toast.show('Action failed', 'Bill approval action could not be completed.', 'danger');
          return;
        }

        this.verifications.update((items) => items.map((item) => (item.id === response.id ? response : item)));
        if (this.editingId() === response.id) {
          this.remarks = response.remarks || this.remarks;
        }
        this.toast.show(titles[action], `${response.invoice_number} marked as ${response.approval_status || titles[action]}.`, 'success');
        this.closeApprovalReasonDialog();
      });
  }

  private runApprovalActionForRows(rows: BillVerification[], action: BillApprovalAction, reason?: string): void {
    const verificationIds = rows.map((row) => String(row.id || '').trim()).filter(Boolean);
    if (!verificationIds.length) {
      this.closeApprovalReasonDialog();
      this.resetFormAfterSubmit();
      return;
    }

    const endpoint =
      action === 'approve'
        ? 'approve'
        : action === 'reject'
          ? 'reject'
          : 'force-approve';
    const titles: Record<BillApprovalAction, string> = {
      approve: 'Approved',
      reject: 'Rejected',
      'force-approve': 'Force approved',
    };

    this.saving.set(true);
    forkJoin(
      verificationIds.map((verificationId) =>
        this.api
          .post<BillVerification>(`/api/bill-verifications/${verificationId}/${endpoint}`, { reason: reason || null })
          .pipe(catchError(() => of(null))),
      ),
    ).subscribe((responses) => {
      this.saving.set(false);
      const successful = responses.filter((response): response is BillVerification => !!response);
      if (!successful.length) {
        this.toast.show('Action failed', 'Bill approval action could not be completed.', 'danger');
        return;
      }

      const updates = new Map(successful.map((item) => [item.id, item]));
      this.verifications.update((items) => items.map((item) => updates.get(item.id) || item));
      const first = successful[0];
      const partialFailure = successful.length !== verificationIds.length;
      this.toast.show(
        partialFailure ? 'Partial update' : titles[action],
        partialFailure
          ? `${successful.length} of ${verificationIds.length} bill approval action(s) completed for ${first.invoice_number}.`
          : `${first.invoice_number} marked as ${first.approval_status || titles[action]}.`,
        partialFailure ? 'info' : 'success',
      );
      this.closeApprovalReasonDialog();
      this.resetFormAfterSubmit();
    });
  }

  private defaultApprovalAction(approvalStatus: string | null | undefined): BillApprovalAction {
    const normalized = (approvalStatus || '').trim().toLowerCase();
    if (normalized === 'approved') return 'approve';
    if (normalized === 'rejected') return 'reject';
    if (normalized === 'force approved') return 'force-approve';
    return 'approve';
  }

  private prepareSubmitApprovalDialog(): boolean {
    if (!this.loaId || !this.invoiceNumber.trim() || !this.invoiceDate.trim()) {
      this.toast.show('Missing details', 'Seller LOA, invoice number, and invoice date are required.', 'danger');
      return false;
    }
    if (!this.editingId() && !this.billEntries()[0]?.billDate) {
      this.toast.show('Missing bill date', 'Bill date is required.', 'danger');
      return false;
    }
    if (this.editingId() && !this.billDate) {
      this.toast.show('Missing details', 'Bill date is required.', 'danger');
      return false;
    }

    const mismatchLabels = this.currentSubmitMismatchLabels();
    const hasAnyMismatch = this.currentSubmitHasAnyBillLineMismatch();
    const allChecksMatched = !hasAnyMismatch && this.currentSubmitAllChecksMatched();
    const needsAdminDecision = this.isApprovalAdmin();

    this.pendingApprovalDialogMode = 'submit';
    this.pendingApprovalVerificationId = null;
    this.pendingApprovalInvoiceNumber = this.invoiceNumber.trim() || 'Selected bill';
    this.pendingApprovalSummary = this.buildCurrentApprovalSummary();
    this.pendingApprovalAllowActionSelection = needsAdminDecision;
    this.pendingApprovalAction = this.isApprovalAdmin() ? 'approve' : null;
    this.pendingApprovalStatusLabel = allChecksMatched
      ? 'Matched'
      : mismatchLabels.length
        ? `Mismatched ${mismatchLabels.join('/')}`
        : 'Needs Review';
    this.pendingApprovalStatusTone = allChecksMatched ? 'success' : mismatchLabels.length ? 'danger' : 'warning';
    this.approvalReason = '';
    return true;
  }

  private currentSubmitHasAnyBillLineMismatch(): boolean {
    const entries = this.activeBillLineEntries();
    if (!entries.length) return false;

    return entries.some((entry) =>
      this.lineRatesMatch(entry) === false ||
      this.quantitiesMatch(entry.billedQuantum, this.currentExpectedQuantum()) === false ||
      this.amountsMatch(entry.submittedAmount, this.currentExpectedAmount()) === false,
    );
  }

  private currentSubmitAllChecksMatched(): boolean {
    return (
      this.rateMatchesCurrentReference() === true &&
      this.quantumMatchesCurrentReference() === true &&
      this.amountMatchesCurrentReference() === true
    );
  }

  private currentSubmitMismatchLabels(): string[] {
    const labels: string[] = [];
    if (this.rateMatchesCurrentReference() !== true) labels.push('Rate');
    if (this.quantumMatchesCurrentReference() !== true) labels.push('Quantum');
    if (this.amountMatchesCurrentReference() !== true) labels.push('Amount');
    return labels;
  }

  private buildCurrentApprovalSummary(): ApprovalDialogSummary {
    const referenceRow = this.currentReferenceRow();
    const activeEntry = this.activeBillLineEntries()[0];
    return {
      invoiceNumber: this.invoiceNumber.trim() || '-',
      loaNumber: this.selectedLoaNumber() || referenceRow?.loaNumber || '-',
      billPeriodFrom: this.editingId() ? this.billPeriodFrom : activeEntry?.billPeriodFrom || '',
      billPeriodTo: this.editingId() ? this.billPeriodTo : activeEntry?.billPeriodTo || '',
      quantum: this.editingId()
        ? this.toNumberOrNull(this.billedQuantum)
        : this.toNumberOrNull(activeEntry?.billedQuantum),
      rate: this.editingId()
        ? this.toNumberOrNull(this.submittedRate)
        : this.toNumberOrNull(activeEntry?.submittedRate),
      amount: this.editingId()
        ? this.toNumberOrNull(this.submittedAmount)
        : this.toNumberOrNull(activeEntry?.submittedAmount),
    };
  }

  protected currentBillDetailsSummary(): string {
    const rows = this.showForm() && this.showBillSection() && !this.showVerificationSection()
      ? this.tableSellerBillRows()
      : this.reviewSellerBillRows();

    if (rows.length) {
      return rows
        .map((row, index) => [
          `Bill Date: ${this.formatDate(row.billDate)}`,
          `Bill Period From: ${this.formatDate(row.billPeriodFrom)}`,
          `Bill Period To: ${this.formatDate(row.billPeriodTo)}`,
          `LOA Approved Quantum: ${this.formatQuantum(row.totalQuantum ?? row.approvedQuantum ?? null)}`,
          `Billed Quantum: ${this.formatQuantum(row.billedQuantum)}`,
          `LOA Rate: ${row.configuredRateLabel || '-'}`,
          `Submitted Rate: ${this.formatRate(row.submittedRate)}`,
          `Expected Amount: ${this.formatAmount(row.expectedAmount ?? row.totalAmount ?? null)}`,
          `Submitted Amount: ${this.formatAmount(row.submittedAmount)}`,
          `Approval Status: ${this.displayVerificationStatus(row)}`,
        ].join('\n'))
        .join('\n\n');
    }

    const referenceRow = this.currentReferenceRow();
    const activeEntry = this.activeBillLineEntries()[0];
    return [
      `Bill Date: ${this.formatDate(this.editingId() ? this.billDate : activeEntry?.billDate || '')}`,
      `Bill Period From: ${this.formatDate(this.editingId() ? this.billPeriodFrom : activeEntry?.billPeriodFrom || '')}`,
      `Bill Period To: ${this.formatDate(this.editingId() ? this.billPeriodTo : activeEntry?.billPeriodTo || '')}`,
      `Total Quantum: ${this.formatQuantum(referenceRow?.totalQuantum ?? referenceRow?.approvedQuantum ?? null)}`,
      `Billed Quantum: ${this.formatQuantum(this.toNumberOrNull(this.editingId() ? this.billedQuantum : activeEntry?.billedQuantum))}`,
      `Configured Rate: ${this.currentExpectedRateLabel()}`,
      `Submitted Rate: ${this.formatRate(this.toNumberOrNull(this.editingId() ? this.submittedRate : activeEntry?.submittedRate))}`,
      `Expected Amount: ${this.formatAmount(referenceRow?.expectedAmount ?? referenceRow?.totalAmount ?? null)}`,
      `Submitted Amount: ${this.formatAmount(this.toNumberOrNull(this.editingId() ? this.submittedAmount : activeEntry?.submittedAmount))}`,
      `Approval Status: ${referenceRow ? this.displayVerificationStatus(referenceRow) : 'Pending'}`,
    ].join('\n');
  }

  private rowVerificationStatus(row: SellerBillGenerateRow): string {
    const mismatchLabels: string[] = [];

    const approvedQuantum = this.toNumberOrNull(row.approvedQuantum ?? row.totalQuantum);
    const billedQuantum = this.toNumberOrNull(row.billedQuantum);
    if (approvedQuantum !== null && billedQuantum !== null && Math.abs(approvedQuantum - billedQuantum) >= 0.0001) {
      mismatchLabels.push('Quantum');
    }

    const loaRate =
      this.toNumberOrNull(row.configuredRateValue) ??
      this.extractRateValue(row.configuredRateLabel) ??
      null;
    const submittedRate = this.toNumberOrNull(row.submittedRate);
    if (loaRate !== null && submittedRate !== null && Math.abs(loaRate - submittedRate) >= 0.0001) {
      mismatchLabels.push('Rate');
    }

    const expectedAmount = this.toNumberOrNull(row.expectedAmount ?? row.totalAmount);
    const submittedAmount = this.toNumberOrNull(row.submittedAmount);
    if (expectedAmount !== null && submittedAmount !== null && Math.abs(expectedAmount - submittedAmount) >= 0.01) {
      mismatchLabels.push('Amount');
    }

    if (!mismatchLabels.length && approvedQuantum !== null && billedQuantum !== null && loaRate !== null && submittedRate !== null && expectedAmount !== null && submittedAmount !== null) {
      return 'Matched';
    }

    if (mismatchLabels.length) {
      return `Mismatched ${mismatchLabels.join('/')}`;
    }

    return (row.verificationStatus || '').trim() || 'Pending Verification';
  }

  private rateMatchesCurrentReference(): boolean | null {
    const row = this.currentReferenceRow();
    const expectedRate =
      this.toNumberOrNull(row?.configuredRateValue) ??
      this.extractRateValue(row?.configuredRateLabel) ??
      this.extractedContextLoaRate() ??
      this.toNumberOrNull(row?.submittedRate) ??
      null;
    if (expectedRate === null) return null;

    const submittedRates = this.activeBillLineEntries()
      .map((entry) => this.toNumberOrNull(entry.submittedRate))
      .filter((value): value is number => value !== null);
    if (!submittedRates.length) return null;

    return submittedRates.some((rate) => Math.abs(expectedRate - rate) < 0.0001);
  }

  private quantumMatchesCurrentReference(): boolean | null {
    const expectedQuantum = this.currentReferenceRow()?.totalQuantum ?? this.extractedContextLoaQuantum();
    if (expectedQuantum === null) return null;

    const billedQuantums = this.activeBillLineEntries()
      .map((entry) => this.toNumberOrNull(entry.billedQuantum))
      .filter((value): value is number => value !== null);
    if (!billedQuantums.length) return null;

    return billedQuantums.some((value) => Math.abs(value - expectedQuantum) < 0.0001);
  }

  private amountMatchesCurrentReference(): boolean | null {
    const row = this.currentReferenceRow();
    const rowExpected = row?.expectedAmount ?? row?.totalAmount ?? null;
    const loaRate = this.extractedContextLoaRate();
    const loaQuantum = this.extractedContextLoaQuantum();
    const contextExpected = loaRate !== null && loaQuantum !== null ? Number((loaRate * loaQuantum).toFixed(2)) : null;
    const expectedAmount = rowExpected ?? contextExpected;
    if (expectedAmount === null) return null;

    const submittedAmounts = this.activeBillLineEntries()
      .map((entry) => this.toNumberOrNull(entry.submittedAmount))
      .filter((value): value is number => value !== null);
    if (!submittedAmounts.length) return null;

    return submittedAmounts.some((value) => Math.abs(value - expectedAmount) < 0.01);
  }

  private lineRatesMatch(entry: BillEntryForm): boolean | null {
    const expectedRate = this.currentExpectedRateValue();
    const submittedRate = this.toNumberOrNull(entry.submittedRate);
    if (expectedRate === null || submittedRate === null) return null;
    return Math.abs(expectedRate - submittedRate) < 0.0001;
  }

  private currentExpectedReferenceRow(): SellerBillGenerateRow | null {
    const row = this.currentReferenceRow();
    const rowRate =
      this.toNumberOrNull(row?.configuredRateValue) ??
      this.extractRateValue(row?.configuredRateLabel);
    return rowRate !== null ? row : this.invoiceRedirectReferenceRow() ?? row;
  }

  private currentExpectedRateValue(): number | null {
    const row = this.currentExpectedReferenceRow();
    return (
      this.toNumberOrNull(row?.configuredRateValue) ??
      this.extractRateValue(row?.configuredRateLabel) ??
      this.extractedContextLoaRate() ??
      null
    );
  }

  private withResolvedConfiguredRate(row: SellerBillGenerateRow): SellerBillGenerateRow {
    const configuredRateValue =
      this.toNumberOrNull(row.configuredRateValue) ??
      this.extractRateValue(row.configuredRateLabel);
    const configuredRateLabel = String(row.configuredRateLabel || '').trim();
    const loa = this.loas().find((item) => String(item.id) === String(row.loaId || '').trim());
    const fallbackRateValue = this.sellerLoaRateValue(loa);
    const fallbackRateLabel = this.sellerLoaRateLabel(loa);
    if (
      configuredRateValue === null &&
      (!configuredRateLabel || configuredRateLabel === '-') &&
      fallbackRateValue === null &&
      (!fallbackRateLabel || fallbackRateLabel === '-')
    ) {
      return row;
    }

    const resolvedConfiguredRateValue = configuredRateValue ?? fallbackRateValue;
    const resolvedConfiguredRateLabel =
      configuredRateLabel && configuredRateLabel !== '-' ? configuredRateLabel : fallbackRateLabel;
    const resolvedQuantum =
      this.toNumberOrNull(row.approvedQuantum) ??
      this.toNumberOrNull(row.totalQuantum) ??
      this.toNumberOrNull(row.billedQuantum);
    const calculatedExpectedAmount = this.calculateExpectedAmount(resolvedQuantum, resolvedConfiguredRateValue);
    const resolvedExpectedAmount =
      calculatedExpectedAmount ??
      this.toNumberOrNull(row.expectedAmount) ??
      this.toNumberOrNull(row.totalAmount);

    return {
      ...row,
      configuredRateValue: resolvedConfiguredRateValue,
      configuredRateLabel: resolvedConfiguredRateLabel,
      totalAmount: calculatedExpectedAmount ?? this.toNumberOrNull(row.totalAmount) ?? resolvedExpectedAmount,
      expectedAmount: resolvedExpectedAmount,
    };
  }

  private quantitiesMatch(value: string | number | null | undefined, expected: number | null | undefined): boolean | null {
    const current = this.toNumberOrNull(value);
    const target = this.toNumberOrNull(expected);
    if (current === null || target === null) return null;
    return Math.abs(current - target) < 0.0001;
  }

  private amountsMatch(value: string | number | null | undefined, expected: number | null | undefined): boolean | null {
    const current = this.toNumberOrNull(value);
    const target = this.toNumberOrNull(expected);
    if (current === null || target === null) return null;
    return Math.abs(current - target) < 0.01;
  }

  private extractRateValue(label: string | null | undefined): number | null {
    const raw = String(label || '').trim();
    if (!raw) return null;
    const match = raw.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  protected updateBillEntry(index: number, patch: Partial<BillEntryForm>): void {
    this.billEntries.update((items) =>
      items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const next = { ...item, ...patch };
        return {
          ...next,
          submittedAmount: this.calculateSubmittedAmount(next.billedQuantum, next.submittedRate),
        };
      }),
    );
    this.bumpSelectionVersion();
    if (patch.billPeriodFrom !== undefined || patch.billPeriodTo !== undefined) {
      this.sellerBillsPage.set(1);
      if (this.showBillSection()) {
        this.fetchSellerBillRowsForMapping();
      }
    }
  }

  protected updateEditBilledQuantum(value: string | number): void {
    this.billedQuantum = value;
    this.submittedAmount = this.calculateSubmittedAmount(this.billedQuantum, this.submittedRate);
    this.bumpSelectionVersion();
  }

  protected updateEditSubmittedRate(value: string | number): void {
    this.submittedRate = value;
    this.submittedAmount = this.calculateSubmittedAmount(this.billedQuantum, this.submittedRate);
    this.bumpSelectionVersion();
  }

  protected isBilledQuantumLocked(value: string | number | null | undefined): boolean {
    return this.toNumberOrNull(value) === 0;
  }

  protected addBillEntry(): void {
    this.showForm.set(true);
    this.scrollToInvoiceSection();

    if (this.editingId()) {
      const currentEditLine: BillEntryForm = {
        billDate: this.billDate || '',
        billPeriodFrom: this.billPeriodFrom || '',
        billPeriodTo: this.billPeriodTo || '',
        billedQuantum: this.billedQuantum,
        submittedRate: this.submittedRate,
        submittedAmount: this.submittedAmount,
      };
      this.editingId.set(null);
      this.billEntries.set([currentEditLine, this.createEmptyBillEntry()]);
      this.toast.show('Line added', 'Switched to multi-line mode and kept your current bill as line 1.', 'info');
      return;
    }

    this.billEntries.update((items) => [...items, this.createEmptyBillEntry()]);
  }

  protected removeBillEntry(index: number): void {
    this.billEntries.update((items) => {
      if (items.length <= 1) return items;
      return items.filter((_, itemIndex) => itemIndex !== index);
    });
  }

  private loadData(): void {
    this.loading.set(true);
    forkJoin({
      vendors: this.vendorsService.list().pipe(
        catchError(() =>
          this.isVendor() && this.sellerVendorId
            ? this.vendorsService.get(this.sellerVendorId).pipe(
                map((vendor) => (vendor ? [vendor] : [])),
                catchError(() => of([] as Vendor[])),
              )
            : of([] as Vendor[]),
        ),
      ),
      contracts: this.contractsService.list().pipe(catchError(() => of([] as Contract[]))),
      loas: this.loaService.list().pipe(catchError(() => of([]))),
      verifications: this.api.get<BillVerification[]>('/api/bill-verifications').pipe(catchError(() => of([]))),
      applications: this.api.get<ApplicationApi[]>('/api/applications').pipe(catchError(() => of([]))),
    })
      .pipe(
        switchMap(({ vendors, contracts, loas, verifications, applications }) => {
          const loaIds = Array.from(
            new Set(
              (applications || [])
                .map((item) => String(item.seller_loa_id || '').trim())
                .filter((item) => !!item),
            ),
          );

          if (!loaIds.length) {
            return of({ vendors, contracts, loas, verifications, applications, configsByLoa: {} as Record<string, LoaBillConfig[]> });
          }

          return forkJoin(
            loaIds.map((loaId) =>
              this.loaService.listBillConfigs(loaId).pipe(
                catchError(() => of([] as LoaBillConfig[])),
                map((configs) => ({ loaId, configs })),
              ),
            ),
          ).pipe(
            map((configRows) => ({
              vendors,
              loas,
              contracts,
              verifications,
              applications,
              configsByLoa: configRows.reduce<Record<string, LoaBillConfig[]>>((acc, item) => {
                acc[item.loaId] = item.configs || [];
                return acc;
              }, {}),
            })),
          );
        }),
      )
      .subscribe(({ vendors, contracts, loas, verifications, applications, configsByLoa }) => {
        this.vendors.set((vendors || []).filter((row) => !!row?.id));
        this.contracts.set(contracts || []);
        this.loas.set(loas || []);
        this.verifications.set(verifications || []);
        this.applications.set(applications || []);
        this.billConfigsByLoa.set(configsByLoa || {});
        this.loading.set(false);
      });
  }

  private extractAndApplyInvoiceDetails(file: File): void {
    const formData = new FormData();
    formData.append('invoice_file', file);
    this.extractingInvoice.set(true);
    this.uploadSectionError.set('');
    this.showForm.set(true);
    this.http
      .post<ExtractedInvoicePayload>(this.api.resolve('/api/bill-verifications/extract-invoice'), formData, { withCredentials: true })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.extractingInvoice.set(false);
          const message = typeof error.error?.detail === 'string' && error.error.detail.trim()
            ? error.error.detail.trim()
            : 'Could not read invoice details from the uploaded file.';
          this.uploadSectionError.set(message);
          this.toast.show('Extraction failed', message, 'danger');
          return of(null);
        }),
      )
      .subscribe((payload) => {
        this.extractingInvoice.set(false);
        if (!payload) return;
        this.uploadSectionError.set('');
        this.extractedInvoicePreview.set(payload);
        this.applyExtractedInvoicePayload(payload);
      });
  }

  private applyExtractedInvoicePayload(payload: ExtractedInvoicePayload): void {
    this.showForm.set(true);
    this.extractedInvoicePreview.set(payload);
    const matchedContext = payload.matched_context || {};
    const extractedContractCollections = this.normalizeExtractedContractLoaCollections(matchedContext.contract_loa_collections);
    this.extractedContractLoaCollections.set(extractedContractCollections);
    this.extractedContextLoaRate.set(this.toNumberOrNull(matchedContext.loa_rate));
    this.extractedContextLoaQuantum.set(this.toNumberOrNull(matchedContext.loa_quantum));
    const sellerName = String(payload.seller_name || '').trim();
    const buyerName = String(payload.buyer_name || '').trim();
    const extractedMappingRowsFromPayload = this.toSellerBillGenerateRows(payload.bill_mapping_row || null);
    const extractedMappingRowsFromCollections = this.buildRowsFromExtractedContractLoaCollections(extractedContractCollections);
    const extractedMappingRows = this.mergeExtractedMappingRows(
      extractedMappingRowsFromPayload,
      extractedMappingRowsFromCollections,
    );
    const extractedMappingRow = extractedMappingRows[0] || null;
    const extractedContracts = this.resolveContractsFromExtractedContext(
      String(matchedContext.contract_id || '').trim(),
      String(matchedContext.contract_number || '').trim(),
      extractedContractCollections,
      extractedMappingRows,
    );
    const invoiceNumber = this.resolveExtractedInvoiceNumber(payload, extractedMappingRow);
    const invoiceDate = this.resolveExtractedInvoiceDate(payload, extractedMappingRow);
    const inferredBillDate = this.resolveExtractedBillDate(payload, extractedMappingRow);
    const inferredBillPeriodFrom =
      this.toIsoDateInput(extractedMappingRow?.billPeriodFrom || null) ||
      this.toIsoDateInput(payload.period_from || null) ||
      inferredBillDate;
    const inferredBillPeriodTo =
      this.toIsoDateInput(extractedMappingRow?.billPeriodTo || null) ||
      this.toIsoDateInput(payload.period_to || null) ||
      inferredBillDate;
    const totalAmount = this.toNumberOrNull(payload.totals?.total_amount ?? null);
    const itemQuantity = this.firstItemQuantity(payload.item_details || []);
    const inferredRate = totalAmount !== null && itemQuantity !== null && itemQuantity > 0 ? Number((totalAmount / itemQuantity).toFixed(4)) : null;
    const extractedBillEntries = this.buildExtractedBillEntries(
      payload.item_details || [],
      inferredBillDate,
      inferredBillPeriodFrom,
      inferredBillPeriodTo,
      inferredRate,
      totalAmount,
    );
    const extractedBillEntry = extractedBillEntries[0] || this.createEmptyBillEntry();
    const extractedRowsWithInvoiceValues = this.applyExtractedEntriesToMappingRows(extractedMappingRows, extractedBillEntries);
    this.extractedBillMappingRows.set(extractedRowsWithInvoiceValues);
    this.extractedBillMappingActive.set(extractedRowsWithInvoiceValues.length > 0);
    this.applyUploadedPeriodMapping(extractedRowsWithInvoiceValues);

    const sellerId =
      String(extractedMappingRow?.sellerVendorId || '').trim() ||
      String(matchedContext.seller_vendor_id || '').trim() ||
      this.findSellerVendorIdFromExtracted({
        seller_name: sellerName,
      });
    if (!this.isVendor() && sellerId && sellerId !== this.sellerVendorId) {
      this.sellerVendorId = sellerId;
      this.onSellerChange();
    }

    const buyerId =
      String(extractedMappingRow?.buyerVendorId || '').trim() ||
      String(matchedContext.buyer_vendor_id || '').trim() ||
      this.findBuyerVendorIdFromExtracted({
        buyer_name: buyerName,
      });
    const resolvedBuyerId = this.resolveBuyerIdFromExtractedContracts(buyerId, extractedContracts);
    if (resolvedBuyerId && resolvedBuyerId !== this.buyerVendorId) {
      this.buyerVendorId = resolvedBuyerId;
      this.onBuyerChange();
    }

    const extractedContractIds = extractedContracts
      .map((contract) => String(contract.id || '').trim())
      .filter(Boolean);
    this.extractedMatchedContractIds.set(extractedContractIds);
    this.selectAllExtractedCollections();
    const fallbackContractIds = this.availableContracts().map((contract) => String(contract.id || '').trim()).filter(Boolean);
    const defaultContractIds = extractedContractIds.length ? extractedContractIds : fallbackContractIds;
    this.onContractChange(defaultContractIds);

    const matchedLoaId = String(extractedMappingRow?.loaId || matchedContext.loa_id || '').trim();
    if (matchedLoaId && this.availableSellerLoas().some((loa) => String(loa.id) === matchedLoaId)) {
      this.onLoaChange(matchedLoaId);
    }

    this.autoSelectBillContextFromExtracted();
    if (extractedMappingRow?.billType) {
      this.billType = extractedMappingRow.billType;
      this.billTypeLockedByUpload = true;
    } else {
      this.applyExtractedBillType(payload);
    }

    if (invoiceNumber) this.invoiceNumber = invoiceNumber;
    if (invoiceDate) this.invoiceDate = invoiceDate;

    if (this.editingId()) {
      this.billDate = extractedBillEntry.billDate;
      this.billPeriodFrom = extractedBillEntry.billPeriodFrom;
      this.billPeriodTo = extractedBillEntry.billPeriodTo;
      this.billedQuantum = extractedBillEntry.billedQuantum;
      this.submittedRate = extractedBillEntry.submittedRate;
      this.submittedAmount = extractedBillEntry.submittedAmount;
    } else {
      this.billEntries.set(extractedBillEntries.length ? extractedBillEntries : [this.createEmptyBillEntry()]);
    }

    const declaration = (payload.declaration || '').trim();
    if (declaration && !this.remarks.trim()) {
      this.remarks = declaration;
    }

    this.toast.show('Invoice parsed', 'Invoice fields were auto-filled from uploaded file.', 'success');
  }

  private resetFormAfterSubmit(): void {
    this.editingId.set(null);
    this.selectedSellerBillRowKey.set(null);
    this.invoiceRedirectReferenceRow.set(null);
    this.showForm.set(false);
    this.showBillSection.set(false);
    this.showVerificationSection.set(false);
    this.showPreviewPanel.set(true);
    this.suppressStoredInvoiceFile.set(false);
    this.manualSellerBillRows.set([]);
    this.hiddenSellerBillRowKeys.set([]);
    this.addSellerBillRowDialogOpen.set(false);
    this.manualSellerBillRowForm = this.createManualSellerBillRowForm();
    this.clearExtractedInvoiceData();
    this.clearSelectedFile();
  }

  private clearExtractedInvoiceData(): void {
    this.uploadSectionError.set('');
    this.extractedInvoicePreview.set(null);
    this.extractedBillMappingActive.set(false);
    this.extractedBillMappingRows.set([]);
    this.sellerBillRowsFromBackend.set([]);
    this.selectedExtractedCollectionKeys.set([]);
    this.uploadedBillCycle.set('');
    this.uploadedPeriodFrom.set('');
    this.uploadedPeriodTo.set('');
    this.extractedContractLoaCollections.set([]);
    this.extractedMatchedContractIds.set([]);
    this.extractedContextLoaRate.set(null);
    this.extractedContextLoaQuantum.set(null);
    this.invoiceRedirectReferenceRow.set(null);
    this.billTypeLockedByUpload = false;
    this.billType = 'SOP';
    if (!this.isVendor()) {
      this.sellerVendorId = '';
    }
    this.buyerVendorId = '';
    this.contractId = '';
    this.selectedContractIds = [];
    this.loaId = '';
    this.billMappingLoaId = '';
    this.invoiceNumber = '';
    this.invoiceDate = '';
    this.billDate = '';
    this.billPeriodFrom = '';
    this.billPeriodTo = '';
    this.billedQuantum = '';
    this.submittedRate = '';
    this.submittedAmount = '';
    this.billEntries.set([this.createEmptyBillEntry()]);
    this.remarks = '';
  }

  private fetchSellerBillRowsForMapping(): void {
    const sellerId = String(this.sellerVendorId || '').trim();
    const loaId = String(this.loaId || '').trim();
    const activeBillLine = this.activeBillLineEntries()[0];
    const billPeriodFrom =
      this.toIsoDateInput(this.extractedInvoicePreview()?.period_from) ||
      String(activeBillLine?.billPeriodFrom || this.billPeriodFrom || '').trim();
    const billPeriodTo =
      this.toIsoDateInput(this.extractedInvoicePreview()?.period_to) ||
      String(activeBillLine?.billPeriodTo || this.billPeriodTo || '').trim();
    if (!sellerId) {
      this.sellerBillRowsFromBackend.set([]);
      this.fetchingSellerBillRows.set(false);
      this.sellerBillsPage.set(1);
      return;
    }

    this.fetchingSellerBillRows.set(true);
    this.fetchGeneratedSellerBillRows(sellerId, loaId, billPeriodFrom, billPeriodTo).subscribe((rows) => {
      this.fetchingSellerBillRows.set(false);
      this.sellerBillRowsFromBackend.set(this.toSellerBillGenerateRows(rows || []));
      this.sellerBillsPage.set(1);
      this.bumpSelectionVersion();
    });
  }

  private fetchSellerBillRowsForEdit(verification: BillVerification, fallbackRowKey?: string): void {
    const sellerId = String(verification.seller_vendor_id || '').trim();
    const loaId = String(verification.loa_id || '').trim();
    const billPeriodFrom = String(verification.bill_period_from || verification.bill_date || '').trim();
    const billPeriodTo = String(verification.bill_period_to || verification.bill_date || '').trim();

    if (!sellerId) {
      this.sellerBillRowsFromBackend.set([]);
      this.fetchingSellerBillRows.set(false);
      this.sellerBillsPage.set(1);
      this.scrollToInvoiceSection();
      return;
    }

    this.fetchingSellerBillRows.set(true);
    this.fetchGeneratedSellerBillRows(sellerId, loaId, billPeriodFrom, billPeriodTo).subscribe((rows) => {
      this.fetchingSellerBillRows.set(false);
      const mappedRows = this.toSellerBillGenerateRows(rows || []);
      this.sellerBillRowsFromBackend.set(mappedRows);
      this.sellerBillsPage.set(1);

      const matchedRow =
        mappedRows.find((item) => String(item.verificationId || '').trim() === String(verification.id || '').trim()) ||
        mappedRows.find((item) =>
          item.loaId === loaId &&
          item.billType === ((verification.bill_type as BillTypeOption) || 'SOP') &&
          item.billPeriodFrom === billPeriodFrom &&
          item.billPeriodTo === billPeriodTo,
        ) ||
        mappedRows[0] ||
        null;

      if (matchedRow) {
        const matchedBuyerId = String(matchedRow.buyerVendorId || '').trim();
        const matchedContractId = String(matchedRow.contractId || '').trim();
        this.selectedSellerBillRowKey.set(matchedRow.rowKey || fallbackRowKey || null);
        this.buyerVendorId = matchedBuyerId;
        if (matchedContractId) {
          this.onContractChange([matchedContractId]);
        } else {
          this.contractId = '';
          this.selectedContractIds = [];
        }
      } else {
        this.selectedSellerBillRowKey.set(fallbackRowKey || null);
        this.buyerVendorId = '';
        this.contractId = '';
        this.selectedContractIds = [];
      }

      this.bumpSelectionVersion();
      this.scrollToInvoiceSection();
    });
  }

  private fetchGeneratedSellerBillRows(
    sellerId: string,
    loaId: string,
    billPeriodFrom: string,
    billPeriodTo: string,
  ): Observable<ExtractedBillMappingRow[]> {
    
    const requestRows = (periodFrom?: string, periodTo?: string): Observable<ExtractedBillMappingRow[]> => {
      const params = new URLSearchParams({ seller_vendor_id: sellerId });
      if (loaId) params.set('loa_id', loaId);
      if (periodFrom) params.set('bill_period_from', periodFrom);
      if (periodTo) params.set('bill_period_to', periodTo);
      return this.api
        .get<ExtractedBillMappingRow[]>(`/api/bill-verifications/generated-rows?${params.toString()}`)
        .pipe(catchError(() => of([] as ExtractedBillMappingRow[])));
    };

    return requestRows(billPeriodFrom, billPeriodTo);
  }

  private buildExtractedBillEntry(
    extractedMappingRow: SellerBillGenerateRow | null,
    inferredBillDate: string,
    inferredBillPeriodFrom: string,
    inferredBillPeriodTo: string,
    itemQuantity: number | null,
    inferredRate: number | null,
    totalAmount: number | null,
  ): BillEntryForm {
    const positiveItemQuantity = itemQuantity !== null && itemQuantity > 0 ? itemQuantity : null;
    const positiveMappedQuantum =
      this.toNumberOrNull(extractedMappingRow?.billedQuantum) ??
      this.toNumberOrNull(extractedMappingRow?.totalQuantum) ??
      this.extractedContextLoaQuantum();
    const resolvedQuantum = this.preferReasonableExtractedQuantum(
      positiveItemQuantity,
      positiveMappedQuantum !== null && positiveMappedQuantum > 0 ? positiveMappedQuantum : null,
    );
    const mappedRate =
      this.toNumberOrNull(extractedMappingRow?.submittedRate) ??
      this.toNumberOrNull(extractedMappingRow?.configuredRateValue) ??
      this.extractRateValue(extractedMappingRow?.configuredRateLabel) ??
      this.extractedContextLoaRate() ??
      null;
    const mappedAmount =
      this.toNumberOrNull(extractedMappingRow?.submittedAmount) ??
      this.toNumberOrNull(extractedMappingRow?.expectedAmount) ??
      this.toNumberOrNull(extractedMappingRow?.totalAmount) ??
      null;
    const derivedRateFromAmount =
      totalAmount !== null && resolvedQuantum !== null && resolvedQuantum > 0 ? Number((totalAmount / resolvedQuantum).toFixed(4)) : null;
    const resolvedRate = inferredRate ?? mappedRate ?? derivedRateFromAmount;

    return {
      billDate: extractedMappingRow?.billDate || inferredBillDate || '',
      billPeriodFrom: extractedMappingRow?.billPeriodFrom || inferredBillPeriodFrom || inferredBillDate || '',
      billPeriodTo: extractedMappingRow?.billPeriodTo || inferredBillPeriodTo || inferredBillDate || '',
      billedQuantum: resolvedQuantum ?? '',
      submittedRate: resolvedRate ?? '',
      submittedAmount: totalAmount ?? mappedAmount ?? '',
    };
  }

  private buildExtractedBillEntries(
    items: ExtractedInvoiceItem[],
    inferredBillDate: string,
    inferredBillPeriodFrom: string,
    inferredBillPeriodTo: string,
    fallbackRate: number | null,
    fallbackAmount: number | null,
  ): BillEntryForm[] {

    if (items.length === 0) {
      return this.billEntries();
    }

    const billLines: BillEntryForm[] = [];

    for (const data of items) {
      const billPeriodFrom = this.toIsoDateInput(data.bill_from_date) || inferredBillPeriodFrom || inferredBillDate || '';
      const billPeriodTo = this.toIsoDateInput(data.bill_to_date) || inferredBillPeriodTo || billPeriodFrom;
      const billDate = billPeriodTo || inferredBillDate || billPeriodFrom;

      billLines.push({
        billDate,
        billPeriodFrom,
        billPeriodTo,
        billedQuantum: this.extractedItemQuantity(data) ?? '',
        submittedRate: this.extractedItemRate(data) ?? fallbackRate ?? '',
        submittedAmount: this.extractedItemAmount(data) ?? fallbackAmount ?? '',
      });

    }

    return billLines;

   
  }

  private applyExtractedEntriesToMappingRows(
    rows: SellerBillGenerateRow[],
    entries: BillEntryForm[],
  ): SellerBillGenerateRow[] {
    if (!rows.length || !entries.length) return rows;

    const normalizedEntries = entries.filter((entry) =>
      this.toNumberOrNull(entry.billedQuantum) !== null ||
      this.toNumberOrNull(entry.submittedRate) !== null ||
      this.toNumberOrNull(entry.submittedAmount) !== null,
    );
    if (!normalizedEntries.length) return rows;

    const usedRows = new Set<string>();
    return rows.map((row, index) => {
      const matchedEntry =
        normalizedEntries.find((entry) => {
          const entryFrom = String(entry.billPeriodFrom || entry.billDate || '').trim();
          const entryTo = String(entry.billPeriodTo || entry.billDate || '').trim();
          return (
            entryFrom &&
            entryTo &&
            row.billPeriodFrom === entryFrom &&
            row.billPeriodTo === entryTo &&
            !usedRows.has(`${row.rowKey}::${entryFrom}::${entryTo}`)
          );
        }) ||
        (index === 0 ? normalizedEntries[0] : null);

      if (!matchedEntry) return row;

      usedRows.add(`${row.rowKey}::${row.billPeriodFrom}::${row.billPeriodTo}`);
      return {
        ...row,
        billedQuantum: this.preferReasonableExtractedQuantum(
          this.toNumberOrNull(matchedEntry.billedQuantum),
          this.toNumberOrNull(row.totalQuantum) ?? this.toNumberOrNull(row.approvedQuantum),
        ),
        submittedRate: this.toNumberOrNull(matchedEntry.submittedRate) ?? row.submittedRate,
        submittedAmount: this.toNumberOrNull(matchedEntry.submittedAmount) ?? row.submittedAmount,
      };
    });
  }

  private deduplicateBillEntries(entries: BillEntryForm[]): BillEntryForm[] {
    const dedup = new Map<string, BillEntryForm>();
    entries.forEach((entry) => {
      const key = [
        String(entry.billDate || '').trim(),
        String(entry.billPeriodFrom || '').trim(),
        String(entry.billPeriodTo || '').trim(),
        String(this.toNumberOrNull(entry.billedQuantum) ?? ''),
        String(this.toNumberOrNull(entry.submittedRate) ?? ''),
        String(this.toNumberOrNull(entry.submittedAmount) ?? ''),
      ].join('::');
      if (!dedup.has(key)) {
        dedup.set(key, entry);
      }
    });
    return [...dedup.values()];
  }

  private buildExtractedBillEntryFromItem(
    item: ExtractedInvoiceItem | null | undefined,
    inferredBillDate: string,
    fallbackRate: number | null,
  ): BillEntryForm | null {
    if (!item) return null;

    const quantity = this.extractedItemQuantity(item);
    const rate = this.extractedItemRate(item);
    const amount = this.extractedItemAmount(item);
    const period = this.extractPeriodFromItemDescription(item.description);
    const billDate = period?.from || inferredBillDate || '';
    const submittedRate =
      rate ??
      (amount !== null && quantity !== null && quantity > 0 ? Number((amount / quantity).toFixed(4)) : fallbackRate);

    if (!billDate && quantity === null && amount === null) {
      return null;
    }

    return {
      billDate,
      billPeriodFrom: period?.from || billDate,
      billPeriodTo: period?.to || period?.from || billDate,
      billedQuantum: quantity ?? '',
      submittedRate: submittedRate ?? '',
      submittedAmount: amount ?? '',
    };
  }

  private currentEditingVerification(): BillVerification | null {
    const editingId = this.editingId();
    if (!editingId) return null;
    return this.verifications().find((item) => item.id === editingId) || null;
  }

  private currentReadOnlyReviewRow(): SellerBillGenerateRow | null {
    const referenceRow = this.currentReferenceRow();
    if (referenceRow?.verificationId) return referenceRow;

    const current = this.currentEditingVerification();
    if (!current) return null;

    return {
      rowKey: String(current.id || ''),
      applicationId: '',
      applicationNumber: current.invoice_number || '-',
      applicationNumberTooltip: current.invoice_number || '-',
      invoiceNumber: current.invoice_number || null,
      sellerVendorId: current.seller_vendor_id,
      sellerName: current.seller_name || this.sellerName(current.seller_vendor_id),
      buyerVendorId: null,
      buyerName: '',
      contractId: null,
      contractNumber: '',
      loaId: current.loa_id,
      loaNumber: current.loa_number || current.loa_id,
      billType: ((current.bill_type as BillTypeOption) || 'SOP'),
      billCycle: current.bill_cycle || '',
      billDate: current.bill_date,
      billPeriodFrom: current.bill_period_from || current.bill_date,
      billPeriodTo: current.bill_period_to || current.bill_date,
      approvedQuantum: null,
      totalQuantum: Number(current.billed_quantum || 0),
      totalAmount: current.expected_amount ?? null,
      expectedAmount: current.expected_amount ?? null,
      billedQuantum: current.billed_quantum ?? null,
      configuredRateLabel: current.expected_rate !== null && current.expected_rate !== undefined ? String(current.expected_rate) : '-',
      configuredRateValue: current.expected_rate ?? null,
      submittedRate: current.submitted_rate ?? null,
      submittedAmount: current.submitted_amount ?? null,
      verificationId: current.id,
      verificationStatus: current.verification_status || 'Pending Verification',
      verificationNotes: current.verification_notes || [],
      approvalStatus: current.approval_status ?? null,
      approvalReason: current.approval_reason ?? null,
      approvalRole: current.approval_role ?? null,
      approvalAt: current.approval_at ?? null,
      statusTooltip: this.statusTooltip(current),
    };
  }

  private revokeInvoicePreviewObjectUrl(): void {
    if (this.invoicePreviewObjectUrl) {
      URL.revokeObjectURL(this.invoicePreviewObjectUrl);
    }
    this.invoicePreviewObjectUrl = null;
    this.invoicePreviewSourceFile = null;
  }

  private clearSelectedFile(): void {
    this.revokeInvoicePreviewObjectUrl();
    this.selectedFile = null;
    const input = this.invoiceFileInput?.nativeElement;
    if (input) input.value = '';
  }

  private buildCreateFormData(): FormData {
    const formData = new FormData();
    if (this.sellerVendorId) formData.append('seller_vendor_id', this.sellerVendorId);
    formData.append('loa_id', this.loaId);
    formData.append('bill_type', this.billType);
    formData.append('invoice_number', this.invoiceNumber.trim());
    if (this.invoiceDate) formData.append('invoice_date', this.invoiceDate);
    formData.append(
      'bill_entries_json',
      JSON.stringify(
        this.billEntries().map((entry) => {
          const resolvedBillCycle = this.selectedBillCycleForDate(entry.billDate);
          const isDailyMultiLine = this.billEntries().length > 1 && resolvedBillCycle === 'daily';
          const normalizedPeriodFrom = isDailyMultiLine ? entry.billDate || null : entry.billPeriodFrom || null;
          const normalizedPeriodTo = isDailyMultiLine ? entry.billDate || null : entry.billPeriodTo || null;

          return {
            bill_type: this.billType,
            bill_date: entry.billDate,
            bill_period_from: normalizedPeriodFrom,
            bill_period_to: normalizedPeriodTo,
            billed_quantum: this.toNumberOrNull(entry.billedQuantum),
            submitted_rate: this.toNumberOrNull(entry.submittedRate),
            submitted_amount: this.toNumberOrNull(entry.submittedAmount),
          };
        }),
      ),
    );
    if (this.remarks.trim()) formData.append('remarks', this.remarks.trim());
    if (this.selectedFile) formData.append('invoice_file', this.selectedFile);
    return formData;
  }

  private buildEditFormData(): FormData {
    const formData = new FormData();
    if (this.sellerVendorId) formData.append('seller_vendor_id', this.sellerVendorId);
    formData.append('loa_id', this.loaId);
    formData.append('bill_type', this.billType);
    formData.append('invoice_number', this.invoiceNumber.trim());
    if (this.invoiceDate) formData.append('invoice_date', this.invoiceDate);
    formData.append('bill_date', this.billDate);
    if (this.billPeriodFrom) formData.append('bill_period_from', this.billPeriodFrom);
    if (this.billPeriodTo) formData.append('bill_period_to', this.billPeriodTo);
    const billedQuantum = this.toNumberOrNull(this.billedQuantum);
    const rate = this.toNumberOrNull(this.submittedRate);
    const amount = this.toNumberOrNull(this.submittedAmount);
    if (billedQuantum !== null) formData.append('billed_quantum', String(billedQuantum));
    if (rate !== null) formData.append('submitted_rate', String(rate));
    if (amount !== null) formData.append('submitted_amount', String(amount));
    if (this.remarks.trim()) formData.append('remarks', this.remarks.trim());
    if (this.selectedFile) formData.append('invoice_file', this.selectedFile);
    return formData;
  }

  private toNumberOrNull(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toLooseNumberOrNull(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const normalized = String(value)
      .trim()
      .replace(/[^0-9.-]/g, '');
    if (!normalized) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private normalizeExtractedContractLoaCollections(
    value: ExtractedContractLoaCollection[] | null | undefined,
  ): ExtractedContractLoaCollection[] {
    if (!Array.isArray(value)) return [];
    const rows: ExtractedContractLoaCollection[] = [];
    value.forEach((item) => {
      const contractId = String(item?.contractId || item?.contract_id || '').trim();
      if (!contractId) return;
      const contractNumber = String(item?.contractNumber || item?.contract_number || contractId).trim() || contractId;
      const loaCollection: ExtractedContractLoaItem[] = [];
      const responderCollection: ExtractedContractResponderItem[] = [];
      const sourceLoaCollection = Array.isArray(item?.loaCollection)
        ? item.loaCollection
        : Array.isArray(item?.loa_collection)
          ? item.loa_collection
          : [];
      const sourceResponderCollection = Array.isArray(item?.responderCollection)
        ? item.responderCollection
        : Array.isArray(item?.responder_collection)
          ? item.responder_collection
          : [];

      sourceLoaCollection.forEach((loa) => {
        const loaId = String(loa?.loaId || loa?.loa_id || '').trim();
        if (!loaId) return;
        const loaNumber = String(loa?.loaNumber || loa?.loa_number || loaId).trim() || loaId;
        loaCollection.push({
          loa_id: loaId,
          loa_number: loaNumber,
          loaId,
          loaNumber: loaNumber,
        });
      });
      sourceResponderCollection.forEach((responder) => {
        const responderVendorId = String(responder?.responderVendorId || responder?.responder_vendor_id || '').trim();
        if (!responderVendorId) return;
        const responderVendorName =
          String(responder?.responderVendorName || responder?.responder_vendor_name || responderVendorId).trim() || responderVendorId;
        responderCollection.push({
          responder_vendor_id: responderVendorId,
          responder_vendor_name: responderVendorName,
          responderVendorId,
          responderVendorName,
        });
      });
      rows.push({
        contract_id: contractId,
        contract_number: contractNumber,
        loa_collection: loaCollection,
        responder_collection: responderCollection,
        contractId,
        contractNumber,
        loaCollection,
        responderCollection,
      });
    });
    return rows;
  }

  private extractedCollectionKey(item: ExtractedContractLoaCollection): string {
    const contractId = String(item.contractId || item.contract_id || '').trim();
    const loaIds = Array.from(
      new Set(
        (item.loaCollection || item.loa_collection || [])
          .map((loa) => String(loa.loaId || loa.loa_id || '').trim())
          .filter(Boolean),
      ),
    ).sort((left, right) => left.localeCompare(right));
    const buyerVendorIds = Array.from(
      new Set(
        (item.responderCollection || item.responder_collection || [])
          .map((buyer) => String(buyer.responderVendorId || buyer.responder_vendor_id || '').trim())
          .filter(Boolean),
      ),
    ).sort((left, right) => left.localeCompare(right));

    return [contractId, loaIds.join(','), buyerVendorIds.join(',')].join('::');
  }

  private selectedExtractedCollectionFilters(): ExtractedCollectionFilterState[] {
    const selectedKeys = new Set(this.selectedExtractedCollectionKeys());
    if (!selectedKeys.size) return [];

    return this.visibleExtractedContractLoaCollections()
      .map((item) => this.extractedCollectionScope(item))
      .filter((item): item is ExtractedCollectionFilterState => !!item && selectedKeys.has(item.key));
  }

  private extractedCollectionScope(item: ExtractedContractLoaCollection): ExtractedCollectionFilterState | null {
    const contractId = String(item.contractId || item.contract_id || '').trim();
    if (!contractId) return null;

    const loaIds = Array.from(
      new Set(
        (item.loaCollection || item.loa_collection || [])
          .map((loa) => String(loa.loaId || loa.loa_id || '').trim())
          .filter(Boolean),
      ),
    );
    const buyerVendorIds = Array.from(
      new Set(
        (item.responderCollection || item.responder_collection || [])
          .map((buyer) => String(buyer.responderVendorId || buyer.responder_vendor_id || '').trim())
          .filter(Boolean),
      ),
    );
    const key = this.extractedCollectionKey(item);
    if (!key) return null;

    return { key, contractId, loaIds, buyerVendorIds };
  }

  private selectAllExtractedCollections(): void {
    this.selectedExtractedCollectionKeys.set(
      this.visibleExtractedContractLoaCollections()
        .map((item) => this.extractedCollectionKey(item))
        .filter(Boolean),
    );
  }

  private rowHasPunchedApplications(row: SellerBillGenerateRow): boolean {
    return this.punchedApplicationCount(row) > 0;
  }

  private matchesSellerBillRowFilters(
    row: SellerBillGenerateRow,
    clientId: string,
    loaId: string,
    cycle: string,
    term: string,
    dateFrom: string,
    dateTo: string,
    uploadedCycle: string,
    uploadedFrom: string,
    uploadedTo: string,
    selectedCollectionFilters: ExtractedCollectionFilterState[],
  ): boolean {
    if (clientId && row.sellerVendorId !== clientId) return false;
    if (loaId && row.loaId !== loaId) return false;
    if (uploadedCycle && row.billCycle.trim().toLowerCase() !== uploadedCycle) return false;
    if (cycle && row.billCycle.trim().toLowerCase() !== cycle) return false;

    if (term) {
      const matches = [
        row.applicationNumber,
        row.invoiceNumber,
        row.sellerName,
        row.buyerName,
        row.contractNumber,
        row.loaNumber,
        row.billType,
        row.billCycle,
        row.verificationStatus,
        row.approvalStatus,
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
      if (!matches) return false;
    }

    const billDate = (row.billDate || '').trim();
    if (dateFrom && (!billDate || billDate < dateFrom)) return false;
    if (dateTo && (!billDate || billDate > dateTo)) return false;

    const rowFrom = (row.billPeriodFrom || '').trim();
    const rowTo = (row.billPeriodTo || '').trim();
    const activeBillLine = this.activeBillLineEntries()[0];
    const selectedBillPeriodFrom = String(activeBillLine?.billPeriodFrom || this.billPeriodFrom || '').trim();
    const selectedBillPeriodTo = String(activeBillLine?.billPeriodTo || this.billPeriodTo || '').trim();
    if (!this.rowMatchesSelectedPeriod(rowFrom, rowTo, selectedBillPeriodFrom, selectedBillPeriodTo)) return false;
    if (uploadedFrom && rowFrom !== uploadedFrom) return false;
    if (uploadedTo && rowTo !== uploadedTo) return false;

    if (this.extractedContractLoaCollections().length) {
      if (!selectedCollectionFilters.length) return false;
      if (!this.matchesSelectedCollectionRow(row, selectedCollectionFilters)) return false;
    }

    return true;
  }

  private isAdminVisibleBillRow(row: SellerBillGenerateRow): boolean {
    if (!this.isBillViewOnlyAdmin()) return true;
    if (!String(row.verificationId || '').trim()) return false;

    const approvalStatus = String(row.approvalStatus || '').trim().toLowerCase();
    if (approvalStatus === 'approved' || approvalStatus === 'rejected' || approvalStatus === 'force approved') {
      return true;
    }

    const verificationStatus = String(row.verificationStatus || '').trim().toLowerCase();
    return !!verificationStatus && verificationStatus !== 'pending verification';
  }

  private matchesSelectedCollectionRow(
    row: SellerBillGenerateRow,
    selectedCollectionFilters: ExtractedCollectionFilterState[],
  ): boolean {
    const rowContractId = String(row.contractId || '').trim();
    const rowLoaId = String(row.loaId || '').trim();
    const rowBuyerId = String(row.buyerVendorId || '').trim();
    return selectedCollectionFilters.some((filter) => {
      if (rowContractId !== filter.contractId) return false;
      if (filter.loaIds.length && !filter.loaIds.includes(rowLoaId)) return false;
      if (filter.buyerVendorIds.length && !filter.buyerVendorIds.includes(rowBuyerId)) return false;
      return true;
    });
  }

  private buildRowsFromExtractedContractLoaCollections(
    collections: ExtractedContractLoaCollection[],
  ): SellerBillGenerateRow[] {
    if (!collections.length) return [];

    const contractLoaPairs = new Set<string>();
    collections.forEach((item) => {
      const contractId = String(item.contractId || item.contract_id || '').trim();
      if (!contractId) return;
      const loas = Array.isArray(item.loaCollection) ? item.loaCollection : item.loa_collection || [];
      loas.forEach((loa) => {
        const loaId = String(loa.loaId || loa.loa_id || '').trim();
        if (!loaId) return;
        contractLoaPairs.add(`${contractId}::${loaId}`);
      });
    });

    if (!contractLoaPairs.size) return [];

    const dedup = new Map<string, SellerBillGenerateRow>();
    this.sellerBillsToGenerate()
      .filter((row) => {
        const contractId = String(row.contractId || '').trim();
        const loaId = String(row.loaId || '').trim();
        if (!contractId || !loaId) return false;
        return contractLoaPairs.has(`${contractId}::${loaId}`);
      })
      .forEach((row) => {
        dedup.set(row.rowKey, row);
      });

    return [...dedup.values()];
  }

  private mergeExtractedMappingRows(
    payloadRows: SellerBillGenerateRow[],
    collectionRows: SellerBillGenerateRow[],
  ): SellerBillGenerateRow[] {
    const dedup = new Map<string, SellerBillGenerateRow>();
    [...payloadRows, ...collectionRows].forEach((row) => {
      if (!row?.rowKey) return;
      if (!dedup.has(row.rowKey)) {
        dedup.set(row.rowKey, row);
      }
    });
    return [...dedup.values()];
  }

  private mergedMappingRows(
    backendRows: SellerBillGenerateRow[],
    extractedRows: SellerBillGenerateRow[],
  ): SellerBillGenerateRow[] {
    if (!extractedRows.length) return backendRows;
    if (!backendRows.length) return extractedRows;

    const extractedByKey = new Map<string, SellerBillGenerateRow>();
    const extractedBySignature = new Map<string, SellerBillGenerateRow>();
    const extractedByPeriodSignature = new Map<string, SellerBillGenerateRow>();

    extractedRows.forEach((row) => {
      extractedByKey.set(row.rowKey, row);
      extractedBySignature.set(this.mappingRowSignature(row), row);
      extractedByPeriodSignature.set(this.mappingRowPeriodSignature(row), row);
    });

    const merged = backendRows.map((row) => {
      const extracted =
        extractedByKey.get(row.rowKey) ||
        extractedBySignature.get(this.mappingRowSignature(row)) ||
        extractedByPeriodSignature.get(this.mappingRowPeriodSignature(row)) ||
        null;
      if (!extracted) return row;
      return {
        ...row,
        sellerVendorId: this.preferredRequiredMappingId(row.sellerVendorId, extracted.sellerVendorId),
        sellerName: this.preferredMappingText(row.sellerName, extracted.sellerName, ['-', 'No seller linked']),
        buyerVendorId: this.preferredMappingId(row.buyerVendorId, extracted.buyerVendorId),
        buyerName: this.preferredMappingText(row.buyerName, extracted.buyerName, ['-', 'No buyer linked']),
        contractId: this.preferredMappingId(row.contractId, extracted.contractId),
        contractNumber: this.preferredMappingText(row.contractNumber, extracted.contractNumber, ['-', 'No contract linked']),
        loaId: this.preferredMappingId(row.loaId, extracted.loaId) || row.loaId,
        loaNumber: this.preferredMappingText(row.loaNumber, extracted.loaNumber, ['-']),
        applicationId: extracted.applicationId || row.applicationId,
        applicationNumber: extracted.applicationNumber !== '-' ? extracted.applicationNumber : row.applicationNumber,
        applicationNumberTooltip:
          extracted.applicationNumberTooltip !== '-' ? extracted.applicationNumberTooltip : row.applicationNumberTooltip,
        invoiceNumber: extracted.invoiceNumber ?? row.invoiceNumber,
        billedQuantum: extracted.billedQuantum ?? row.billedQuantum,
        submittedRate: extracted.submittedRate ?? row.submittedRate,
        submittedAmount: extracted.submittedAmount ?? row.submittedAmount,
        verificationStatus: extracted.verificationStatus || row.verificationStatus,
        verificationNotes: extracted.verificationNotes?.length ? extracted.verificationNotes : row.verificationNotes,
      } satisfies SellerBillGenerateRow;
    });

    return merged;
  }

  private preferredMappingId(current: string | null | undefined, extracted: string | null | undefined): string | null {
    const currentValue = String(current || '').trim();
    const extractedValue = String(extracted || '').trim();
    if (currentValue) return currentValue;
    return extractedValue || null;
  }

  private preferredRequiredMappingId(current: string | null | undefined, extracted: string | null | undefined): string {
    return this.preferredMappingId(current, extracted) || '';
  }

  private preferredMappingText(
    current: string | null | undefined,
    extracted: string | null | undefined,
    placeholders: string[] = [],
  ): string {
    const currentValue = String(current || '').trim();
    const extractedValue = String(extracted || '').trim();
    const blockedValues = new Set(placeholders.map((value) => value.trim().toLowerCase()));
    if (currentValue && !blockedValues.has(currentValue.toLowerCase())) {
      return currentValue;
    }
    if (extractedValue) {
      return extractedValue;
    }
    return currentValue;
  }

  private mappingRowSignature(row: SellerBillGenerateRow): string {
    return [
      String(row.loaId || '').trim(),
      String(row.billType || '').trim().toUpperCase(),
      String(row.billPeriodFrom || '').trim(),
      String(row.billPeriodTo || '').trim(),
      String(row.contractId || '').trim(),
      String(row.buyerVendorId || '').trim(),
    ].join('::');
  }

  private mappingRowPeriodSignature(row: SellerBillGenerateRow): string {
    return [
      String(row.loaId || '').trim(),
      String(row.billType || '').trim().toUpperCase(),
      String(row.billPeriodFrom || '').trim(),
      String(row.billPeriodTo || '').trim(),
      String(row.buyerVendorId || '').trim(),
    ].join('::');
  }

  private mergeTableSellerBillRows(
    baseRows: SellerBillGenerateRow[],
    manualRows: SellerBillGenerateRow[],
  ): SellerBillGenerateRow[] {
    const manualRowsBySignature = new Map<string, SellerBillGenerateRow>();
    const manualRowsByPeriodSignature = new Map<string, SellerBillGenerateRow>();

    manualRows.forEach((row) => {
      manualRowsBySignature.set(this.mappingRowSignature(row), row);
      manualRowsByPeriodSignature.set(this.mappingRowPeriodSignature(row), row);
    });

    const matchedManualRowKeys = new Set<string>();
    const merged = baseRows.map((row) => {
      const manualOverride =
        manualRowsBySignature.get(this.mappingRowSignature(row)) ||
        manualRowsByPeriodSignature.get(this.mappingRowPeriodSignature(row)) ||
        null;
      if (!manualOverride) return row;

      matchedManualRowKeys.add(String(manualOverride.rowKey || this.mappingRowSignature(manualOverride)).trim());
      return {
        ...row,
        billDate: String(manualOverride.billDate || row.billDate).trim() || row.billDate,
        billPeriodFrom: String(manualOverride.billPeriodFrom || row.billPeriodFrom).trim() || row.billPeriodFrom,
        billPeriodTo: String(manualOverride.billPeriodTo || row.billPeriodTo).trim() || row.billPeriodTo,
        billedQuantum: this.toNumberOrNull(manualOverride.billedQuantum) ?? row.billedQuantum,
        submittedRate: this.toNumberOrNull(manualOverride.submittedRate) ?? row.submittedRate,
        submittedAmount: this.toNumberOrNull(manualOverride.submittedAmount) ?? row.submittedAmount,
        totalAmount: this.toNumberOrNull(manualOverride.totalAmount) ?? row.totalAmount,
        expectedAmount: this.toNumberOrNull(manualOverride.expectedAmount) ?? row.expectedAmount,
        invoiceNumber: manualOverride.invoiceNumber ?? row.invoiceNumber,
        verificationStatus: manualOverride.verificationStatus || row.verificationStatus,
        verificationNotes: manualOverride.verificationNotes?.length ? manualOverride.verificationNotes : row.verificationNotes,
        statusTooltip: manualOverride.statusTooltip ?? row.statusTooltip,
      } satisfies SellerBillGenerateRow;
    });

    const unmatchedManualRows = manualRows.filter((row) => {
      const uniqueKey = String(row.rowKey || this.mappingRowSignature(row)).trim();
      return !matchedManualRowKeys.has(uniqueKey);
    });

    return [...merged, ...unmatchedManualRows];
  }

  private withActiveBillLineValues(rows: SellerBillGenerateRow[]): SellerBillGenerateRow[] {
    const activeEntries = this.activeBillLineEntries();
    if (!activeEntries.length) return rows;

    const selectedRowKey = this.selectedSellerBillRowKey();
    const referenceRow = this.currentReferenceRow();

    return rows.map((row) => {
      const matchedEntry =
        activeEntries.find((entry) => this.billEntryMatchesSellerBillRow(entry, row)) ||
        null;
      const isSelected =
        (selectedRowKey && row.rowKey === selectedRowKey) ||
        (!!referenceRow && row.rowKey === referenceRow.rowKey);

      const activeEntry = matchedEntry || (isSelected ? activeEntries[0] : null);
      if (!activeEntry) return row;

      const editedQuantum = this.toNumberOrNull(activeEntry.billedQuantum);

      return {
        ...row,
        billedQuantum: editedQuantum ?? row.billedQuantum,
        submittedRate: this.toNumberOrNull(activeEntry.submittedRate) ?? row.submittedRate,
        submittedAmount: this.toNumberOrNull(activeEntry.submittedAmount) ?? row.submittedAmount,
      };
    });
  }

  private fallbackSellerBillRowsFromEntries(): SellerBillGenerateRow[] {
    if (!(this.showForm() && this.showBillSection())) return [];

    const entries = this.activeBillLineEntries();
    if (!entries.length) return [];

    const sellerVendorId = String(this.sellerVendorId || '').trim();
    const buyerVendorId = String(this.buyerVendorId || '').trim() || null;
    const contractId = String(this.contractId || '').trim() || null;
    const loaId = String(this.loaId || '').trim();
    if (!loaId) return [];

    const loa = this.loas().find((row) => String(row.id) === loaId);
    const contract = contractId ? this.contracts().find((row) => String(row.id) === contractId) || null : null;
    const buyerName = buyerVendorId ? this.vendors().find((row) => String(row.id) === buyerVendorId)?.vendor_name || buyerVendorId : 'No buyer linked';
    const extractedRateRow =
      this.extractedBillMappingRows().find((row) =>
        String(row.loaId || '').trim() === loaId &&
        String(row.contractId || '').trim() === contractId &&
        String(row.buyerVendorId || '').trim() === String(buyerVendorId || '').trim(),
      ) ||
      this.currentReferenceRow();
    const configuredRateValue =
      this.toNumberOrNull(extractedRateRow?.configuredRateValue) ??
      this.sellerLoaRateValue(loa);
    const configuredRateLabel =
      String(extractedRateRow?.configuredRateLabel || '').trim() ||
      this.sellerLoaRateLabel(loa);

    const meaningfulEntries = entries.filter((entry) =>
      !!String(entry.billDate || '').trim() ||
      !!String(entry.billPeriodFrom || '').trim() ||
      !!String(entry.billPeriodTo || '').trim() ||
      this.toNumberOrNull(entry.billedQuantum) !== null ||
      this.toNumberOrNull(entry.submittedAmount) !== null,
    );
    if (!meaningfulEntries.length) return [];
    type FallbackSegment = BillEntryForm & {
      cycleBillDate: string;
      cycleBillPeriodFrom: string;
      cycleBillPeriodTo: string;
      cycleBillCycle: string;
    };

    type FallbackGroup = {
      key: string;
      billCycle: string;
      bucketFrom: string;
      bucketTo: string;
      billDate: string;
      entries: FallbackSegment[];
    };

    const groups = new Map<string, FallbackGroup>();
    meaningfulEntries.forEach((entry, index) => {
      const segments = this.splitFallbackEntryByBillCycle(entry);
      segments.forEach((segment, segmentIndex) => {
        const anchorDate = String(segment.cycleBillDate || segment.cycleBillPeriodTo || segment.cycleBillPeriodFrom || '').trim();
        const billCycle = segment.cycleBillCycle || this.selectedBillCycleForDate(anchorDate) || '';
        const normalizedCycle = billCycle.trim().toLowerCase();

        if (!anchorDate || normalizedCycle.startsWith('daily') || !normalizedCycle) {
          groups.set(`line::${index}::${segmentIndex}`, {
            key: `line::${index}::${segmentIndex}`,
            billCycle,
            bucketFrom: String(segment.cycleBillPeriodFrom || segment.billDate || '').trim(),
            bucketTo: String(segment.cycleBillPeriodTo || segment.billDate || '').trim(),
            billDate: String(segment.cycleBillDate || segment.billDate || '').trim(),
            entries: [segment],
          });
          return;
        }

        const bucket = this.billCycleBucket(
          billCycle,
          anchorDate,
        );
        const existing = groups.get(bucket.bucketKey);
        if (existing) {
          existing.entries.push(segment);
          return;
        }
        groups.set(bucket.bucketKey, {
          key: bucket.bucketKey,
          billCycle,
          bucketFrom: bucket.bucketFrom,
          bucketTo: bucket.bucketTo,
          billDate: bucket.bucketTo,
          entries: [segment],
        });
      });
    });

    return [...groups.values()].map((group, index) => {
      const billedQuantum = Number(
        group.entries.reduce((sum, entry) => sum + (this.toNumberOrNull(entry.billedQuantum) ?? 0), 0).toFixed(4),
      );
      const approvedQuantum = this.extractedContextLoaQuantum();
      const referenceQuantum = approvedQuantum !== null ? approvedQuantum : billedQuantum;
      const expectedAmountValue = this.calculateExpectedAmount(approvedQuantum ?? referenceQuantum, configuredRateValue);
      const submittedAmountValue = Number(
        group.entries.reduce((sum, entry) => {
          const amount = this.toNumberOrNull(entry.submittedAmount);
          if (amount !== null) return sum + amount;
          const quantum = this.toNumberOrNull(entry.billedQuantum);
          const rate = this.toNumberOrNull(entry.submittedRate);
          return sum + (quantum !== null && rate !== null ? quantum * rate : 0);
        }, 0).toFixed(2),
      );
      const submittedRate =
        billedQuantum > 0 ? Number((submittedAmountValue / billedQuantum).toFixed(4)) : this.toNumberOrNull(group.entries[0]?.submittedRate);

      return {
        rowKey: `uploaded-entry::${loaId}::${group.key}::${group.bucketFrom}::${group.bucketTo}::${index}`,
        applicationId: '',
        applicationNumber: '-',
        applicationNumberTooltip: '-',
        invoiceNumber: this.invoiceNumber || null,
        sellerVendorId,
        sellerName: this.selectedSellerName(),
        buyerVendorId,
        buyerName,
        contractId,
        contractNumber: contract?.contract_number || (contractId || 'No contract linked'),
        loaId,
        loaNumber: loa?.loa_number || loaId,
        billType: this.billType,
        billCycle: group.billCycle,
        billDate: group.billDate,
        billPeriodFrom: group.bucketFrom,
        billPeriodTo: group.bucketTo,
        approvedQuantum,
        totalQuantum: referenceQuantum,
        totalAmount: expectedAmountValue ?? submittedAmountValue,
        expectedAmount: expectedAmountValue ?? submittedAmountValue,
        billedQuantum,
        configuredRateLabel,
        configuredRateValue,
        submittedRate,
        submittedAmount: submittedAmountValue,
        verificationId: null,
        verificationStatus: 'Pending Verification',
        verificationNotes: [],
        approvalStatus: null,
        approvalReason: null,
        approvalRole: null,
        approvalAt: null,
        statusTooltip: 'Showing uploaded bill line summary because no generated mapping row is available yet.',
      } satisfies SellerBillGenerateRow;
    });
  }

  private meaningfulBillLineEntries(): BillEntryForm[] {
    return this.activeBillLineEntries().filter((entry) =>
      !!String(entry.billDate || '').trim() ||
      !!String(entry.billPeriodFrom || '').trim() ||
      !!String(entry.billPeriodTo || '').trim() ||
      this.toNumberOrNull(entry.billedQuantum) !== null ||
      this.toNumberOrNull(entry.submittedAmount) !== null,
    );
  }

  private rowsMatchUploadedBillLinePeriods(rows: SellerBillGenerateRow[], entries: BillEntryForm[]): boolean {
    const meaningfulEntries = (entries || []).filter((entry) =>
      !!String(entry.billPeriodFrom || '').trim() || !!String(entry.billPeriodTo || '').trim(),
    );
    if (!meaningfulEntries.length) return true;
    if (!rows.length) return false;

    return meaningfulEntries.every((entry) => {
      const entryFrom = String(entry.billPeriodFrom || entry.billDate || '').trim();
      const entryTo = String(entry.billPeriodTo || entry.billDate || '').trim();
      if (!entryFrom && !entryTo) return false;

      return rows.some((row) =>
        String(row.billPeriodFrom || '').trim() === entryFrom &&
        String(row.billPeriodTo || '').trim() === entryTo,
      );
    });
  }

  private splitFallbackEntryByBillCycle(entry: BillEntryForm): Array<BillEntryForm & {
    cycleBillDate: string;
    cycleBillPeriodFrom: string;
    cycleBillPeriodTo: string;
    cycleBillCycle: string;
  }> {
    const anchorDate = String(entry.billDate || entry.billPeriodTo || entry.billPeriodFrom || '').trim();
    const billCycle = this.selectedBillCycleForDate(anchorDate) || '';
    return this.buildBillEntriesForCycle(entry, billCycle).map((item) => ({
      ...item,
      cycleBillDate: item.billDate,
      cycleBillPeriodFrom: item.billPeriodFrom,
      cycleBillPeriodTo: item.billPeriodTo,
      cycleBillCycle: billCycle,
    }));
  }

  private buildBillEntriesForCycle(
    entry: BillEntryForm,
    billCycle: string,
  ): Array<BillEntryForm> {
    const normalizedCycle = billCycle.trim().toLowerCase();
    const entryFrom = String(entry.billPeriodFrom || entry.billDate || '').trim();
    const entryTo = String(entry.billPeriodTo || entry.billDate || '').trim();
    const parsedFrom = this.parseDateValue(entryFrom);
    const parsedTo = this.parseDateValue(entryTo || entryFrom);

    if (!parsedFrom || !parsedTo || !normalizedCycle) {
      return [{
        ...entry,
        billDate: String(entry.billDate || entryTo || entryFrom || '').trim(),
        billPeriodFrom: entryFrom,
        billPeriodTo: entryTo || entryFrom,
      }];
    }

    const rangeStart = parsedFrom.getTime() <= parsedTo.getTime() ? parsedFrom : parsedTo;
    const rangeEnd = parsedFrom.getTime() <= parsedTo.getTime() ? parsedTo : parsedFrom;
    const periods = this.billPeriodsForCycle(rangeStart, rangeEnd, billCycle);
    if (periods.length <= 1) {
      return [{
        ...entry,
        billDate: String(entry.billDate || entryTo || entryFrom || '').trim() || this.formatIsoDate(rangeEnd),
        billPeriodFrom: entryFrom || this.formatIsoDate(rangeStart),
        billPeriodTo: entryTo || entryFrom || this.formatIsoDate(rangeEnd),
      }];
    }

    const totalDays = this.rangeDayCount(rangeStart, rangeEnd);
    const totalQuantum = this.toNumberOrNull(entry.billedQuantum);
    const totalAmount = this.toNumberOrNull(entry.submittedAmount);
    const fallbackRate = this.toNumberOrNull(entry.submittedRate);

    return periods.map((period) => {
      const overlapFrom = period.from.getTime() > rangeStart.getTime() ? period.from : rangeStart;
      const overlapTo = period.to.getTime() < rangeEnd.getTime() ? period.to : rangeEnd;
      const overlapDays = overlapTo.getTime() >= overlapFrom.getTime() ? this.rangeDayCount(overlapFrom, overlapTo) : 0;
      const ratio = totalDays > 0 ? overlapDays / totalDays : 0;
      const billedQuantum =
        totalQuantum !== null ? Number((totalQuantum * ratio).toFixed(4)) : entry.billedQuantum;
      const submittedAmount =
        totalAmount !== null ? Number((totalAmount * ratio).toFixed(2)) : entry.submittedAmount;

      let submittedRate: string | number = entry.submittedRate;
      if (typeof billedQuantum === 'number' && billedQuantum > 0 && typeof submittedAmount === 'number') {
        submittedRate = Number((submittedAmount / billedQuantum).toFixed(4));
      } else if (fallbackRate !== null) {
        submittedRate = fallbackRate;
      }
      return {
        ...entry,
        billDate: this.formatIsoDate(period.billDate),
        billPeriodFrom: this.formatIsoDate(period.from),
        billPeriodTo: this.formatIsoDate(period.to),
        billedQuantum,
        submittedAmount,
        submittedRate,
      };
    });
  }

  private buildManualSellerBillRowsForCycle(input: {
    loaId: string;
    loaNumber: string;
    sellerVendorId: string;
    sellerName: string;
    buyerVendorId: string | null;
    buyerName: string;
    contractId: string | null;
    contractNumber: string;
    billType: BillTypeOption;
    billCycle: string;
    approvedQuantum: number | null;
    billedQuantum: number | null;
    configuredRateValue: number | null;
    submittedRate: number | null;
    expectedAmount: number | null;
    submittedAmount: number | null;
    billPeriodFrom: string;
    billPeriodTo: string;
    billDate: string;
  }): SellerBillGenerateRow[] {
    const entrySegments = this.buildBillEntriesForCycle(
      {
        billDate: input.billDate,
        billPeriodFrom: input.billPeriodFrom,
        billPeriodTo: input.billPeriodTo,
        billedQuantum: input.billedQuantum ?? '',
        submittedRate: input.submittedRate ?? '',
        submittedAmount: input.submittedAmount ?? '',
      },
      input.billCycle,
    );
    const approvedSegments = this.buildBillEntriesForCycle(
      {
        billDate: input.billDate,
        billPeriodFrom: input.billPeriodFrom,
        billPeriodTo: input.billPeriodTo,
        billedQuantum: input.approvedQuantum ?? '',
        submittedRate: input.configuredRateValue ?? '',
        submittedAmount: input.expectedAmount ?? '',
      },
      input.billCycle,
    );

    return entrySegments.map((segment, index) => {
      const approvedSegment = approvedSegments[index] || approvedSegments[0] || null;
      const approvedQuantum = this.toNumberOrNull(approvedSegment?.billedQuantum);
      const billedQuantum = this.toNumberOrNull(segment.billedQuantum);
      const submittedRate = this.toNumberOrNull(segment.submittedRate);
      const submittedAmount = this.toNumberOrNull(segment.submittedAmount);
      const expectedAmount =
        approvedQuantum !== null && input.configuredRateValue !== null
          ? Number((approvedQuantum * input.configuredRateValue).toFixed(2))
          : this.toNumberOrNull(approvedSegment?.submittedAmount);

      return {
        rowKey: `manual-entry::${input.loaId}::${segment.billPeriodFrom}::${segment.billPeriodTo}::${Date.now()}::${index}`,
        applicationId: '',
        applicationNumber: '-',
        applicationNumberTooltip: '-',
        invoiceNumber: this.invoiceNumber || null,
        sellerVendorId: input.sellerVendorId,
        sellerName: input.sellerName,
        buyerVendorId: input.buyerVendorId,
        buyerName: input.buyerName,
        contractId: input.contractId,
        contractNumber: input.contractNumber,
        loaId: input.loaId,
        loaNumber: input.loaNumber,
        billType: input.billType,
        billCycle: input.billCycle,
        billDate: segment.billDate,
        billPeriodFrom: segment.billPeriodFrom,
        billPeriodTo: segment.billPeriodTo,
        approvedQuantum,
        totalQuantum: approvedQuantum ?? billedQuantum ?? 0,
        totalAmount: expectedAmount,
        expectedAmount,
        billedQuantum,
        configuredRateLabel: input.configuredRateValue !== null ? String(input.configuredRateValue) : '-',
        configuredRateValue: input.configuredRateValue,
        submittedRate,
        submittedAmount,
        verificationId: null,
        verificationStatus: 'Pending Verification',
        verificationNotes: [],
        approvalStatus: null,
        approvalReason: null,
        approvalRole: null,
        approvalAt: null,
        statusTooltip: 'Manually added row from Add Bill dialog.',
      };
    });
  }

  private billEntryMatchesSellerBillRow(entry: BillEntryForm, row: SellerBillGenerateRow): boolean {
    const entryDate = String(entry.billDate || '').trim();
    const entryFrom = String(entry.billPeriodFrom || entry.billDate || '').trim();
    const entryTo = String(entry.billPeriodTo || entry.billDate || '').trim();
    const rowDate = String(row.billDate || '').trim();
    const rowFrom = String(row.billPeriodFrom || '').trim();
    const rowTo = String(row.billPeriodTo || '').trim();

    const matchesPeriod = !!entryFrom && !!entryTo && entryFrom === rowFrom && entryTo === rowTo;
    const matchesDate = !!entryDate && !!rowDate && entryDate === rowDate;
    if (matchesPeriod || matchesDate) return true;

    return this.splitFallbackEntryByBillCycle(entry).some((segment) =>
      this.rowMatchesSelectedPeriod(
        rowFrom,
        rowTo,
        String(segment.cycleBillPeriodFrom || segment.billPeriodFrom || segment.billDate || '').trim(),
        String(segment.cycleBillPeriodTo || segment.billPeriodTo || segment.billDate || '').trim(),
      ),
    );
  }

  private scrollToInvoiceSection(): void {
    setTimeout(() => {
      this.invoiceFormSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  private createEmptyBillEntry(): BillEntryForm {
    return {
      billDate: '',
      billPeriodFrom: '',
      billPeriodTo: '',
      billedQuantum: '',
      submittedRate: '',
      submittedAmount: '',
    };
  }

  private createManualSellerBillRowForm(): ManualSellerBillRowForm {
    const selectedLoaId = String(this.loaId || '').trim();
    const loa = this.loas().find((item) => String(item.id) === selectedLoaId);
    const loaRate = this.sellerLoaRateValue(loa);
    return {
      buyerVendorId: String(this.buyerVendorId || '').trim(),
      contractId: String(this.contractId || '').trim(),
      loaId: selectedLoaId,
      billType: this.billType,
      billCycle: this.selectedBillCycleForDate(this.billDate || this.billEntries()[0]?.billDate) || 'daily',
      invoiceDate: String(this.invoiceDate || '').trim(),
      billDate: '',
      billPeriodFrom: '',
      billPeriodTo: '',
      approvedQuantum: '',
      billedQuantum: '',
      loaRate: loaRate !== null ? String(loaRate) : '',
      submittedRate: '',
      expectedAmount: '',
      submittedAmount: '',
    };
  }

  private calculateSubmittedAmount(
    billedQuantum: string | number | null | undefined,
    submittedRate: string | number | null | undefined,
  ): string | number {
    const quantum = this.toNumberOrNull(billedQuantum);
    const rate = this.toNumberOrNull(submittedRate);
    if (quantum === null || rate === null) return '';
    return Number((quantum * rate).toFixed(2));
  }

  private calculateExpectedAmount(
    approvedQuantum: string | number | null | undefined,
    loaRate: string | number | null | undefined,
  ): number | null {
    const quantum = this.toNumberOrNull(approvedQuantum);
    const rate = this.toNumberOrNull(loaRate);
    if (quantum === null || rate === null) return null;
    return Number((quantum * rate).toFixed(2));
  }

  private preferReasonableExtractedQuantum(
    extractedQuantum: number | null,
    mappedQuantum: number | null,
  ): number | null {
    if (extractedQuantum === null) return mappedQuantum;
    if (mappedQuantum === null || mappedQuantum <= 0) return extractedQuantum;

    // Ignore obvious OCR/LLM quantity errors when the extracted value is far away from the mapped quantum.
    if (extractedQuantum >= mappedQuantum * 5 || extractedQuantum <= mappedQuantum / 5) {
      return mappedQuantum;
    }

    return extractedQuantum;
  }

  private findVendorIdByName(name: string): string | null {
    const wanted = this.normalizeLookupName(name);
    if (!wanted) return null;
    const exact = this.vendors().find((vendor) => this.normalizeLookupName(vendor.vendor_name) === wanted);
    if (exact) return String(exact.id);
    const partial = this.vendors().find((vendor) => {
      const source = this.normalizeLookupName(vendor.vendor_name);
      return source.includes(wanted) || wanted.includes(source);
    });
    return partial ? String(partial.id) : null;
  }

  private findSellerVendorIdFromExtracted(seller: {
    seller_name?: string | null;
    seller_cin?: string | null;
    seller_email?: string | null;
    seller_state?: string | null;
    seller_gstin?: string | null;
    seller_address?: string | null;
  }): string | null {
    const normalized = {
      name: this.normalizeLookupName(seller.seller_name),
      cin: this.normalizeTaxId(seller.seller_cin),
      email: this.normalizeEmail(seller.seller_email),
      state: this.normalizeLookupName(seller.seller_state),
      gstin: this.normalizeTaxId(seller.seller_gstin),
      address: this.normalizeAddress(seller.seller_address),
    };

    const checks = [
      { value: normalized.name, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name },
      { value: normalized.cin, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.cin) === normalized.cin },
      { value: normalized.email, matches: (vendor: Vendor) => this.normalizeEmail(vendor.email) === normalized.email },
      { value: normalized.state, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.state) === normalized.state },
      { value: normalized.gstin, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.gst_number) === normalized.gstin },
      {
        value: normalized.address,
        matches: (vendor: Vendor) =>
          this.normalizeAddress(vendor.billing_address) === normalized.address ||
          this.normalizeAddress(vendor.registered_address) === normalized.address,
      },
    ].filter((check) => !!check.value);

    if (!checks.length) return null;

    const strict = this.vendors().filter((vendor) => checks.every((check) => check.matches(vendor)));
    if (strict.length === 1) return String(strict[0].id);
    if (strict.length > 1) {
      const byName = strict.find((vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name);
      return String((byName || strict[0]).id);
    }

    if (normalized.gstin) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.gst_number) === normalized.gstin);
      if (vendor) return String(vendor.id);
    }
    if (normalized.cin) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.cin) === normalized.cin);
      if (vendor) return String(vendor.id);
    }
    if (normalized.email) {
      const vendor = this.vendors().find((row) => this.normalizeEmail(row.email) === normalized.email);
      if (vendor) return String(vendor.id);
    }
    if (normalized.name) {
      return this.findVendorIdByName(seller.seller_name || '');
    }
    return null;
  }

  private findBuyerVendorIdFromExtracted(buyer: {
    buyer_name?: string | null;
    buyer_state?: string | null;
    buyer_gstin?: string | null;
    buyer_pan?: string | null;
    buyer_address?: string | null;
    place_of_supply?: string | null;
  }): string | null {
    const normalized = {
      name: this.normalizeLookupName(buyer.buyer_name),
      state: this.normalizeLookupName(buyer.buyer_state || buyer.place_of_supply),
      gstin: this.normalizeTaxId(buyer.buyer_gstin),
      pan: this.normalizeTaxId(buyer.buyer_pan),
      address: this.normalizeAddress(buyer.buyer_address),
    };

    const checks = [
      { value: normalized.name, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name },
      { value: normalized.state, matches: (vendor: Vendor) => this.normalizeLookupName(vendor.state) === normalized.state },
      { value: normalized.gstin, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.gst_number) === normalized.gstin },
      { value: normalized.pan, matches: (vendor: Vendor) => this.normalizeTaxId(vendor.pan_number) === normalized.pan },
      {
        value: normalized.address,
        matches: (vendor: Vendor) =>
          this.normalizeAddress(vendor.billing_address) === normalized.address ||
          this.normalizeAddress(vendor.registered_address) === normalized.address,
      },
    ].filter((check) => !!check.value);

    if (!checks.length) return null;

    const strict = this.vendors().filter((vendor) => checks.every((check) => check.matches(vendor)));
    if (strict.length === 1) return String(strict[0].id);
    if (strict.length > 1) {
      const byName = strict.find((vendor) => this.normalizeLookupName(vendor.vendor_name) === normalized.name);
      return String((byName || strict[0]).id);
    }

    if (normalized.gstin) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.gst_number) === normalized.gstin);
      if (vendor) return String(vendor.id);
    }
    if (normalized.pan) {
      const vendor = this.vendors().find((row) => this.normalizeTaxId(row.pan_number) === normalized.pan);
      if (vendor) return String(vendor.id);
    }
    if (normalized.name) {
      return this.findVendorIdByName(buyer.buyer_name || '');
    }
    return null;
  }

  private normalizeLookupName(value: string | null | undefined): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private normalizeTaxId(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  private normalizeEmail(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  private normalizeAddress(value: string | null | undefined): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private firstItemQuantity(items: ExtractedInvoiceItem[]): number | null {
    for (const item of items) {
      const parsed = this.extractedItemQuantity(item);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  private extractedItemQuantity(item: ExtractedInvoiceItem | null | undefined): number | null {
    return this.toLooseNumberOrNull(item?.quantity ?? null);
  }

  private extractedItemRate(item: ExtractedInvoiceItem | null | undefined): number | null {
    return this.toLooseNumberOrNull(item?.rate ?? null);
  }

  private extractedItemAmount(item: ExtractedInvoiceItem | null | undefined): number | null {
    return this.toLooseNumberOrNull(item?.amount ?? null);
  }

  private extractPeriodFromItemDescription(
    description: string | null | undefined,
  ): { from: string; to: string } | null {
    const source = String(description || '').replace(/\s+/g, ' ').trim();
    if (!source) return null;

    const matches = [...source.matchAll(/\b\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}\b/g)]
      .map((match) => this.toIsoDateInput(match[0]))
      .filter(Boolean);
    if (!matches.length) return null;

    return {
      from: matches[0],
      to: matches[1] || matches[0],
    };
  }

  private findMatchingExtractedInvoiceItem(
    row: SellerBillGenerateRow,
    items: ExtractedInvoiceItem[],
  ): ExtractedInvoiceItem | null {
    const rowFrom = String(row.billPeriodFrom || row.billDate || '').trim();
    const rowTo = String(row.billPeriodTo || row.billDate || '').trim();
    if (!rowFrom && !rowTo) return null;

    return (
      items.find((item) => {
        const period = this.extractPeriodFromItemDescription(item.description);
        if (!period) return false;
        return period.from === rowFrom && period.to === rowTo;
      }) || null
    );
  }

  private shouldSplitInvoiceItemsIntoBillEntries(items: ExtractedInvoiceItem[]): boolean {
    if (items.length < 2) return false;

    const meaningfulItems = items.filter((item) => this.isMeaningfulExtractedInvoiceItem(item));
    return meaningfulItems.length > 1;
  }

  private isMeaningfulExtractedInvoiceItem(item: ExtractedInvoiceItem | null | undefined): boolean {
    if (!item) return false;

    const description = String(item.description || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const quantity = this.extractedItemQuantity(item);
    const amount = this.extractedItemAmount(item);

    if (quantity !== null || amount !== null) return true;
    if (!description) return false;

    return description.includes('power supply for the period');
  }

  private resolveExtractedInvoiceNumber(
    payload: ExtractedInvoicePayload,
    extractedMappingRow: SellerBillGenerateRow | null,
  ): string {
    const candidates = [
      String(payload.invoice_number || '').trim(),
      String(extractedMappingRow?.invoiceNumber || '').trim(),
      this.uploadedInvoiceFileNameBase(),
    ];
    return candidates.find((value) => !!value) || '';
  }

  private uploadedInvoiceFileNameBase(): string {
    const fileName = String(this.selectedFile?.name || '').trim();
    if (!fileName) return '';
    return fileName.replace(/\.[^.]+$/, '').trim();
  }

  private resolveExtractedInvoiceDate(
    payload: ExtractedInvoicePayload,
    extractedMappingRow: SellerBillGenerateRow | null,
  ): string {
    const candidates = [
      payload.invoice_date || null,
      extractedMappingRow?.billDate || null,
      payload.delivery_date || null,
      payload.trade_date || null,
    ];
    for (const candidate of candidates) {
      const normalized = this.toIsoDateInput(candidate);
      if (normalized) return normalized;
    }
    return '';
  }

  private resolveExtractedBillDate(
    payload: ExtractedInvoicePayload,
    extractedMappingRow: SellerBillGenerateRow | null,
  ): string {
    const candidates = [
      extractedMappingRow?.billDate || null,
      payload.delivery_date || null,
      payload.trade_date || null,
      payload.invoice_date || null,
    ];
    for (const candidate of candidates) {
      const normalized = this.toIsoDateInput(candidate);
      if (normalized) return normalized;
    }
    return '';
  }

  private toIsoDateInput(value: string | null | undefined): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const ddmmyyyy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
    const ddmmyyyyDots = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (ddmmyyyyDots) return `${ddmmyyyyDots[3]}-${ddmmyyyyDots[2]}-${ddmmyyyyDots[1]}`;
    const dmy2 = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{2})$/);
    if (dmy2) return `20${dmy2[3]}-${dmy2[2]}-${dmy2[1]}`;
    const dmy2Dots = raw.match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
    if (dmy2Dots) return `20${dmy2Dots[3]}-${dmy2Dots[2]}-${dmy2Dots[1]}`;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '';
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private syncContractFilters(): void {
    const availableBuyerIds = new Set(this.availableBuyers().map((vendor) => String(vendor.id)));
    if (this.buyerVendorId && !availableBuyerIds.has(this.buyerVendorId)) {
      this.buyerVendorId = '';
    }

    const availableContractIds = new Set(this.contractOptions().map((contract) => String(contract.id)));
    this.selectedContractIds = this.selectedContractIds.filter((id) => availableContractIds.has(id));
    if (this.contractId && !availableContractIds.has(this.contractId)) this.contractId = '';
    if (!this.contractId && this.selectedContractIds.length) this.contractId = this.selectedContractIds[0];
    if (!this.selectedContractIds.length && this.contractId) this.selectedContractIds = [this.contractId];

    const availableSellerLoaIds = new Set(this.availableSellerLoas().map((loa) => String(loa.id)));
    if (this.loaId && !availableSellerLoaIds.has(this.loaId)) {
      this.loaId = '';
    }

    const availableBuyerLoaIds = new Set(this.availableBuyerLoas().map((loa) => String(loa.id)));
    if (this.billMappingLoaId && !availableBuyerLoaIds.has(this.billMappingLoaId)) {
      this.billMappingLoaId = '';
    }
  }

  private autoSelectBillContextFromExtracted(): void {
    this.syncContractFilters();

    const sellerLoas = this.availableSellerLoas();
    if (!this.loaId && sellerLoas.length === 1) {
      this.loaId = String(sellerLoas[0].id);
    }

    if (!this.loaId && !this.contractId && sellerLoas.length === 1) {
      this.loaId = String(sellerLoas[0].id);
    }

    const buyerLoas = this.availableBuyerLoas();
    if (!this.billMappingLoaId && buyerLoas.length === 1) {
      this.billMappingLoaId = String(buyerLoas[0].id);
    }
  }

  private applyExtractedBillType(payload: ExtractedInvoicePayload): void {
    this.billType = 'SOP';
    this.billTypeLockedByUpload = true;
  }

  private toSellerBillGenerateRow(row: ExtractedBillMappingRow | null): SellerBillGenerateRow | null {
    if (!row) return null;
    const billType = String(row.billType || '')
      .trim()
      .toUpperCase() as BillTypeOption;
    if (!row.loaId || !row.billPeriodFrom || !row.billPeriodTo || !row.billDate || !['OA', 'SOP', 'TMR'].includes(billType)) {
      return null;
    }

    return {
      rowKey: String(row.rowKey || `${row.loaId}::${billType}::${row.billPeriodFrom}::${row.billPeriodTo}`),
      applicationId: String(row.applicationId || ''),
      applicationNumber: String(row.applicationNumber || '-'),
      applicationNumberTooltip: String(row.applicationNumberTooltip || row.applicationNumber || '-'),
      invoiceNumber: row.invoiceNumber ?? null,
      sellerVendorId: String(row.sellerVendorId || ''),
      sellerName: String(row.sellerName || '-'),
      buyerVendorId: row.buyerVendorId ?? null,
      buyerName: String(row.buyerName || 'No buyer linked'),
      contractId: row.contractId ?? null,
      contractNumber: String(row.contractNumber || 'No contract linked'),
      loaId: String(row.loaId),
      loaNumber: String(row.loaNumber || row.loaId),
      billType,
      billCycle: String(row.billCycle || ''),
      billDate: String(row.billDate),
      billPeriodFrom: String(row.billPeriodFrom),
      billPeriodTo: String(row.billPeriodTo),
      approvedQuantum: this.toNumberOrNull(row.approvedQuantum) ?? this.toNumberOrNull(row.totalQuantum),
      totalQuantum: this.toNumberOrNull(row.totalQuantum) ?? 0,
      totalAmount: this.toNumberOrNull(row.totalAmount),
      expectedAmount: this.toNumberOrNull(row.expectedAmount),
      billedQuantum: this.toNumberOrNull(row.billedQuantum),
      configuredRateLabel: String(row.configuredRateLabel || '-'),
      configuredRateValue: this.toNumberOrNull(row.configuredRateValue) ?? this.extractRateValue(row.configuredRateLabel),
      submittedRate: this.toNumberOrNull(row.submittedRate),
      submittedAmount: this.toNumberOrNull(row.submittedAmount),
      verificationId: row.verificationId ?? null,
      verificationStatus: String(row.verificationStatus || 'Pending Verification'),
      verificationNotes: Array.isArray(row.verificationNotes) ? row.verificationNotes.filter((item) => !!String(item || '').trim()) : [],
      approvalStatus: row.approvalStatus ?? null,
      approvalReason: row.approvalReason ?? null,
      approvalRole: row.approvalRole ?? null,
      approvalAt: row.approvalAt ?? null,
      statusTooltip: row.statusTooltip ?? null,
    };
  }

  private toSellerBillGenerateRows(
    rows: ExtractedBillMappingRow | ExtractedBillMappingRow[] | null,
  ): SellerBillGenerateRow[] {
    const normalizedRows = Array.isArray(rows) ? rows : rows ? [rows] : [];
    return normalizedRows
      .map((row) => this.toSellerBillGenerateRow(row))
      .filter((row): row is SellerBillGenerateRow => !!row);
  }

  private applyUploadedPeriodMapping(rows: SellerBillGenerateRow[]): void {
    const normalizedCycles = Array.from(
      new Set(rows.map((row) => String(row.billCycle || '').trim().toLowerCase()).filter(Boolean)),
    );
    const normalizedFrom = Array.from(new Set(rows.map((row) => String(row.billPeriodFrom || '').trim()).filter(Boolean)));
    const normalizedTo = Array.from(new Set(rows.map((row) => String(row.billPeriodTo || '').trim()).filter(Boolean)));

    // Apply upload-time filters only when extraction suggests a single shared period/cycle.
    // This keeps all extracted contract rows visible when the invoice spans multiple periods/cycles.
    this.uploadedBillCycle.set(normalizedCycles.length === 1 ? normalizedCycles[0] : '');
    this.uploadedPeriodFrom.set(normalizedFrom.length === 1 ? normalizedFrom[0] : '');
    this.uploadedPeriodTo.set(normalizedTo.length === 1 ? normalizedTo[0] : '');
  }

  private selectedContracts(): Contract[] {
    const selectedIds = new Set(this.selectedContractIds.map((id) => String(id).trim()).filter(Boolean));
    if (!selectedIds.size && this.contractId) selectedIds.add(String(this.contractId).trim());
    if (!selectedIds.size) return [];
    return this.contractOptions().filter((contract) => selectedIds.has(String(contract.id)));
  }

  private resolveContractsFromExtractedContext(
    matchedContractId: string,
    matchedContractNumber: string,
    collections: ExtractedContractLoaCollection[],
    mappingRows: SellerBillGenerateRow[],
  ): Contract[] {
    const contractIds = new Set<string>();
    const contractNumbers = new Set<string>();

    if (matchedContractId) contractIds.add(matchedContractId);
    if (matchedContractNumber) contractNumbers.add(this.normalizeLookupName(matchedContractNumber));

    collections.forEach((item) => {
      const contractId = String(item.contractId || item.contract_id || '').trim();
      const contractNumber = String(item.contractNumber || item.contract_number || '').trim();
      if (contractId) contractIds.add(contractId);
      if (contractNumber) contractNumbers.add(this.normalizeLookupName(contractNumber));
    });

    mappingRows.forEach((row) => {
      const contractId = String(row.contractId || '').trim();
      const contractNumber = String(row.contractNumber || '').trim();
      if (contractId) contractIds.add(contractId);
      if (contractNumber && contractNumber !== 'No contract linked') {
        contractNumbers.add(this.normalizeLookupName(contractNumber));
      }
    });

    if (!contractIds.size && !contractNumbers.size) return [];

    return this.contracts().filter((contract) => {
      const contractId = String(contract.id || '').trim();
      const contractNumber = this.normalizeLookupName(contract.contract_number || '');
      return contractIds.has(contractId) || (!!contractNumber && contractNumbers.has(contractNumber));
    });
  }

  private resolveBuyerIdFromExtractedContracts(currentBuyerId: string | null | undefined, contracts: Contract[]): string {
    const normalizedBuyerId = String(currentBuyerId || '').trim();
    if (!contracts.length) return normalizedBuyerId;

    if (normalizedBuyerId && contracts.some((contract) => this.contractIncludesVendor(contract, normalizedBuyerId, 'buyer'))) {
      return normalizedBuyerId;
    }

    const uniqueBuyerIds = Array.from(
      new Set(
        contracts.flatMap((contract) => this.contractPartyVendorIds(contract, 'buyer')),
      ),
    );

    return uniqueBuyerIds.length === 1 ? uniqueBuyerIds[0] : normalizedBuyerId;
  }

  private contractSellerLoaIds(contract: Contract | null): string[] {
    if (!contract) return [];
    return (contract.party_details || [])
      .filter((party) => this.contractPartyRole(party.loa_id, party.party_type) === 'seller')
      .map((party) => String(party.loa_id || '').trim())
      .filter((value) => !!value);
  }

  private contractBuyerLoaIds(contract: Contract | null): string[] {
    if (!contract) return [];
    return (contract.party_details || [])
      .filter((party) => this.contractPartyRole(party.loa_id, party.party_type) === 'buyer')
      .map((party) => String(party.loa_id || '').trim())
      .filter((value) => !!value);
  }

  private rowMatchesSelectedPeriod(rowFrom: string, rowTo: string, selectedFrom: string, selectedTo: string): boolean {
    const parsedSelectedFrom = this.parseDateValue(selectedFrom);
    const parsedSelectedTo = this.parseDateValue(selectedTo);
    if (!parsedSelectedFrom && !parsedSelectedTo) return true;

    const parsedRowFrom = this.parseDateValue(rowFrom);
    const parsedRowTo = this.parseDateValue(rowTo);
    const effectiveRowFrom = parsedRowFrom || parsedRowTo;
    const effectiveRowTo = parsedRowTo || parsedRowFrom;
    if (!effectiveRowFrom || !effectiveRowTo) return false;

    if (parsedSelectedFrom && this.floorDate(effectiveRowTo).getTime() < this.floorDate(parsedSelectedFrom).getTime()) return false;
    if (parsedSelectedTo && this.floorDate(effectiveRowFrom).getTime() > this.floorDate(parsedSelectedTo).getTime()) return false;
    return true;
  }

  private shouldRelaxUploadPeriodFilter(rows: SellerBillGenerateRow[], selectedFrom: string, selectedTo: string): boolean {
    const normalizedSelectedFrom = selectedFrom.trim();
    const normalizedSelectedTo = selectedTo.trim();
    if (!normalizedSelectedFrom && !normalizedSelectedTo) return false;
    if (!(this.showForm() && this.showBillSection() && !this.showVerificationSection())) return false;
    if (!this.sellerBillRowsFromBackend().length) return false;

    return !rows.some((row) =>
      this.rowMatchesSelectedPeriod(String(row.billPeriodFrom || ''), String(row.billPeriodTo || ''), normalizedSelectedFrom, normalizedSelectedTo),
    );
  }

  private rowMatchesAnyBillLinePeriod(rowFrom: string, rowTo: string, entries: BillEntryForm[]): boolean {
    const normalizedEntries = (entries || []).filter((entry) =>
      !!String(entry?.billPeriodFrom || '').trim() || !!String(entry?.billPeriodTo || '').trim(),
    );
    if (!normalizedEntries.length) return true;

    return normalizedEntries.some((entry) =>
      this.rowMatchesSelectedPeriod(
        rowFrom,
        rowTo,
        String(entry.billPeriodFrom || '').trim(),
        String(entry.billPeriodTo || '').trim(),
      ),
    );
  }

  private contractIncludesVendor(contract: Contract, vendorId: string, role: 'buyer' | 'seller'): boolean {
    const normalizedVendorId = String(vendorId || '').trim();
    if (!normalizedVendorId) return false;
    return (contract.party_details || []).some((party) => {
      if (String(party.vendor_id || '').trim() !== normalizedVendorId) return false;
      return this.contractPartyRole(party.loa_id, party.party_type) === role;
    });
  }

  private contractPartyVendorIds(contract: Contract, role: 'buyer' | 'seller'): string[] {
    return (contract.party_details || [])
      .filter((party) => this.contractPartyRole(party.loa_id, party.party_type) === role)
      .map((party) => String(party.vendor_id || '').trim())
      .filter((value) => !!value);
  }

  private contractPartyRole(
    loaId: string | null | undefined,
    partyType: string | null | undefined,
  ): 'buyer' | 'seller' | 'bank' | 'unknown' {
    const loa = this.loas().find((item) => String(item.id) === String(loaId || '').trim()) || null;
    const loaKind = this.resolveLoaKind(loa);
    if (loaKind === 'sell') return 'seller';
    if (loaKind === 'buy') return 'buyer';
    if (loaKind === 'bank') return 'bank';

    const normalizedPartyType = String(partyType || '').trim().toLowerCase();
    if (normalizedPartyType.includes('sell')) return 'seller';
    if (normalizedPartyType.includes('buy')) return 'buyer';
    if (normalizedPartyType.includes('bank')) return 'bank';
    return 'unknown';
  }

  private resolveLoaKind(loa: Loa | null | undefined): 'buy' | 'sell' | 'bank' | 'unknown' {
    const category = String(loa?.category || '').trim().toLowerCase();
    if (category.includes('sell') || category.includes('sale')) return 'sell';
    if (category.includes('buy') || category.includes('purchase')) return 'buy';
    if (category.includes('bank')) return 'bank';
    return 'unknown';
  }

  private selectedBillCycleForDate(billDate: string | null | undefined): string | null {
    const targetDate = this.parseDateValue(billDate);
    const configs = (this.billConfigsByLoa()[this.loaId] || [])
      .filter((item) => (item.bill_type || '').trim().toUpperCase() === this.billType)
      .sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''));

    if (!configs.length) return null;
    if (!targetDate) return (configs[0].bill_cycle || '').trim().toLowerCase() || null;

    const matched = configs.find((config) => {
      const effectiveDate = this.parseDateValue(config.effective_date);
      return !effectiveDate || effectiveDate.getTime() <= targetDate.getTime();
    });
    return ((matched || configs[0]).bill_cycle || '').trim().toLowerCase() || null;
  }

  private supportedBillConfigs(configs: LoaBillConfig[]): LoaBillConfig[] {
    return (configs || [])
      .filter((item) => {
        const billType = (item.bill_type || '').trim().toUpperCase();
        return billType === 'SOP';
      })
      .sort((left, right) => {
        const leftDate = left.effective_date || '';
        const rightDate = right.effective_date || '';
        if (left.bill_type !== right.bill_type) return left.bill_type.localeCompare(right.bill_type);
        return rightDate.localeCompare(leftDate);
      })
      .filter((item, index, rows) => index === rows.findIndex((row) => (row.bill_type || '').trim().toUpperCase() === (item.bill_type || '').trim().toUpperCase()));
  }

  private matchingVerification(
    verifications: BillVerification[],
    loaId: string,
    billType: BillTypeOption,
    billCycle: string,
    periodFrom: Date,
    periodTo: Date,
    rowTotalQuantum: number,
  ): BillVerification | null {
    const matches = verifications
      .map((row) => {
        if (row.loa_id !== loaId) return null;
        if ((row.bill_type || '').trim().toUpperCase() !== billType) return null;

        const range = this.resolveVerificationRange(row, billCycle, rowTotalQuantum);
        if (!range) return null;
        const matchKind = this.verificationRangeMatchKind(periodFrom, periodTo, range.from, range.to);
        if (matchKind === 0) return null;

        return { row, matchKind, specificity: range.specificity, spanDays: range.spanDays };
      })
      .filter(
        (
          item,
        ): item is {
          row: BillVerification;
          matchKind: number;
          specificity: number;
          spanDays: number;
        } => !!item,
      );

    if (!matches.length) return null;
    matches.sort((left, right) => {
      if (right.matchKind !== left.matchKind) return right.matchKind - left.matchKind;
      if (right.specificity !== left.specificity) return right.specificity - left.specificity;
      if (left.spanDays !== right.spanDays) return left.spanDays - right.spanDays;
      return (right.row.created_at || '').localeCompare(left.row.created_at || '');
    });
    return matches[0].row;
  }

  private rangesExactlyMatch(fromA: Date, toA: Date, fromB: Date, toB: Date): boolean {
    return this.floorDate(fromA).getTime() === this.floorDate(fromB).getTime() && this.floorDate(toA).getTime() === this.floorDate(toB).getTime();
  }

  private verificationRangeMatchKind(periodFrom: Date, periodTo: Date, verificationFrom: Date, verificationTo: Date): number {
    if (this.rangesExactlyMatch(periodFrom, periodTo, verificationFrom, verificationTo)) return 2;
    return 0;
  }

  private rangeContains(containerFrom: Date, containerTo: Date, innerFrom: Date, innerTo: Date): boolean {
    return this.floorDate(containerFrom).getTime() <= this.floorDate(innerFrom).getTime() &&
      this.floorDate(containerTo).getTime() >= this.floorDate(innerTo).getTime();
  }

  private resolveVerificationRange(
    row: BillVerification,
    billCycle: string,
    rowTotalQuantum: number,
  ): { from: Date; to: Date; specificity: number; spanDays: number } | null {
    const explicitFrom = this.parseDateValue(row.bill_period_from);
    const explicitTo = this.parseDateValue(row.bill_period_to);
    if (explicitFrom && explicitTo) {
      const from = explicitFrom.getTime() <= explicitTo.getTime() ? explicitFrom : explicitTo;
      const to = explicitFrom.getTime() <= explicitTo.getTime() ? explicitTo : explicitFrom;
      return {
        from,
        to,
        specificity: 3,
        spanDays: this.rangeDayCount(from, to),
      };
    }

    const billDate = this.parseDateValue(row.bill_date);
    if (!billDate) return null;

    const normalizedCycle = (billCycle || '').trim().toLowerCase();
    if (normalizedCycle.startsWith('daily')) {
      return {
        from: billDate,
        to: billDate,
        specificity: 1,
        spanDays: 1,
      };
    }

    return {
      from: billDate,
      to: billDate,
      specificity: 1,
      spanDays: 1,
    };
  }

  private rangeDayCount(from: Date, to: Date): number {
    return Math.max(1, Math.floor((this.floorDate(to).getTime() - this.floorDate(from).getTime()) / 86400000) + 1);
  }

  private floorDate(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private applicationComboLabel(applicationNumbers: string[]): { label: string; tooltip: string } {
    const numbers = applicationNumbers || [];
    if (!numbers.length) {
      return { label: '-', tooltip: '-' };
    }

    const tooltip = numbers.join(', ');
    const limit = 3;
    const shown = numbers.slice(0, limit);
    const remaining = numbers.length - shown.length;
    const label = remaining > 0 ? `${shown.join(', ')} +${remaining} more` : shown.join(', ');
    return { label, tooltip };
  }

  private entityComboLabel(values: string[], kind: 'buyer' | 'contract'): { label: string; tooltip: string } {
    const items = (values || []).filter((value) => !!String(value || '').trim());
    if (!items.length) {
      return { label: kind === 'buyer' ? 'No buyer linked' : 'No contract linked', tooltip: '-' };
    }
    if (items.length === 1) {
      return { label: items[0], tooltip: items[0] };
    }

    const tooltip = items.join(', ');
    return {
      label: kind === 'buyer' ? `Multiple buyers (${items.length})` : `Multiple contracts (${items.length})`,
      tooltip,
    };
  }

  private loaScheduleBounds(apps: ApplicationApi[]): { start: Date; end: Date } | null {
    const parsed = apps
      .flatMap((app) => this.approvedScheduleDates(app))
      .map((item) => this.parseDateValue(item))
      .filter((item): item is Date => !!item)
      .sort((left, right) => left.getTime() - right.getTime());

    if (!parsed.length) return null;

    const start = this.floorDate(parsed[0]);
    const end = this.floorDate(parsed[parsed.length - 1]);
    if (end.getTime() < start.getTime()) return null;
    return { start, end };
  }

  private applicationDateBounds(app: ApplicationApi): { start: Date; end: Date } | null {
    const parsed = this.approvedScheduleDates(app)
      .map((item) => this.parseDateValue(item))
      .filter((item): item is Date => !!item)
      .sort((left, right) => left.getTime() - right.getTime());

    if (!parsed.length) return null;
    return { start: this.floorDate(parsed[0]), end: this.floorDate(parsed[parsed.length - 1]) };
  }

  private billPeriodsForCycle(
    scheduleStart: Date,
    scheduleEnd: Date,
    billCycle: string,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const cycle = (billCycle || '').trim().toLowerCase();

    if (cycle.startsWith('monthly')) {
      return this.monthlyBillPeriods(scheduleStart, scheduleEnd);
    }
    if (cycle.startsWith('weekly')) {
      return this.weeklyBillPeriods(scheduleStart, scheduleEnd);
    }
    if (cycle.startsWith('daily')) {
      return this.dailyBillPeriods(scheduleStart, scheduleEnd);
    }

    if (scheduleEnd.getTime() < scheduleStart.getTime()) return [];
    return [{ from: scheduleStart, to: scheduleEnd, billDate: scheduleEnd }];
  }

  private dailyBillPeriods(
    scheduleStart: Date,
    scheduleEnd: Date,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const start = this.floorDate(scheduleStart);
    const end = this.floorDate(scheduleEnd);
    if (end.getTime() < start.getTime()) return [];

    const periods: Array<{ from: Date; to: Date; billDate: Date }> = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (cursor.getTime() <= end.getTime()) {
      const day = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
      periods.push({ from: day, to: day, billDate: day });
      cursor.setDate(cursor.getDate() + 1);
    }
    return periods;
  }

  private monthlyBillPeriods(
    scheduleStart: Date,
    scheduleEnd: Date,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const periods: Array<{ from: Date; to: Date; billDate: Date }> = [];
    let cursor = new Date(scheduleStart.getFullYear(), scheduleStart.getMonth(), 1);
    const endMonth = new Date(scheduleEnd.getFullYear(), scheduleEnd.getMonth(), 1);

    while (cursor.getTime() <= endMonth.getTime()) {
      const monthFrom = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const monthTo = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      if (this.rangesOverlap(monthFrom, monthTo, scheduleStart, scheduleEnd)) {
        periods.push({ from: monthFrom, to: monthTo, billDate: monthTo });
      }
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return periods;
  }

  private weeklyBillPeriods(
    scheduleStart: Date,
    scheduleEnd: Date,
  ): Array<{ from: Date; to: Date; billDate: Date }> {
    const periods: Array<{ from: Date; to: Date; billDate: Date }> = [];
    let cursor = new Date(scheduleStart.getFullYear(), scheduleStart.getMonth(), 1);
    const endMonth = new Date(scheduleEnd.getFullYear(), scheduleEnd.getMonth(), 1);

    while (cursor.getTime() <= endMonth.getTime()) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const lastDay = new Date(year, month + 1, 0).getDate();

      const buckets: Array<{ startDay: number; endDay: number }> = [
        { startDay: 1, endDay: 8 },
        { startDay: 9, endDay: 15 },
        { startDay: 16, endDay: 23 },
        { startDay: 24, endDay: lastDay },
      ];

      buckets.forEach((bucket) => {
        const from = new Date(year, month, bucket.startDay);
        const to = new Date(year, month, bucket.endDay);
        if (!this.rangesOverlap(from, to, scheduleStart, scheduleEnd)) return;
        periods.push({ from, to, billDate: to });
      });

      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return periods;
  }

  private rangesOverlap(fromA: Date, toA: Date, fromB: Date, toB: Date): boolean {
    return fromA.getTime() <= toB.getTime() && fromB.getTime() <= toA.getTime();
  }

  private billCycleBucket(
    billCycle: string,
    anchorDateYmd: string,
  ): { bucketKey: string; bucketLabel: string; bucketFrom: string; bucketTo: string } {
    const cycle = (billCycle || '').trim().toLowerCase();
    const parsedAnchor = this.parseDateValue(anchorDateYmd) ?? new Date();
    const year = parsedAnchor.getFullYear();
    const month = parsedAnchor.getMonth() + 1;
    const day = parsedAnchor.getDate();
    const lastDay = this.daysInMonth(year, month);

    if (cycle.startsWith('monthly')) {
      const from = `${year}-${String(month).padStart(2, '0')}-01`;
      const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return {
        bucketKey: `${year}-${String(month).padStart(2, '0')}`,
        bucketLabel: `${this.monthLabel(year, month)} (1-${lastDay})`,
        bucketFrom: from,
        bucketTo: to,
      };
    }

    if (cycle.startsWith('weekly')) {
      const { startDay, endDay } =
        day <= 8
          ? { startDay: 1, endDay: 8 }
          : day <= 15
            ? { startDay: 9, endDay: 15 }
            : day <= 23
              ? { startDay: 16, endDay: 23 }
              : { startDay: 24, endDay: lastDay };

      const from = `${year}-${String(month).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
      const to = `${year}-${String(month).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
      return {
        bucketKey: `${year}-${String(month).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`,
        bucketLabel: `${this.monthLabel(year, month)} (${startDay}-${endDay})`,
        bucketFrom: from,
        bucketTo: to,
      };
    }

    if (cycle.startsWith('daily')) {
      const value = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return {
        bucketKey: value,
        bucketLabel: value,
        bucketFrom: value,
        bucketTo: value,
      };
    }

    const value = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return {
      bucketKey: value,
      bucketLabel: value,
      bucketFrom: value,
      bucketTo: value,
    };
  }

  private daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
  }

  private monthLabel(year: number, month: number): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const name = monthNames[month - 1] || String(month);
    return `${name} ${year}`;
  }

  private billRateLabel(config: LoaBillConfig): string {
    const billType = (config.bill_type || '').trim().toUpperCase();
    const rateType = (config.rate_type || '').trim().toUpperCase();
    if (billType === 'TMR') {
      return this.valueOrDash(config.tmr_rate ?? config.rate);
    }
    if (rateType === 'MCP') {
      const base = this.valueOrDash(config.mcp_rate ?? config.rate);
      const condition = (config.mcp_condition || '').trim();
      return condition ? `${condition} ${base}`.trim() : base;
    }
    return this.valueOrDash(config.rate ?? config.mcp_rate);
  }

  private billRateValue(config: LoaBillConfig): number | null {
    const billType = (config.bill_type || '').trim().toUpperCase();
    const rateType = (config.rate_type || '').trim().toUpperCase();
    const candidate =
      billType === 'TMR'
        ? config.tmr_rate ?? config.rate
        : rateType === 'MCP'
          ? config.mcp_rate ?? config.rate
          : config.rate ?? config.mcp_rate;
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private sellerLoaRateLabel(loa: Loa | undefined): string {
    if (!loa) return '-';
    const configuredRateValue = this.toNumberOrNull(loa.configuredRateValue) ?? this.toNumberOrNull(loa.loa_rate);
    if (configuredRateValue !== null) return String(configuredRateValue);
    const rateType = String(loa.rate_type || '').trim().toUpperCase();
    if (rateType === 'MCP') {
      const base = this.valueOrDash(loa.mcp_value);
      const condition = String(loa.mcp_condition || '').trim();
      return condition ? `${condition} ${base}`.trim() : base;
    }
    return this.valueOrDash(loa.fixed_rate ?? loa.mcp_value);
  }

  private sellerLoaRateValue(loa: Loa | undefined): number | null {
    if (!loa) return null;
    const configuredRateValue = this.toNumberOrNull(loa.configuredRateValue) ?? this.toNumberOrNull(loa.loa_rate);
    if (configuredRateValue !== null) return configuredRateValue;
    const rateType = String(loa.rate_type || '').trim().toUpperCase();
    const candidate = rateType === 'MCP' ? loa.mcp_value ?? loa.fixed_rate : loa.fixed_rate ?? loa.mcp_value;
    return this.toNumberOrNull(candidate);
  }

  private applicationQuantumForRange(app: ApplicationApi, from: Date, to: Date): number {
    return (app.approval_schedules || [])
      .filter((schedule) => schedule.is_approved === true)
      .reduce((sum, schedule) => {
        return sum + this.approvedScheduleQuantumForRange(schedule, from, to);
      }, 0);
  }

  private approvedScheduleQuantumForRange(schedule: ApplicationApprovalScheduleApi, from: Date, to: Date): number {
    const scheduleFrom = this.parseDateValue(schedule.schedule_from);
    const scheduleTo = this.parseDateValue(schedule.schedule_to || schedule.schedule_from) || scheduleFrom;
    if (!scheduleFrom || !scheduleTo) return 0;
    if (!this.rangesOverlap(from, to, scheduleFrom, scheduleTo)) return 0;

    const overlapFrom = scheduleFrom.getTime() > from.getTime() ? scheduleFrom : from;
    const overlapTo = scheduleTo.getTime() < to.getTime() ? scheduleTo : to;
    if (overlapTo.getTime() < overlapFrom.getTime()) return 0;

    const dayDifference = Math.max(1, Math.floor((this.floorDate(overlapTo).getTime() - this.floorDate(overlapFrom).getTime()) / 86400000) + 1);
    const timeDifference = this.scheduleHourDifference(schedule.start_time, schedule.end_time);
    const approvedQuantum = this.approvedScheduleQuantum(schedule);
    if (approvedQuantum <= 0 || timeDifference <= 0) return 0;

    return dayDifference * timeDifference * 250 * approvedQuantum;
  }

  private applicationHasApprovedSchedule(app: ApplicationApi): boolean {
    return (app.approval_schedules || []).some((schedule) => schedule.is_approved === true);
  }

  private approvedScheduleDates(app: ApplicationApi): string[] {
    return (app.approval_schedules || [])
      .filter((item) => item.is_approved === true)
      .flatMap((item) => [item.schedule_from, item.schedule_to || item.schedule_from]);
  }

  private applicationAmountForRange(apps: ApplicationApi[], from: Date, to: Date, rate: number): number {
    return apps.reduce((sum, app) => {
      return (
        sum +
        (app.approval_schedules || [])
          .filter((schedule) => schedule.is_approved === true)
          .reduce((scheduleSum, schedule) => scheduleSum + this.approvedScheduleAmountForRange(schedule, from, to, rate), 0)
      );
    }, 0);
  }

  private approvedScheduleAmountForRange(schedule: ApplicationApprovalScheduleApi, from: Date, to: Date, rate: number): number {
    const scheduleFrom = this.parseDateValue(schedule.schedule_from);
    const scheduleTo = this.parseDateValue(schedule.schedule_to || schedule.schedule_from) || scheduleFrom;
    if (!scheduleFrom || !scheduleTo) return 0;
    if (!this.rangesOverlap(from, to, scheduleFrom, scheduleTo)) return 0;

    const overlapFrom = scheduleFrom.getTime() > from.getTime() ? scheduleFrom : from;
    const overlapTo = scheduleTo.getTime() < to.getTime() ? scheduleTo : to;
    if (overlapTo.getTime() < overlapFrom.getTime()) return 0;

    const dayDifference = Math.max(1, Math.floor((this.floorDate(overlapTo).getTime() - this.floorDate(overlapFrom).getTime()) / 86400000) + 1);
    const timeDifference = this.scheduleHourDifference(schedule.start_time, schedule.end_time);
    const approvedQuantum = this.approvedScheduleQuantum(schedule);
    if (approvedQuantum <= 0 || timeDifference <= 0) return 0;

    return dayDifference * timeDifference * 250 * approvedQuantum * rate;
  }

  private approvedScheduleQuantum(schedule: ApplicationApprovalScheduleApi): number {
    const acceptedMw = Number(schedule.accepted_mw);
    if (Number.isFinite(acceptedMw) && acceptedMw > 0) return acceptedMw;

    const requestedMw = Number(schedule.requested_mw);
    if (Number.isFinite(requestedMw) && requestedMw > 0) return requestedMw;

    const mwh = Number(schedule.mwh);
    return Number.isFinite(mwh) && mwh > 0 ? mwh : 0;
  }

  private scheduleQuantumForRange(schedule: ApplicationScheduleApi, from: Date, to: Date): number {
    const scheduleFrom = this.parseDateValue(schedule.from_date);
    const scheduleTo = this.parseDateValue(schedule.to_date || schedule.from_date) || scheduleFrom;
    if (!scheduleFrom || !scheduleTo) return 0;
    if (!this.rangesOverlap(from, to, scheduleFrom, scheduleTo)) return 0;

    const overlapFrom = scheduleFrom.getTime() > from.getTime() ? scheduleFrom : from;
    const overlapTo = scheduleTo.getTime() < to.getTime() ? scheduleTo : to;
    if (overlapTo.getTime() < overlapFrom.getTime()) return 0;

    const dayDifference = Math.max(1, Math.floor((this.floorDate(overlapTo).getTime() - this.floorDate(overlapFrom).getTime()) / 86400000) + 1);
    const timeDifference = this.scheduleHourDifference(schedule.from_time, schedule.to_time);
    const quantum = Number(schedule.quantum);
    if (!Number.isFinite(quantum) || quantum <= 0 || timeDifference <= 0) return 0;

    return dayDifference * timeDifference * 250 * quantum;
  }

  private scheduleHourDifference(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const startMinutes = this.timeToMinutes(fromTime, false);
    const endMinutes = this.timeToMinutes(toTime, true);
    if (startMinutes === null || endMinutes === null) return 0;
    if (endMinutes <= startMinutes) return 0;
    return (endMinutes - startMinutes) / 60;
  }

  private timeToMinutes(value: string | null | undefined, allow24Hour = false): number | null {
    const raw = (value || '').trim();
    if (!raw) return allow24Hour ? 1440 : 0;
    const [hourRaw, minuteRaw] = raw.split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (allow24Hour && hour === 24 && minute === 0) return 1440;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return hour * 60 + minute;
  }

  private valueOrDash(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }

  private parseDateValue(value: string | null | undefined): Date | null {
    const raw = (value || '').trim();
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const parsed = new Date(`${raw}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split('/');
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split('-');
      const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
